<template>
  <div class="ur-page report-container column-report">
    <b-alert variant="warning" :show="error != null && !isBusy">
      <span class="font-weight-bold">{{ error }}</span>
    </b-alert>

    <!-- ── Page Header ─────────────────────────────────────── -->
    <div class="ur-header">
      <div>
        <nav class="ur-breadcrumb">
          <span>Reports</span>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
            <polyline points="9 18 15 12 9 6" />
          </svg>
          <span :style="viewCrumb ? '' : 'color:#12283F'">Units</span>
          <template v-if="viewCrumb">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
              <polyline points="9 18 15 12 9 6" />
            </svg>
            <span style="color:#12283F">{{ viewCrumb }}</span>
          </template>
        </nav>
        <div class="ur-title-row">
          <h1 class="ur-title">{{ viewTitle }}</h1>
          <!-- Shortcut to the summary. Embedded in /project-reports this raises
               the Unit Summary tab; standalone it swaps the view in place. -->
          <button v-if="!isSummaryView" class="ur-badge" @click="goToSummary">Unit Summary</button>
          <button v-if="!embedded && !isUnitView" class="ur-badge ur-badge--ghost" @click="goToUnits">
            Back to units
          </button>
          <span class="ur-last-updated">Last updated <strong>{{ formatUpdated }}</strong></span>
        </div>
      </div>
      <div style="flex:1"></div>
      <div class="ur-header-divider"></div>
      <button class="ur-btn-ghost" @click="exportData()">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Export CSV
      </button>
      <button class="ur-btn-ghost" @click="generateReport()">
        <b-spinner v-if="loading" small variant="secondary" style="margin-right:4px"></b-spinner>
        <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Download PDF
      </button>
      <div class="ur-avatar">{{ userInitials }}</div>
    </div>

    <!-- ── Main Content ────────────────────────────────────── -->
    <div class="ur-content">

      <!-- KPI / Status Summary — one card per metric, split Active vs Sold/Leased -->
      <div class="ur-kpi-grid" v-if="filteredMlsData && filteredMlsData.length" v-show="isUnitView">
        <div class="ur-kpi2-card" v-for="c in kpiCards" :key="c.key">
          <span class="ur-kpi2-accent" :style="{ background: c.accent }"></span>
          <div class="ur-kpi2-label">{{ c.label }}</div>
          <div class="ur-kpi2-segs">
            <template v-for="(s, i) in c.segments">
              <div v-if="i > 0" :key="s.key + '-div'" class="ur-kpi2-vdiv"></div>
              <div :key="s.key" class="ur-kpi2-seg">
                <div class="ur-kpi2-seg__lbl" :class="'ur-kpi2-seg__lbl--' + s.key" :title="s.label">
                  <span class="ur-kpi2-dot"></span>{{ s.label }}
                </div>
                <div class="ur-kpi2-seg__val">{{ s.value }}</div>
                <div v-if="s.sub" class="ur-kpi2-seg__sub" :class="{ 'ur-kpi2-seg__sub--pos': s.subAccent }">
                  {{ s.sub }}
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="ur-toolbar" v-show="isUnitView">
        <div class="ur-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" stroke-width="1.75">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input v-model="filter_rows.filterText" placeholder="Search project, address, unit #…"
            class="ur-search__input" />
          <button v-if="filter_rows.filterText" @click="filter_rows.filterText = ''" class="ur-search__clear">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div class="ur-dropdown">
          <button @click="openDropdown = openDropdown === 'segment' ? null : 'segment'" class="ur-dropdown__btn">
            <span v-if="segFilter !== 'All'" class="ur-seg-dot"
              :class="'ur-seg-dot--' + (segFilter === 'Rental' ? 'rental' : 'forsale')"></span>
            {{ segFilter === 'All' ? 'All Segments' : segFilter }}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" stroke-width="1.75">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          <template v-if="openDropdown === 'segment'">
            <div class="ur-dropdown__overlay" @click="openDropdown = null"></div>
            <div class="ur-dropdown__menu">
              <button v-for="opt in segOptions" :key="opt" @click="setSegFilter(opt)"
                :class="['ur-dropdown__item', { 'ur-dropdown__item--on': segFilter === opt }]">
                <span v-if="opt !== 'All'" class="ur-seg-dot"
                  :class="'ur-seg-dot--' + (opt === 'Rental' ? 'rental' : 'forsale')"></span>
                {{ opt === 'All' ? 'All Segments' : opt }}
              </button>
            </div>
          </template>
        </div>

        <div class="ur-segmented">
          <span class="ur-seg-label">Status</span>
          <div class="ur-seg-group">
            <button @click="setStatusFilter('All')"
              :class="['ur-seg-btn', { 'ur-seg-btn--on': isStatusAll }]">All</button>
            <button v-for="opt in custom_filter.status.status_options" :key="opt.value"
              @click="setStatusFilter(opt.value)"
              :class="['ur-seg-btn', { 'ur-seg-btn--on': !isStatusAll && custom_filter.status.status_selected.includes(opt.value) }]">
              {{ opt.text }}
            </button>
          </div>
        </div>

        <div class="ur-segmented">
          <span class="ur-seg-label">Listing</span>
          <div class="ur-seg-group">
            <button @click="setListingFilter('All')"
              :class="['ur-seg-btn', { 'ur-seg-btn--on': isListingAll }]">All</button>
            <button v-for="opt in custom_filter.listingType.listingType_options" :key="opt.value"
              @click="setListingFilter(opt.value)"
              :class="['ur-seg-btn', { 'ur-seg-btn--on': !isListingAll && custom_filter.listingType.listingType_selected.includes(opt.value) }]">
              {{ opt.text }}
            </button>
          </div>
        </div>

        <div class="ur-dropdown">
          <button @click="openDropdown = openDropdown === 'type' ? null : 'type'" class="ur-dropdown__btn">
            {{ selectedTypeLabel }}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" stroke-width="1.75">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          <template v-if="openDropdown === 'type'">
            <div class="ur-dropdown__overlay" @click="openDropdown = null"></div>
            <div class="ur-dropdown__menu ur-dropdown__menu--multi">
              <button @click="clearTypeFilter"
                :class="['ur-dropdown__item', { 'ur-dropdown__item--on': !custom_filter.beds.beds_selected.length }]">All
                Types</button>
              <button v-for="opt in custom_filter.beds.beds_options" :key="opt.id" @click="toggleTypeFilter(opt.id)"
                :class="['ur-dropdown__item ur-dropdown__item--check', { 'ur-dropdown__item--on': custom_filter.beds.beds_selected.includes(opt.id) }]">
                <span class="ur-dropdown__check" aria-hidden="true">
                  <svg v-if="custom_filter.beds.beds_selected.includes(opt.id)" width="10" height="10"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                {{ opt.label }}
              </button>
            </div>
          </template>
        </div>

        <div class="ur-dropdown">
          <button @click="openDropdown = openDropdown === 'baths' ? null : 'baths'" class="ur-dropdown__btn">
            {{ selectedBathsLabel }}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" stroke-width="1.75">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          <template v-if="openDropdown === 'baths'">
            <div class="ur-dropdown__overlay" @click="openDropdown = null"></div>
            <div class="ur-dropdown__menu ur-dropdown__menu--multi">
              <button @click="clearBathsFilter"
                :class="['ur-dropdown__item', { 'ur-dropdown__item--on': !custom_filter.baths.baths_selected.length }]">Any
                baths</button>
              <button v-for="opt in custom_filter.baths.baths_options" :key="opt.id" @click="toggleBathsFilter(opt.id)"
                :class="['ur-dropdown__item ur-dropdown__item--check', { 'ur-dropdown__item--on': custom_filter.baths.baths_selected.includes(opt.id) }]">
                <span class="ur-dropdown__check" aria-hidden="true">
                  <svg v-if="custom_filter.baths.baths_selected.includes(opt.id)" width="10" height="10"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                {{ opt.label }}
              </button>
            </div>
          </template>
        </div>

        <DateSlider label="Price Date" v-model="priceDateDays" />

        <DateSlider label="Sold/Leased Date" v-model="soldDateDays" />

        <div style="flex:1"></div>

        <b-button v-b-modal.modal-filters class="ur-btn-ghost ur-btn-ghost--sm">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
            <line x1="4" y1="21" x2="4" y2="14" />
            <line x1="4" y1="10" x2="4" y2="3" />
            <line x1="12" y1="21" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12" y2="3" />
            <line x1="20" y1="21" x2="20" y2="16" />
            <line x1="20" y1="12" x2="20" y2="3" />
            <line x1="1" y1="14" x2="7" y2="14" />
            <line x1="9" y1="8" x2="15" y2="8" />
            <line x1="17" y1="16" x2="23" y2="16" />
          </svg>
          More filters
        </b-button>

        <button class="ur-filter-selected-btn" :class="{ 'ur-filter-selected-btn--active': filterToSelected }"
          :disabled="!selectedCount && !filterToSelected" @click="toggleFilterSelected">
          <font-awesome-icon :icon="['fas', 'filter']" />
          {{ filterToSelected ? "Show All" : "Filter"
          }}<span v-if="selectedCount && !filterToSelected">
            ({{ selectedCount }})</span>
        </button>

        <RangeInputFilters v-model="rangeInputFilters" v-on:reset-filter="resetFilter" class="d-none" />

        <button class="ur-btn-ghost ur-btn-ghost--sm" @click="saveFilters">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          </svg>
          Save view
        </button>

        <button @click="custom_header_field = !custom_header_field"
          :class="['ur-fields-btn', { 'ur-fields-btn--on': custom_header_field }]">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="9" y1="3" x2="9" y2="21" />
            <line x1="15" y1="3" x2="15" y2="21" />
          </svg>
          Fields
          <svg v-if="!custom_header_field" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="1.75">
            <polyline points="6 9 12 15 18 9" />
          </svg>
          <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>
      </div>

      <!-- Fields Panel -->
      <div v-if="custom_header_field && isUnitView" class="ur-fields-panel" ref="target_custom_header_field">
        <div class="ur-fp-header">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#12283F" stroke-width="1.75">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="9" y1="3" x2="9" y2="21" />
            <line x1="15" y1="3" x2="15" y2="21" />
          </svg>
          <span class="ur-fp-title">Fields</span>
          <span class="ur-fp-hint">Drag to reorder · click + to add from available</span>
          <div style="flex:1"></div>
          <button @click="resetHeaders" class="ur-ghost-link">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 .49-3.87" />
            </svg>
            Reset
          </button>
          <button @click="custom_header_field = false" class="ur-icon-btn ur-icon-btn--sm">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div class="ur-fp-body">
          <div class="ur-field-list">
            <div class="ur-fl-header">
              <span class="ur-fl-accent ur-fl-accent--navy"></span>
              <span class="ur-fl-title">Shown Data Points</span>
              <span class="ur-fl-count">· {{fields ? fields.filter(f =>
                !['project', 'selected'].includes(f.label.toLowerCase()) && f.key !== 'custom_price_change').length : 0
              }}
                shown · order matches table columns</span>
            </div>
            <draggable class="ur-chips" :list="fields" group="field" @change="fieldsOn" :sort="true">
              <template v-for="(element, index) in fields">
                <div
                  v-if="!['project', 'selected'].includes(element.label.toLowerCase()) && element.key !== 'custom_price_change'"
                  :key="'chip-sh-' + element.label" class="ur-chip ur-chip--filled">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.45)"
                    stroke-width="1.75">
                    <circle cx="9" cy="5" r="1" />
                    <circle cx="9" cy="12" r="1" />
                    <circle cx="9" cy="19" r="1" />
                    <circle cx="15" cy="5" r="1" />
                    <circle cx="15" cy="12" r="1" />
                    <circle cx="15" cy="19" r="1" />
                  </svg>
                  <span class="ur-chip__num">{{ index }}.</span>
                  <span>{{ element.label }}</span>
                </div>
              </template>
            </draggable>
          </div>
          <div class="ur-field-list">
            <div class="ur-fl-header">
              <span class="ur-fl-accent ur-fl-accent--grey"></span>
              <span class="ur-fl-title">Drag &amp; Drop Data Points</span>
              <span class="ur-fl-count">· {{ list2 ? list2.length : 0 }} available</span>
            </div>
            <draggable class="ur-chips" :list="list2" group="field" :sort="false">
              <div v-for="element in list2" :key="'chip-av-' + element.label" class="ur-chip ur-chip--outline">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#B6BAC2" stroke-width="1.75">
                  <circle cx="9" cy="5" r="1" />
                  <circle cx="9" cy="12" r="1" />
                  <circle cx="9" cy="19" r="1" />
                  <circle cx="15" cy="5" r="1" />
                  <circle cx="15" cy="12" r="1" />
                  <circle cx="15" cy="19" r="1" />
                </svg>
                <span>{{ element.label }}</span>
              </div>
            </draggable>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="ur-table-wrap" v-show="isUnitView">
        <div ref="tabulatorTable"></div>
      </div>

      <!-- ── Unit Summary view ───────────────────────────────────
           The cross-project roll-up. It used to open as a modal over this
           report; it is a view (a tab, when embedded) now so it can sit beside
           the per-project drill-downs it opens rather than covering them.
           v-show, not v-if: Tabulator needs its container to survive the
           switch, and the two tables are cheap to keep mounted. -->
      <div v-show="isSummaryView" class="ur-summary-view">
        <div class="ur-summary-bar">
          <div class="ur-dropdown">
            <button @click="summaryOpenDropdown = !summaryOpenDropdown" class="ur-dropdown__btn">
              {{ summaryCityFilter.length === 0 ? 'All Cities' : summaryCityFilter.length === 1 ?
                summaryCityFilter[0] :
                summaryCityFilter.length + ' cities' }}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" stroke-width="1.75">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <template v-if="summaryOpenDropdown">
              <div class="ur-dropdown__overlay" @click="summaryOpenDropdown = false"></div>
              <div class="ur-dropdown__menu ur-dropdown__menu--multi">
                <button @click="summaryCityFilter = []"
                  :class="['ur-dropdown__item', { 'ur-dropdown__item--on': summaryCityFilter.length === 0 }]">All
                  Cities &amp; Neighbourhoods</button>
                <div v-if="summaryUniqueCities.length" class="ur-dropdown__group-label">Cities</div>
                <button v-for="c in summaryUniqueCities" :key="'sc_' + c" @click="toggleSummaryCityFilter(c)"
                  :class="['ur-dropdown__item', { 'ur-dropdown__item--on': summaryCityFilter.includes(c) }]">{{ c
                  }}</button>
                <template v-if="summaryUniqueNeighbourhoods.length">
                  <div class="ur-dropdown__group-label">Neighbourhoods</div>
                  <button v-for="n in summaryUniqueNeighbourhoods" :key="'sn_' + n"
                    @click="toggleSummaryCityFilter(n)"
                    :class="['ur-dropdown__item', { 'ur-dropdown__item--on': summaryCityFilter.includes(n) }]">{{ n
                    }}</button>
                </template>
              </div>
            </template>
          </div>
          <div style="flex:1"></div>
          <button @click="exportSummary" class="ur-btn-ghost ur-btn-ghost--sm">
            Export CSV <font-awesome-icon :icon="['fas', 'file-export']" />
          </button>
        </div>
        <div class="ur-summary-card">
          <div ref="summaryProjectsTable" class="ur-summary-table"></div>
        </div>
        <div class="ur-summary-card">
          <div ref="summaryBedsTable" class="ur-summary-table"></div>
        </div>
      </div>

      <!-- ── Project drill-down view ─────────────────────────────
           Every unit row behind one line of the summary's Projects table,
           with the same columns the main report is showing. -->
      <div v-show="isProjectView" class="ur-project-view">
        <UnitKpiBoxes :rows="projectDetailRows" :is-market-rental="isMarketRental" />
        <div class="ur-table-wrap">
          <div ref="projectDetailTable"></div>
        </div>
      </div>
    </div>

    <b-modal v-for="(d, k) in reports" :id="'mls-fp-' + d.floor_plan.fid" hide-footer :key="'al_modal_' + k"
      :title="d.name">
      <div v-if="d.floor_plan.fid" class="d-block text-center">
        <a target="_blank" :href="d.floor_plan.link"><img :src="d.floor_plan.preview" class="w-100" /></a>
      </div>
    </b-modal>

    <b-modal id="modal-filters" hide-footer title="More Filters" size="lg">
      <div class="p-3">
        <FpSpecsFilter v-if="custom_filter.fpSpecs" v-model="custom_filter.fpSpecs.fpSpecs_selected" class="mb-4" />
        <Treeselect v-model="filter_rows.filterArray" :multiple="true" :options="filter_options"
          placeholder="Age, confirmed date, project status…" />
      </div>
    </b-modal>

    <ListingSummaryModal v-if="showListingModal && reportHistoryData" :row="reportHistoryData"
      :history="reportHistory || []" :is-market-strata="!isMarketRental" :raw-data="reportHistoryData"
      @close="showListingModal = false; reportHistory = null" @editMLSEntryNew="editMLSEntry"
      @deleteMLSEntry="deleteMLSEntry" />

  </div>
</template>

<script>
import axios from "axios";
import { onClickOutside, useThrottledRefHistory } from "@vueuse/core";
import { ref, getCurrentInstance } from "vue";
import { BModal, BSpinner, BAlert } from "bootstrap-vue";
import { BACKEND_URL } from "../variables.js";
import { useUserStore } from "../store/UserStore";
import { mapStores, mapState } from "pinia";
import Treeselect from "@riophae/vue-treeselect";
import draggable from "vuedraggable";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import ListingSummaryModal from "./modals/ListingSummaryModal.vue";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { wrapColumns } from "../utilities/tabulatorSorter";
import "tabulator-tables/dist/css/tabulator_bootstrap4.min.css";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import AvailablePricing from "./common/AvailablePricing.vue";
import RpdDoubleSlider from "./common/RpdDoubleSlider.vue";
import { saveView, loadView } from "../composables/asyncView.js";
import { listingTypePillHtml } from "../composables/listingTypePill.js";
import RangeInputFilters from "./common/RangeInputFilters.vue";
import FpSpecsFilter from "../shared/components/FpSpecsFilter.vue";
import DateSlider from "./filters/DateSlider.vue";
import PageLoader from "./common/PageLoader.vue";
import UnitKpiBoxes from "./common/UnitKpiBoxes.vue";
import { useRequestCounter } from "../store/RequestCounter";
import { data } from "jquery";

export default {
  setup() {
    const target_custom_header_field = ref(null);
    const currentInstance = ref(getCurrentInstance());
    onClickOutside(
      target_custom_header_field,
      (event) => (currentInstance.value.proxy.custom_header_field = false)
    );
    return {
      target_custom_header_field,
      currentInstance,
    };
  },
  components: {
    "b-modal": BModal,
    "b-spinner": BSpinner,
    Treeselect,
    ListingSummaryModal,
    draggable,
    DatePicker,
    AvailablePricing,
    "b-alert": BAlert,
    RpdDoubleSlider,
    RangeInputFilters,
    FpSpecsFilter,
    DateSlider,
    PageLoader,
    UnitKpiBoxes,
  },

  props: {
    // Which view is on screen: "unit", "summary", or "project:<project name>".
    //
    // Rendered as a tab of /project-reports the PARENT owns this, so the page's
    // tab bar and the view on screen can never disagree — the report asks for a
    // view by emitting, and gets it back as a prop. Standalone at /unit-report
    // there is no tab bar, so the component keeps the view itself
    // (`internalView`) and switches in place.
    view: {
      type: String,
      default: null,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedFpSpecs: [],
      field_selected: [],
      custom_field: [
        { label: "Age", key: "customAge", sortable: true },
        { label: "Balcony", key: "balcony", sortable: true },
        { label: "Balcony Area", key: "balcony_area", sortable: true },
        {
          label: "View Orientation",
          key: "view_orientation",
          sortable: true,
        },
        { label: "Parking", key: "parking" },
        { label: "Stalls", key: "stalls" },
        {
          label: "Construction Status",
          key: "construction_status",
          sortable: true,
        },
        { label: "Product Type", key: "product_type", sortable: true },
        { label: "Project Type", key: "project_type", sortable: true },
        { label: "Neighbourhood", key: "neighbour", sortable: true },
        { label: "Price Status", key: "price_backup", sortable: true },
        { label: "Occupancy Date", key: "occupancy_date", sortable: true },
        {
          label: "Sale Start Date",
          key: "sales_start_date",
          formatter: (value) => {
            let date = new Date(Number(value) * 1000);
            const options = {
              day: "2-digit",
              month: "short",
              year: "2-digit",
            };
            const formattedDate = date
              .toLocaleDateString("en-GB", options)
              .replace(/ /g, "-");
            return formattedDate;
          },
          sortable: true,
        },
        {
          label: "Plan Name",
          key: "field_mls_plan_name_value",
          sortable: true,
        },
        {
          label: "Project Status",
          key: "custom_project_status",
          sortable: true,
        },
      ],
      filter_rows: {
        filterText: "",
        filterArray: [],
      },
      filter_options: [
        {
          id: "age",
          label: "Age",
          children: [
            { id: "age_0 year", label: "0 year" },
            { id: "age_1 year", label: "1 year" },
            { id: "age_2 year", label: "2 year" },
            { id: "age_3 year", label: "3 year" },
            { id: "age_4 year", label: "4 year" },
            { id: "age_5 year", label: "5 year" },
            { id: "age_6 year", label: "6 year" },
            { id: "age_7 year", label: "7 year" },
            { id: "age_8 year", label: "8 year" },
            { id: "age_9 year", label: "9 year" },
            { id: "age_10 year", label: "10 year" },
          ],
        },
        {
          id: "confdate",
          label: "Confirmed Sold Date",
          children: [
            { id: "conf_Last 30 days", label: "Last 30 days" },
            { id: "conf_Last 60 days", label: "Last 60 days" },
            { id: "conf_Last 90 days", label: "Last 90 days" },
            { id: "conf_Last 180 days", label: "Last 180 days" },
            { id: "conf_Last 360 days", label: "Last 360 days" },
          ],
        },
      ],
      rangeInputFilters: {
        field_mls_unit_size_value: {
          label: "Unit Size",
          value: { min: null, max: null },
        },
        field_mls_list_price_value: {
          label: "List Price",
          value: { min: null, max: null },
        },
        psf: { label: "PSF", value: { min: null, max: null } },
        custom_net_psf: { label: "Net PSF", value: { min: null, max: null } },
        net_price: { label: "Net Price", value: { min: null, max: null } },
      },
      selectedQuarter: null,
      quarterlyOptions: [],
      rangeValue: {
        min: 0,
        max: 10000,
        minValue: 0,
        maxValue: 10000,
        startDate: null,
        endDate: null,
      },
      rangeValueSoldDate: {
        min: 0,
        max: 10000,
        minValue: 0,
        maxValue: 10000,
        startDate: null,
        endDate: null,
      },
      summaryTitle: "",
      pre_sort: "Type",
      priceDateStart: null,
      enablePricedate: false,
      enableSoldDate: false,
      priceDateDays: 0,
      soldDateDays: 0,
      custom_filter: {
        beds: {
          beds_selected: [],
          beds_options: [
            { id: "bed_Studio", label: "Studio" },
            { id: "bed_Jr 1-bed", label: "Jr 1-bed" },
            { id: "bed_1-bed", label: "1-bed" },
            { id: "bed_1-bed + Jr Bed", label: "1-bed + Jr Bed" },
            { id: "bed_2-bed", label: "2-bed" },
            { id: "bed_2-bed + Jr Bed", label: "2-bed + Jr Bed" },
            { id: "bed_3-bed", label: "3-bed" },
            { id: "bed_3-bed + Jr Bed", label: "3-bed + Jr Bed" },
            { id: "bed_4-bed", label: "4-bed" },
          ],
        },
        baths: {
          baths_selected: [],
          baths_options: [
            { id: "bath_1", label: "1" },
            { id: "bath_1.5", label: "1.5" },
            { id: "bath_2", label: "2" },
            { id: "bath_2.5", label: "2.5" },
            { id: "bath_3", label: "3" },
            { id: "bath_3.5", label: "3.5" },
            { id: "bath_4", label: "4" },
            { id: "bath_4.5", label: "4.5" },
            { id: "bath_5", label: "5" },
          ],
        },
        fpSpecs: {
          fpSpecs_selected: [],
          fpSpecs_options: [
            { id: "fp_Bonus Room", label: "Bonus Room" },
            { id: "fp_Den", label: "Den" },
            { id: "fp_Den(2)", label: "Den(2)" },
            { id: "fp_Furnished", label: "Furnished" },
            { id: "fp_Jr Bed(2)", label: "Jr Bed(2)" },
            { id: "fp_Jr Bed", label: "Jr Bed" },
            { id: "fp_Live/Work", label: "Live/Work" },
            { id: "fp_Lock-Off", label: "Lock-Off" },
            { id: "fp_Loft", label: "Loft" },
            { id: "fp_None", label: "None" },
            { id: "fp_PH", label: "PH" },
            { id: "fp_Roof Deck", label: "Roof Deck" },
            { id: "fp_Sub-PH", label: "Sub-PH" },
            { id: "fp_TH", label: "TH" },
          ],
        },
        listingType: {
          listingType_selected: [],
          listingType_options: [],
        },
        status: {
          status_selected: [],
          status_options: [],
        },
        soldDate: {
          soldDate_selected: "",
        },
        priceDate: {
          priceDate_selected: "",
        },
      },
      projectsHeader: [
        {
          key: "name",
          label: "Projects",
          thClass: "text-left mw-400p",
          tdClass: "text-left",
          sortable: true,
        },
        { key: "units", label: "Units", sortable: true },
        {
          key: "size",
          label: "Size",
          tdClass: "bg-t-data-2",
          sortable: true,
        },
        {
          key: "list_price",
          label: "List Price",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        { key: "psf", label: "$PSF", tdClass: "bg-t-data-1", sortable: true },
        {
          key: "net_price",
          label: "Net Price",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        {
          key: "net_psf",
          label: "Net $PSF",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
      ],
      previewHeader: [
        { key: "Type", label: "Type", sortable: true },
        { key: "Baths", label: "Baths", sortable: true },
        { key: "# Units", label: "Units", sortable: true },
        { key: "Units %", label: "Units %", sortable: true },
        { key: "Conf Sold", label: "Conf Sold", sortable: true },
        { key: "Sold %", label: `${this.isMarketRental ? 'Leased %' : 'Sold %'}`, sortable: true },
        {
          key: "Avg Size",
          label: "Avg Size",
          tdClass: "bg-t-data-2",
          sortable: true,
        },
        {
          key: "List Price",
          label: "List Price",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        {
          key: "$PSF",
          label: "$PSF",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        {
          key: "Net Price",
          label: "Net Price",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        {
          key: "Net $PSF",
          label: "Net $PSF",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        { key: "view", label: "" },
      ],
      previewHeaderSold: [
        { key: "Type", label: "Type", sortable: true },
        { key: "Baths", label: "Baths", sortable: true },
        { key: "Pkg", label: "Pkg", sortable: true },
        { key: "# Units", label: "# Units", sortable: true },
        {
          key: "Avg Size",
          label: "Avg Size",
          tdClass: "bg-t-data-2",
          sortable: true,
        },
        {
          key: "Gross Price",
          label: "Gross Price",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        {
          key: "Gross $PSF",
          label: "Gross $PSF",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        {
          key: "Net Price",
          label: "Net Price",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        {
          key: "Net $PSF",
          label: "Net $PSF",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        {
          key: "Sold Price",
          label: "Sold Price",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        {
          key: "Net Sold Price",
          label: "Net Sold Price",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        {
          key: "Net Sold $PSF",
          label: "Net Sold $PSF",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        { key: "view", label: "" },
      ],
      currentDate: new Date(),
      reportHistoryData: null,
      reportHistory: null,
      showListingModal: false,
      filtered_data: [],
      custom_header_field: false,
      loading: false,
      arrayOfImages: [],
      all_rows_selected: false,
      list2: [],
      filter_rows: {
        filterText: "",
        filterArray: [],
      },
      filter_options: [
        {
          id: "age",
          label: "Age",
          children: [
            { id: "age_0 year", label: "0 year" },
            { id: "age_1 year", label: "1 year" },
            { id: "age_2 year", label: "2 year" },
            { id: "age_3 year", label: "3 year" },
            { id: "age_4 year", label: "4 year" },
            { id: "age_5 year", label: "5 year" },
            { id: "age_6 year", label: "6 year" },
            { id: "age_7 year", label: "7 year" },
            { id: "age_8 year", label: "8 year" },
            { id: "age_9 year", label: "9 year" },
            { id: "age_10 year", label: "10 year" },
          ],
        },
        {
          id: "confdate",
          label: "Confirmed Sold Date",
          children: [
            { id: "conf_Last 30 days", label: "Last 30 days" },
            { id: "conf_Last 60 days", label: "Last 60 days" },
            { id: "conf_Last 90 days", label: "Last 90 days" },
            { id: "conf_Last 180 days", label: "Last 180 days" },
            { id: "conf_Last 360 days", label: "Last 360 days" },
          ],
        },
      ],
      sortDesc: true,
      isBusy: false,
      reports: [],
      post: null,
      error: null,
      fields: null,
      totalRows: 1,
      currentPage: 1,
      perPage: 15,
      rowOptions: [
        { value: 15, text: "15 rows" },
        { value: 25, text: "25 rows" },
        { value: 50, text: "50 rows" },
        { value: 100, text: "100 rows" },
      ],
      openDropdown: null,
      // Segment dropdown: bucket units by General Unit Type into For Sale
      // (Market Strata + Fee-Simple) vs Rental. "All" = no filter.
      segFilter: "All",
      segOptions: ["All", "For Sale", "Rental"],
      // NOTE: the Tabulator instances (tabulator, summaryProjectsTab,
      // summaryBedsTab) are intentionally NOT declared here. They are assigned
      // as plain, non-reactive instance properties in the methods below so Vue
      // never deep-observes their circular internals — declaring them in data()
      // makes Vue DevTools try to serialize every ColumnComponent getter, which
      // spams "column component does not have a state/render function" warnings.
      selectedCount: 0,
      filterToSelected: false,
      selectionFilterRefs: null,
      // Standalone view state. Ignored while `embedded` — see the `view` prop.
      internalView: "unit",
      summaryCityFilter: [],
      summaryOpenDropdown: false,
    };
  },
  mounted() {
    this.$nextTick(() => this.initTabulator());
    this.fetchData();
  },
  // Kept alive as a tab of /project-reports: Tabulator sizes itself against a
  // visible container, so it redraws when the tab comes back into view.
  activated() {
    this.redrawActiveView();
  },

  beforeDestroy() {
    if (this.tabulator) {
      this.tabulator.destroy();
      this.tabulator = null;
    }
    this.destroySummaryTabulators();
    if (this.projectDetailTab) {
      this.projectDetailTab.destroy();
      this.projectDetailTab = null;
    }
  },
  created() {
    // Non-reactive instance properties (kept out of data() on purpose — see the
    // note in data()). Tabulator manages its own internal state and must not be
    // wrapped in Vue's reactivity or serialized by DevTools.
    this.tabulator = null;
    this.summaryProjectsTab = null;
    this.summaryBedsTab = null;
    this.projectDetailTab = null;
    this.setRpdHeaderFields();
    this.addToFilterOptions();
  },
  methods: {
    editMLSEntry({ projectId, projectEntry }) {
      const q = this.$route.query;
      const link = `mls_entry_edit?cid=${projectId}&entry=${projectEntry}&project=${q.project || ''}&fid=${q.fid || ''}&uid=${q.uid || ''}&sid=${q.sid || ''}`;
      window.open(link, "_blank");
    },
    deleteMLSEntry({ projectId, projectEntry }) {
      if (!this.isAdmin) return;
      if (!confirm("Do you really want to delete this MLS Entry?")) return;
      $.ajax({
        type: "POST",
        url: `${BACKEND_URL}api/presale_mls_entry_delete`,
        data: { cid: projectId, entry: projectEntry },
      }).done(() => location.reload());
    },
    dataMapper(originalString) {
      return typeof originalString == 'string' ? originalString.replace(/[$,]/g, '') : originalString;
    },
    getDefaultValue(data) {
      let res = data.value;
      if (!this.isRealValue(data.value)) return (res = "-");

      if (["zero"].includes(data.value)) return (res = 0);

      if (
        [
          "field_mls_list_price_value",
          "mls_list_price",
          "net_price",
          "buyer_incentives",
          "custom_net_sold_price",
          "list_price"
        ].includes(data.field.key)
      ) {
        res = `$${Math.round(parseInt(data.value)).toLocaleString("en-US")}`;
      }

      if (
        [
          "realtor_incentives",
          "total_avg_psf",
          "total_net_psf",
          "list_price_net",
          "psf",
          "custom_net_psf",
          "custom_net_sold_psf",
          "sold_price",
          "custom_net_sold_psf",
        ].includes(data.field.key)
      ) {
        if (this.isMarketRental) res = `$${Number(data.value).toFixed(2)}`;
        else
          res = `$${Math.round(parseInt(data.value)).toLocaleString("en-US")}`;
      }

      return res;
    },
    isNotEmpty(response) {
      if (response == null) {
        return false;
      }

      if (typeof response === "string" && response.trim().length === 0) {
        return false;
      }

      if (Array.isArray(response) && response.length === 0) {
        return false;
      }

      if (typeof response === "object" && Object.keys(response).length === 0) {
        return false;
      }

      return true;
    },
    toUTCMidnight(date) {
      return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    },
    resetHeaders() {
      this.fields = JSON.parse(JSON.stringify(this.field_selected));
      this.list2 = JSON.parse(JSON.stringify(this.custom_field));
    },
    async saveFilters() {
      var selected_custom_filter = {
        baths: {
          baths_selected: this.custom_filter.baths.baths_selected,
        },
        beds: {
          beds_selected: this.custom_filter.beds.beds_selected,
        },
        fpSpecs: {
          fpSpecs_selected: this.custom_filter.fpSpecs.fpSpecs_selected,
        },
        listingType: {
          listingType_selected:
            this.custom_filter.listingType.listingType_selected,
        },
        status: {
          status_selected: this.custom_filter.status.status_selected,
        },
        rangeInputFilters: this.rangeInputFilters,
      };
      try {
        saveView(this.getViewCategory, "custom_filter", {
          custom_filter: selected_custom_filter,
        }).then((custom_filter) => {
          saveView(this.getViewCategory, "field_selected", {
            field_selected: this.fields,
          }).then((field_selected) => {
            saveView(this.getViewCategory, "main_filter", {
              main_filter: {
                filterArray: this.filter_rows.filterArray,
                filterText: this.filter_rows.filterText,
                maxRow: this.perPage,
              },
            }).then((res) => {
              saveView(this.getViewCategory, "custom_headers", {
                custom_headers: this.list2,
              }).then((res) => {
                this.$bvToast.toast(`Filters saved successfully!`, {
                  title: "Great job!",
                  variant: "success",
                  autoHideDelay: 5000,
                });
              });
            });
          });
        });
      } catch (error) {
        this.$bvToast.toast(
          `Saving of filters failed. Please contact the administrator. Thank you`,
          {
            title: "Ooops!",
            autoHideDelay: 5000,
            variant: "warning",
          }
        );
        console.log(error);
      }
    },
    async loadFilters() {
      try {
        const custom_filter = await loadView(this.getViewCategory, "custom_filter");
        if (this.isNotEmpty(custom_filter)) {
          for (const key in custom_filter) {
            if (key === "rangeInputFilters") {
              for (const filter in custom_filter[key]) {
                if (this.rangeInputFilters[filter]) {
                  this.rangeInputFilters[filter]["value"] =
                    custom_filter[key][filter]["value"];
                }
              }
            } else if (this.custom_filter[key]) {
              this.custom_filter[key][key + "_selected"] =
                custom_filter[key][key + "_selected"];
            }
          }
        }
      } catch (error) {
        console.log(error);
      }

      try {
        const main = await loadView(this.getViewCategory, "main_filter");
        if (this.isNotEmpty(main)) {
          if (Array.isArray(main.filterArray))
            this.filter_rows.filterArray = main.filterArray;
          if (typeof main.filterText === "string")
            this.filter_rows.filterText = main.filterText;
          if (main.maxRow && main.maxRow != null) this.perPage = main.maxRow;
        }
      } catch (error) {
        console.log(error);
      }

      try {
        const savedShown = await loadView(this.getViewCategory, "field_selected");
        const savedHidden = await loadView(this.getViewCategory, "custom_headers");

        if (this.isNotEmpty(savedShown) || this.isNotEmpty(savedHidden)) {
          const allPossibleFields = [...this.fields, ...this.list2];
          const savedKeys = new Set([
            ...(Array.isArray(savedShown) ? savedShown : []).map((c) => c.key),
            ...(Array.isArray(savedHidden) ? savedHidden : []).map((c) => c.key),
          ]);
          const localKeys = allPossibleFields.map((c) => c.key);
          const matches =
            savedKeys.size === localKeys.length &&
            localKeys.every((k) => savedKeys.has(k));

          if (matches) {
            const keyToDef = Object.fromEntries(
              allPossibleFields.map((c) => [c.key, c])
            );
            this.fields = (Array.isArray(savedShown) ? savedShown : [])
              .map((c) => keyToDef[c.key])
              .filter(Boolean);
            this.list2 = (Array.isArray(savedHidden) ? savedHidden : [])
              .map((c) => keyToDef[c.key])
              .filter(Boolean);
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    resetFilter() {
      this.rangeInputFilters = {
        field_mls_unit_size_value: {
          label: "Unit Size",
          value: { min: null, max: null },
        },
        field_mls_list_price_value: {
          label: "List Price",
          value: { min: null, max: null },
        },
        psf: { label: "PSF", value: { min: null, max: null } },
        net_price: { label: "Net Price", value: { min: null, max: null } },
        custom_net_psf: { label: "Net PSF", value: { min: null, max: null } },
      };
    },
    switchRangeValue(value) {
      if (value == "Price") this.enablePricedate = !this.enablePricedate;
      else if (value == "Sold") this.enableSoldDate = !this.enableSoldDate;
    },
    haveValue(data) {
      return data && !["-", 0, null, ""].includes(data);
    },
    getAge(row) {
      let d2 = new Date();
      let d1 = new Date(row.occupancy_date);

      if (d1 == "Invalid Date") return "-";

      if (d1 > d2) return 0;

      let totalMonths =
        (d2.getFullYear() - d1.getFullYear()) * 12 +
        (d2.getMonth() - d1.getMonth());

      if (d2.getDate() < d1.getDate()) {
        totalMonths -= 1;
      }

      if (totalMonths <= 12) {
        return 1;
      }

      return Math.round(totalMonths / 12);
    },

    getPriceChange(oldValue, newValue) {
      let oldToNum = Number(oldValue);
      let newToNum = Number(newValue);
      let result = {
        diffPercentage: "",
        color: "",
        icon: "",
      };

      if (newValue == "-" || !oldValue)
        return (result = {
          diffPercentage: "",
          color: "",
          icon: "",
        });
      if (oldValue == null) result.diffPercentage = 0;
      else
        result.diffPercentage = Number(
          ((newToNum - oldToNum) / oldToNum) * 100
        ).toFixed(2);

      if (Number(result.diffPercentage) > 0) {
        result.color = "green";
        result.icon = "fa-long-arrow-alt-up";
      } else if (Number(result.diffPercentage) < 0) {
        result.color = "red";
        result.icon = "fa-long-arrow-alt-down";
      } else {
        result.diffPercentage = 0;
        result.color = "black";
        result.icon = "minus";
      }

      return result;
    },
    sortTableByAvgSize(aRow, bRow, key, sortDesc) {
      let acc =
        key == "Avg Size" ? Number(aRow[key].replace(",", "")) : aRow[key];
      let curr =
        key == "Avg Size" ? Number(bRow[key].replace(",", "")) : bRow[key];
      if (sortDesc == true) {
        return acc > curr ? 1 : acc < curr ? -1 : 0;
      } else {
        return acc < curr ? -1 : acc > curr ? 1 : 0;
      }
    },
    setUnits(data, index) {
      const vm = this;
      sessionStorage.setItem(
        vm.$route.query["uids[]"] + index,
        JSON.stringify(data)
      );
      const routeData = this.$router.resolve({
        path: "/unit-summary",
        query: {
          unit: index,
          uids: vm.$route.query["uids[]"],
          status: vm.$route.query["status[]"],
        },
      });
      window.open(routeData.href, "_blank");
    },
    // Ask for a view. Embedded, the tab bar owns it and answers with the
    // `view` prop; standalone the component swaps it in place.
    setView(view) {
      if (this.embedded) this.$emit("open-view", view);
      else this.internalView = view;
    },
    goToSummary() {
      this.setView("summary");
    },
    goToUnits() {
      this.setView("unit");
    },
    // The "View" button on a row of the summary's Projects table. Embedded this
    // opens (or raises) a tab named after the project.
    openProjectDetail(name) {
      if (!name) return;
      this.setView("project:" + name);
    },
    // Tabulator measures itself against a visible container, so whichever table
    // just came into view has to be told to remeasure.
    redrawActiveView() {
      this.$nextTick(() => {
        if (this.isSummaryView) {
          this.initSummaryTabulators();
          if (this.summaryProjectsTab) this.summaryProjectsTab.redraw(true);
          if (this.summaryBedsTab) this.summaryBedsTab.redraw(true);
        } else if (this.isProjectView) {
          this.initProjectDetailTabulator();
          if (this.projectDetailTab) this.projectDetailTab.redraw(true);
        } else if (this.tabulator) {
          this.tabulator.redraw(true);
        }
      });
    },
    fieldsOn: function (evt) {
      console.log("fieldsOn", evt);
    },
    setStatusFilter(val) {
      if (val === 'All') {
        this.custom_filter.status.status_selected = [];
      } else {
        this.custom_filter.status.status_selected = [val];
      }
    },
    setSegFilter(val) {
      this.segFilter = val;
      this.openDropdown = null;
    },
    setListingFilter(val) {
      if (val === 'All') {
        this.custom_filter.listingType.listingType_selected = [];
      } else {
        this.custom_filter.listingType.listingType_selected = [val];
      }
    },
    toggleTypeFilter(id) {
      const sel = this.custom_filter.beds.beds_selected;
      const idx = sel.indexOf(id);
      if (idx === -1) sel.push(id);
      else sel.splice(idx, 1);
    },
    clearTypeFilter() {
      this.custom_filter.beds.beds_selected = [];
      this.openDropdown = null;
    },
    toggleBathsFilter(id) {
      const sel = this.custom_filter.baths.baths_selected;
      const idx = sel.indexOf(id);
      if (idx === -1) sel.push(id);
      else sel.splice(idx, 1);
    },
    clearBathsFilter() {
      this.custom_filter.baths.baths_selected = [];
      this.openDropdown = null;
    },
    addToFilterOptions() {
      const vm = this;
      // A result set can mix for-sale and market-rental units (listing type is
      // derived per row from its status tid), so each option covers the
      // for-sale label and its rental equivalent. `value` stays the for-sale
      // label so previously saved views keep working; `values` drives matching.
      this.custom_filter.listingType.listingType_options = [
        {
          id: "lt_Presale",
          label: "Presale / New Lease",
          text: "Presale / New Lease",
          value: "Presale",
          values: ["Presale", "New Lease"],
        },
        {
          id: "lt_Assignment",
          label: "Assignment",
          text: "Assignment",
          value: "Assignment",
          values: ["Assignment"],
        },
        {
          id: "lt_Resale",
          label: "Resale / Re-Lease",
          text: "Resale / Re-Lease",
          value: "Resale",
          values: ["Resale", "Re-Lease"],
        },
      ];

      if (vm.isMarketRental) {
        this.filter_options.push({
          id: "project_status",
          label: "Project Status",
          children: [
            { id: "ps_Now Leasing", label: "Now Leasing" },
            { id: "ps_Leased Out", label: "Leased Out" },
          ],
        });

        this.custom_filter.status.status_options = [
          {
            id: "status_Active",
            label: "Active",
            text: "Active",
            value: "Active",
          },
          {
            id: "status_Sold",
            label: "Leased",
            text: "Leased",
            value: "Leased",
            values: ["Leased", "Sold"],
          },
        ];
      } else {
        this.filter_options.push({
          id: "project_status",
          label: "Project Status",
          children: [
            { id: "ps_Now Selling", label: "Now Selling" },
            { id: "ps_Sold Out", label: "Sold Out" },
          ],
        });
        this.custom_filter.status.status_options = [
          {
            id: "status_Active",
            label: "Active",
            text: "Active",
            value: "Active",
          },
          {
            id: "status_Sold",
            label: "Sold/Leased",
            text: "Sold/Leased",
            value: "Sold",
            values: ["Sold", "Leased"],
          },
        ];
      }
    },
    setProjectStatus(id) {
      const vm = this;
      let res = "";
      switch (id) {
        case "2880":
          res = "In Planning";
          break;

        case "21135":
          res = vm.isMarketRental ? "Now Leasing" : "Now Selling";
          break;

        case "2881":
          res = "Selling Soon";
          break;

        case "2882":
          res = vm.isMarketRental ? "Now Leasing" : "Now Selling";
          break;

        case "2978":
          res = vm.isMarketRental ? "Leased Out" : "Sold Out";
          break;

        case "11514":
          res = "Sold Out 2";
          break;

        case "2976":
          res = "Leasing Soon";
          break;

        case "11128":
          res = "Now Leasing";
          break;

        case "11126":
          res = "Leased Out";
          break;
      }

      return res;
    },
    setListingType(row) {
      let res = "";
      let priceDate = new Date(row.price_date);
      let soldDate = new Date(row.sold_date);
      let constructionCompletionDate = new Date(row.occupancy_date);
      let soldOutDate = new Date(row.sold_out_date);
      try {
        let hasSoldStatusBeforeEntry = row.mls_history.some(
          (val, index) =>
            val["mls_status"] == "Sold" && index < row.mls_history.length - 1
        );
        res = "Presale";

        if (hasSoldStatusBeforeEntry) {
          if (
            [priceDate, soldDate].some(
              (date) => date < constructionCompletionDate
            )
          )
            res = "Assignment";

          if (
            [priceDate, soldDate].some(
              (date) => date > constructionCompletionDate
            )
          )
            res = "Resale";
        } else {
          if (
            [priceDate, soldDate].some(
              (date) =>
                date > soldOutDate && date < constructionCompletionDate
            )
          )
            res = "Assignment";

          if (
            [priceDate, soldDate].some(
              (date) =>
                date > soldOutDate && date > constructionCompletionDate
            )
          )
            res = "Resale";
        }

        if (this.isMarketRentalStatus(row.field_utc_status_tid)) {
          switch (res) {
            case "Presale":
              res = "New Lease";
              break;

            case "Resale":
              res = "Re-Lease";
              break;
          }
        }

      } catch (error) {
        this.error = error;
        console.log(error);
      }

      return res;
    },
    // Bucket a unit row by its General Unit Type for the KPI split and the
    // segment dropdown. The backend sends `general_unit_type` as an array
    // (e.g. ["Residential", "Market Strata"]); we match on the whole array.
    // Mirrors MarketOverview.segKey(): a rental type → "rental"; everything
    // else (Market Strata, Fee-Simple, missing) collapses into "forsale".
    segmentKey(row) {
      const gut = row && row.general_unit_type;
      const parts = Array.isArray(gut) ? gut : gut != null ? [gut] : [];
      const joined = parts.join(" ").toLowerCase();
      if (joined.includes("rental")) return "rental";
      return "forsale";
    },
    onSelectAllRows() {
      this.reports.map(
        (item) => (item["rowSelected"] = this.all_rows_selected)
      );
    },
    refreshResults() {
      if (this.reports.filter((row) => row.rowSelected === true).length > 0) {
        this.reports = this.reports.filter(
          (report) => report.rowSelected === true
        );
      }
    },
    popHistory(data) {
      const getNetPsf = (row) => {
        const listPrice = parseFloat(row.mls_list_price) || 0;
        const unitSize = parseFloat(row.mls_unit_size) || 0;
        const incentives = parseFloat(row.buyer_incentives) || 0;

        if (listPrice === 0 || unitSize === 0 || this.isRealValue(row.mls_psf)) {
          return row.mls_psf || 0;
        }

        const netPrice = listPrice - incentives;
        const psf = netPrice / unitSize;

        return !this.rowTwoDecimalPsf(data)
          ? Math.round(psf).toLocaleString()
          : Number(psf).toFixed(2);
      };

      // get_mls_history sends the price date as `price_date`, while the summary
      // modal reads `mls_price_date` — for the Price Date column and for the DOM
      // it derives from it. Normalize on the way in, as MlsRental.popHistory does.
      const mappedHistory = (data.mls_history || []).map((item) => {
        return {
          ...item,
          mls_psf: getNetPsf(item),
          mls_price_date: item.price_date
        };
      });

      this.reportHistoryData = data;
      this.reportHistory = mappedHistory;
    },
    generateArrayOfImages() {
      const vm = this;
      vm.arrayOfImages = new Array();
      let promises = [];
      vm.filtered_data.forEach((x) => {
        promises.push(vm.getImageRatio(x.floor_plan.preview));

        vm.getImageRatio(x.floor_plan.preview).then((resolve) => {
          if (resolve.status == "success") {
            vm.arrayOfImages.push({
              project: {
                name: x.name,
                field_mls_address_value: x.field_mls_address_value,
                price: !["", null, 0, "-", undefined].includes(
                  x.field_mls_list_price_value
                )
                  ? `$${Math.round(
                    x.field_mls_list_price_value
                  ).toLocaleString()}`
                  : "-",
                psf:
                  x.psf > 0 && vm.rowTwoDecimalPsf(x)
                    ? `$${Number(x.psf).toFixed(2)}`
                    : !vm.rowTwoDecimalPsf(x)
                      ? `$${Math.round(x.psf)}`
                      : "-",
                priceDate: x.price_date,
              },
              image: resolve.image,
            });
          }
        });
      });
    },
    generateQuarterlyReportCsv() {
      const vm = this;
      function generateCsvFile(csvContent, filename) {
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        if (navigator.msSaveBlob) {
          // For IE 10+
          navigator.msSaveBlob(blob, filename);
        } else {
          const link = document.createElement("a");
          if (link.download !== undefined) {
            // feature detection
            // Browsers that support HTML5 download attribute
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", filename);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        }
      }
      const header = [
        { label: "Project", key: "project" },
        { label: "Developer", key: "developer" },
        { label: "Address", key: "address" },
        { label: "Total Units", key: "total" },
        { label: "Rlsd Units", key: "rlsd_units" },
        { label: "Sold Units", key: "sold_total" },
        { label: "Available Units", key: "available" },
        { label: "Avg Price", key: "avg_price" },
        { label: "Avg $PSF", key: "avg_psf" },
      ];

      let csvContent = "";
      const appendTableToCSV = (title, data, header) => {
        csvContent += title + "\n";
        csvContent += header.map((h) => h.label).join(",") + "\n"; // Add headers

        data.forEach((row) => {
          const rowData = header
            .map((h) => {
              let value = row[h.key];
              if (typeof value === "string" && value.includes(",")) {
                return `"${value.replace(/"/g, '""')}"`; // Enclose in quotes and escape existing quotes
              }
              return value;
            })
            .join(",");
          csvContent += rowData + "\n";
        });
        csvContent += "\n"; // Add an empty line between tables
      };

      var cityProjects = vm.categorizedProjects;
      if (cityProjects) {
        let index = 0;
        for (let key in cityProjects) {
          csvContent += `${key} ${vm.selectedQuarter} Report` + "\n";

          let quarterResults = {
            newProjects: [],
            activelySellingProjects: [],
            soldOutProjects: [],
          };

          cityProjects[key].forEach((data) => {
            if (data.project.projects.newProjects)
              quarterResults.newProjects.push(
                ...data.project.projects.newProjects
              );
            if (data.project.projects.activelySellingProjects)
              quarterResults.activelySellingProjects.push(
                ...data.project.projects.activelySellingProjects
              );
            if (data.project.projects.soldOutProjects)
              quarterResults.soldOutProjects.push(
                ...data.project.projects.soldOutProjects
              );
          });

          if (quarterResults.newProjects) {
            const newProjectsData = this.processProjectData(
              quarterResults.newProjects
            );
            if (newProjectsData.length > 0)
              appendTableToCSV("New Projects", newProjectsData, header);
          }

          if (quarterResults.activelySellingProjects) {
            const activelySellingProjects = this.processProjectData(
              quarterResults.activelySellingProjects
            );
            if (activelySellingProjects.length > 0)
              appendTableToCSV(
                "Active Projects",
                activelySellingProjects,
                header
              );
          }

          if (quarterResults.soldOutProjects) {
            const soldOutProjects = this.processProjectData(
              quarterResults.soldOutProjects
            );
            if (soldOutProjects.length > 0)
              appendTableToCSV("Sold Out Projects", soldOutProjects, header);
          }
          let unitBreakDownHeaders = [
            {
              key: "name",
              label: "Project",
            },
            {
              key: "developer",
              label: "Developer",
            },
            {
              key: "address",
              label: "Address",
            },
            {
              key: "city",
              label: "City",
            },
          ];
          unitBreakDownHeaders.push(
            ...vm.fields.filter(
              (header) =>
                !["checkbox", "customData", "customLocationData"].includes(
                  header.key
                )
            )
          );

          var mergedProjects = [
            ...quarterResults.newProjects,
            ...quarterResults.activelySellingProjects,
            ...quarterResults.soldOutProjects,
          ];
          mergedProjects = mergedProjects.map((item) => {
            return {
              ...item,
              field_mls_list_price_value: `${this.isRealValue(item.field_mls_list_price_value)
                ? item.field_mls_list_price_value
                : "-"
                } ${this.isRealValue(item.custom_price_change.diffPercentage)
                  ? "(" + item.custom_price_change.diffPercentage + "%)"
                  : ""
                }`,
              field_mls_address_value: item.floor_plan.preview
                ? `=HYPERLINK("${item.floor_plan.preview}", "${item.field_mls_address_value}")`
                : item.field_mls_address_value,
              custom_field_mls_beds_value:
                item.custom_field_mls_beds_value.type +
                `${item.custom_field_mls_beds_value.fp_specs ? " + " : ""}` +
                item.custom_field_mls_beds_value.fp_specs,
            };
          });

          const groupedColumns = [
            { label: "Type", key: "Type" },
            { label: "Units", key: "# Units" },
            { label: "Avg Size", key: "Avg Size" },
            { label: "Avg Gross Price", key: "List Price" },
            { label: "Avg Gross $PSF", key: "$PSF" },
            { label: "Net Price", key: "Net Price" },
            { label: "Net $PSF", key: "Net $PSF" },
          ];
          var groupDataByType = vm.groupDataByType(
            mergedProjects.filter((project) => project.price_date != "")
          );
          const tableData = mergedProjects
            .filter((project) => project.price_date != "")
            .map((item, index) => {
              return unitBreakDownHeaders.map((header) => {
                if (header.key == "customData") {
                  return `${item.name}` + "\n" + item.developer;
                }
                if (
                  [
                    "field_mls_list_price_value",
                    "net_price",
                    "buyer_incentives",
                  ].includes(header.key)
                ) {
                  let formatPrice = Math.round(
                    Number(item[header.key])
                  ).toLocaleString();
                  return item[header.key] ? `$${formatPrice}` : "-";
                }
                if (["psf"].includes(header.key)) {
                  let formatPrice = vm.rowTwoDecimalPsf(item)
                    ? Number(item[header.key]).toFixed(2)
                    : Math.round(Number(item[header.key])).toLocaleString();
                  return item[header.key] ? `$${formatPrice}` : "-";
                }
                if (header.key == "customLocationData")
                  return `${item.address}` + "\n" + item.city;

                if (header.key == "net_psf") {
                  if (item.net_price && item.field_mls_unit_size_value) {
                    const rawNetPsf = Number(item.net_price) / Number(item.field_mls_unit_size_value);
                    let netPrice = vm.rowTwoDecimalPsf(item)
                      ? rawNetPsf.toFixed(2)
                      : Math.round(rawNetPsf).toLocaleString();
                    return netPrice ? `$${netPrice}` : "-";
                  }
                }

                if (
                  header.key == "custom_field_mls_beds_value" &&
                  typeof item[header.key == "Object"] &&
                  ![null, ""].includes(item[header.key])
                ) {
                  return (
                    `${item[header.key]["type"]}` +
                    "\n" +
                    item[header.key]["fp_specs"]
                  );
                }

                return item[header.key] ? item[header.key] : "-";
              });
            });

          appendTableToCSV(
            "Unit Summary Report",
            groupDataByType,
            groupedColumns
          );
          appendTableToCSV("Unit Report", mergedProjects, unitBreakDownHeaders);
        }
      }
      const currentUnixTimestamp = Math.floor(Date.now() / 1000);
      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `${currentUnixTimestamp} - ${vm.selectedQuarter}.csv`
        );
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    processProjectData(projects) {
      const vm = this;
      var result = Object.entries(vm.groupByCity(projects)).map(
        ([name, units]) => {
          let developer = units.length > 0 ? units[0].developer : "-";
          let address = units.length > 0 ? units[0].address : "-";
          let project = units.length > 0 ? units[0].name : "-";
          let city = units.length > 0 ? units[0].city : "-";
          return [units[units.length - 1]].map((item) => {
            let history = Object.entries(item.attr_history)[
              Object.entries(item.attr_history).length - 1
            ];
            return {
              project: `${name}`,
              developer: `${developer}`,
              address: `${address}`,
              customData: `${name}\n${developer}`,
              customLocationData: `${address}\n${city}`,
              avg_price: history[1].avg_price
                ? `$${Math.round(history[1].avg_price).toLocaleString()}`
                : "-",
              total_net_psf: history[1].total_net_psf
                ? `$${Math.round(history[1].total_net_psf).toLocaleString()}`
                : "-",
              avg_psf: history[1].avg_psf
                ? `$${Math.round(history[1].avg_psf).toLocaleString()}`
                : "-",
              avg_size: history[1].avg_size
                ? `${Math.round(history[1].avg_size).toLocaleString()}`
                : "-",
              total: item.total_units,
              available: item.available,
              sold_total: item.sold_units,
              rlsd_units: item.rlsd_units,
            };
          });
        }
      );
      return result.flat();
    },
    generateQuarterlyReport() {
      const vm = this;
      const doc = new jsPDF("l", "mm");
      const columns = [
        { label: "Project Name & Developer", key: "customData" },
        { label: "Address & City", key: "customLocationData" },
        { label: "Total Units", key: "total" },
        { label: "Rlsd Units", key: "rlsd_units" },
        { label: "Sold Units", key: "sold_total" },
        { label: "Available Units", key: "available" },
        { label: "Avg Price", key: "avg_price" },
        { label: "Avg $PSF", key: "avg_psf" },
      ];
      // Helper function to process project data
      const processProjectData = (projects) => {
        var result = Object.entries(vm.groupByName(projects)).map(
          ([name, units]) => {
            let developer = units.length > 0 ? units[0].developer : "-";
            let address = units.length > 0 ? units[0].address : "-";
            let city = units.length > 0 ? units[0].city : "-";
            return [units[units.length - 1]].map((item) => {
              let history = Object.entries(item.attr_history)[
                Object.entries(item.attr_history).length - 1
              ];
              return {
                customData: `${name}\n${developer}`,
                customLocationData: `${address}\n${city}`,
                avg_price: history[1].avg_price
                  ? `$${Math.round(history[1].avg_price).toLocaleString()}`
                  : "-",
                total_net_psf: history[1].total_net_psf
                  ? `$${Math.round(history[1].total_net_psf).toLocaleString()}`
                  : "-",
                avg_psf: history[1].avg_psf
                  ? `$${Math.round(history[1].avg_psf).toLocaleString()}`
                  : "-",
                avg_size: history[1].avg_size
                  ? `${Math.round(history[1].avg_size).toLocaleString()}`
                  : "-",
                total: item.total_units,
                available: item.available,
                sold_total: item.sold_units,
                rlsd_units: item.rlsd_units,
              };
            });
          }
        );
        return result.flat();
      };

      // Process and add tables to the PDF
      const addTableToPdf = (data, projectType, finalY) => {
        // doc.text(title, 14, doc.lastAutoTable.finalY + 10);
        doc.text(projectType, 14, finalY);
        doc.autoTable({
          head: [columns.map((col) => col.label)],
          headStyles: {
            fillColor: "#476E93",
            cellPadding: 3,
            valign: "bottom",
          },
          body: data.map((row) => columns.map((col) => row[col.key])),
          startY: finalY + 5,
          styles: {
            cellPadding: 2,
            fontSize: 8,
            valign: "middle",
          },
          rowPageBreak: "avoid",
        });
      };
      let cityProjects = vm.categorizedProjects;
      if (cityProjects) {
        let index = 0;
        for (let key in cityProjects) {
          if (index > 0) doc.addPage("l", "mm");

          doc.text(`${key} ${vm.selectedQuarter} Report`, 15, 10);
          doc.lastAutoTable = { finalY: 15 };
          let quarterResults = {
            newProjects: [],
            activelySellingProjects: [],
            soldOutProjects: [],
          };
          cityProjects[key].forEach((data) => {
            if (data.project.projects.newProjects)
              quarterResults.newProjects.push(
                ...data.project.projects.newProjects
              );
            if (data.project.projects.activelySellingProjects)
              quarterResults.activelySellingProjects.push(
                ...data.project.projects.activelySellingProjects
              );
            if (data.project.projects.soldOutProjects)
              quarterResults.soldOutProjects.push(
                ...data.project.projects.soldOutProjects
              );
          });

          if (quarterResults.newProjects) {
            const newProjectsData = processProjectData(
              quarterResults.newProjects
            );
            if (newProjectsData.length > 0)
              addTableToPdf(
                newProjectsData,
                "New Projects",
                (doc.lastAutoTable.finalY += 10)
              );
          }

          if (quarterResults.activelySellingProjects) {
            const activelySellingProjects = processProjectData(
              quarterResults.activelySellingProjects
            );
            if (activelySellingProjects.length > 0)
              addTableToPdf(
                activelySellingProjects,
                "Active Projects",
                (doc.lastAutoTable.finalY += 10)
              );
          }

          if (quarterResults.soldOutProjects) {
            const soldOutProjects = processProjectData(
              quarterResults.soldOutProjects
            );
            if (soldOutProjects.length > 0)
              addTableToPdf(
                soldOutProjects,
                "Sold Out Projects",
                (doc.lastAutoTable.finalY += 10)
              );
          }
          let unitBreakDownHeaders = vm.fields
            .filter((x) => x.key !== "checkbox")
            .map((item) => {
              return { ...item };
            });

          const mergedProjects = [
            ...quarterResults.newProjects,
            ...quarterResults.activelySellingProjects,
            ...quarterResults.soldOutProjects,
          ];

          const tableData = mergedProjects
            .filter((project) => project.price_date != "")
            .map((item, index) => {
              return unitBreakDownHeaders.map((header) => {
                if (header.key == "customData") {
                  return `${item.name}` + "\n" + item.developer;
                }
                if (
                  [
                    "field_mls_list_price_value",
                    "net_price",
                    "buyer_incentives",
                  ].includes(header.key)
                ) {
                  let formatPrice = Math.round(
                    Number(item[header.key])
                  ).toLocaleString();
                  return item[header.key] ? `$${formatPrice}` : "-";
                }
                if (["psf"].includes(header.key)) {
                  let formatPrice = vm.rowTwoDecimalPsf(item)
                    ? Number(item[header.key]).toFixed(2)
                    : Math.round(Number(item[header.key])).toLocaleString();
                  return item[header.key] ? `$${formatPrice}` : "-";
                }
                if (header.key == "customLocationData")
                  return `${item.address}` + "\n" + item.city;

                if (header.key == "net_psf") {
                  if (item.net_price && item.field_mls_unit_size_value) {
                    const rawNetPsf = Number(item.net_price) / Number(item.field_mls_unit_size_value);
                    let netPrice = vm.rowTwoDecimalPsf(item)
                      ? rawNetPsf.toFixed(2)
                      : Math.round(rawNetPsf).toLocaleString();
                    return netPrice ? `$${netPrice}` : "-";
                  }
                }

                if (
                  header.key == "custom_field_mls_beds_value" &&
                  typeof item[header.key == "Object"] &&
                  ![null, ""].includes(item[header.key])
                ) {
                  return (
                    `${item[header.key]["type"]}` +
                    "\n" +
                    item[header.key]["fp_specs"]
                  );
                }

                return item[header.key] ? item[header.key] : "-";
              });
            });

          const lastActiveColumnData = mergedProjects
            .filter((project) => project.price_date != "")
            .map((row) => {
              return {
                name: row.name,
                unit: row.field_mls_address_value,
                price_backup: row.price_backup,
              };
            });
          const list_price_index = vm.fields
            .filter((x) => x.key !== "checkbox")
            .findIndex((field) => field.key == "field_mls_list_price_value");

          doc.text("Unit Report", 15, (doc.lastAutoTable.finalY += 15));
          doc.autoTable({
            head: [unitBreakDownHeaders.map((header) => header.label)],
            headStyles: {
              fillColor: "#476E93",
              cellPadding: 3,
              valign: "bottom",
            },
            body: tableData,
            startY: (doc.lastAutoTable.finalY += 5),
            styles: {
              cellPadding: 2,
              fontSize: 8,
              valign: "middle",
            },
            rowPageBreak: "avoid",
            didParseCell: async (data) => {
              const { doc, cell, column } = data;
              let rowIndex = data.row.index;
              rowIndex += 1; // Adjust rowIndex to match the table data index
              if (data.cell.raw == "Project") {
                data.cell.styles.halign = "left";
              } else if (
                column.index == list_price_index &&
                data.row.section == "body"
              ) {
                data.cell.styles.textColor = vm.setColor(
                  lastActiveColumnData.filter(
                    (x) =>
                      data.row.cells[0].text.join(" ").includes(x.name) &&
                      x.unit == data.row.cells[2].raw
                  )[0].price_backup
                );
              }
              this.enhanceWordBreak(doc, cell, column);
            },
          });
          index++;
        }
      }

      //Add page numbers to each page
      const pageCount = doc.internal.getNumberOfPages();

      for (var i = 1; i <= pageCount; i++) {
        doc.setPage(i);

        const pageNumberText = "Page " + String(i) + " of " + String(pageCount);
        const xPos = doc.internal.pageSize.width - 20;
        const yPos = doc.internal.pageSize.height - 10;

        doc.setFontSize(8);
        doc.text(pageNumberText, xPos, yPos, null, null, "right"); // "right" aligns the text
      }

      window.open(
        doc.output("bloburl"),
        "_blank",
        "toolbar=no,status=no,menubar=no,scrollbars=no,resizable=no,modal=yes,top=100,left=500,width=1000,height=800"
      );
    },
    async generateReport() {
      const vm = this;
      vm.$bvToast.toast(
        `Download is in progress. Please wait for a moment while we prepare everything for you.`,
        { title: "Downloading...", variant: "info", autoHideDelay: 5000 }
      );
      try {
        vm.loading = true;
        const summaryReport = vm.filteredMlsData;
        const now = new Date();
        const reportDate = now.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
        const reportTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

        const doc = new jsPDF({ unit: 'mm', format: 'letter', orientation: 'landscape' });
        const PW = doc.internal.pageSize.width;
        const PH = doc.internal.pageSize.height;

        const C = {
          navy: [18, 40, 63],
          coral: [252, 88, 104],
          violet: [52, 51, 118],
          fg1: [18, 40, 63],
          fg2: [84, 84, 84],
          fg3: [138, 138, 138],
          success: [31, 157, 85],
          successBg: [232, 245, 238],
          danger: [212, 56, 71],
          divider: [238, 240, 244],
          borderStrong: [201, 204, 210],
          bgSubtle: [237, 242, 254],
          white: [255, 255, 255],
        };
        const M = { l: 14, r: 14 };

        const drawUtilBar = () => {
          doc.setFillColor(...C.navy);
          doc.rect(0, 0, PW, 9, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7);
          doc.setTextColor(255, 255, 255);
          const reportLabel = `REPORT · ${reportDate}`;
          doc.text(reportLabel, M.l, 5.8);
          doc.text(reportTime, M.l + doc.getTextWidth(reportLabel) + 8, 5.8);
          const brandTxt = 'REALPROPERTYDATA.CA';
          doc.text(brandTxt, PW - M.r, 5.8, { align: 'right' });
          doc.setFillColor(...C.coral);
          doc.circle(PW - M.r - doc.getTextWidth(brandTxt) - 2.5, 4.6, 0.9, 'F');
        };

        const drawPageFooter = (pageNum, total) => {
          const fy = PH - 10;
          doc.setDrawColor(...C.divider);
          doc.setLineWidth(0.2);
          doc.line(M.l, fy - 4, PW - M.r, fy - 4);
          doc.setFillColor(...C.coral);
          doc.circle(M.l + 1.2, fy - 1, 0.9, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(6.5);
          doc.setTextColor(...C.fg3);
          doc.text('REAL PROPERTY DATA · CONFIDENTIAL', M.l + 3.5, fy);
          doc.text(`PAGE ${pageNum} OF ${total}`, PW - M.r, fy, { align: 'right' });
        };

        const hexToRgb = (hex) => {
          const h = String(hex || '').replace('#', '');
          if (h.length !== 6) return C.fg1;
          return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
        };

        const drawSectionHeader = (txt, totalLabel) => {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(13);
          doc.setTextColor(...C.fg1);
          doc.text(txt, M.l, y);
          if (totalLabel) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7);
            doc.setTextColor(...C.fg3);
            doc.text(totalLabel, PW - M.r, y, { align: 'right' });
          }
          y += 3;
        };

        // ====== Page 1 Header ======
        let y = 14;
        doc.setFillColor(...C.coral);
        doc.rect(M.l, y, 10, 1.2, 'F');
        y += 4.5;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...C.violet);
        doc.text('UNIT SUMMARY REPORT', M.l, y);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(22);
        doc.setTextColor(...C.fg1);
        doc.text('Unit Summary', M.l, y + 8);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...C.violet);
        doc.text(`${summaryReport.length} UNITS · ${vm.projects.length} PROJECTS`, PW - M.r, y, { align: 'right' });
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(7);
        doc.setTextColor(...C.fg3);
        doc.text(`Generated · ${reportDate}`, PW - M.r, y + 5, { align: 'right' });

        y += 13;
        doc.setDrawColor(...C.divider);
        doc.setLineWidth(0.2);
        doc.line(M.l, y, PW - M.r, y);
        y += 5;

        // Legend
        const legend = [
          { dot: C.navy, label: 'Verified' },
          { dot: C.coral, label: 'Semi-verified' },
          { dot: C.borderStrong, label: 'Estimated' },
        ];
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        const legendItemWidths = legend.map(it => doc.getTextWidth(it.label) + 5 + 6);
        const legendTotalW = legendItemWidths.reduce((a, b) => a + b, 0) - 6;
        let lx = PW - M.r - legendTotalW;
        legend.forEach((it, i) => {
          doc.setFillColor(...it.dot);
          doc.circle(lx + 1.2, y, 1.1, 'F');
          doc.setTextColor(...C.fg2);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7.5);
          doc.text(it.label, lx + 4, y + 1.3);
          lx += legendItemWidths[i];
        });
        y += 5;

        // ====== Data prep — columns synced from tabulator ======
        const unitBreakDownHeaders = vm.getPdfColumns(vm.tabulator);

        const formattedSummary = summaryReport
          .map((item) => ({ ...item }))
          .sort((a, b) => new Date(b.price_date) - new Date(a.price_date));

        const projectsData = vm.projects.map((item) =>
          vm.projectsHeader.map((header) => {
            let value = item[header.key];
            if (['list_price', 'psf', 'net_price', 'net_psf'].includes(header.key)) {
              const n = Number(typeof value === 'string' ? value.replace(/[$,]/g, '') : value);
              if (!vm.isRealValue(value) || isNaN(n) || n === 0) return "—";
              return item._twoDp && ['psf', 'net_psf'].includes(header.key)
                ? `$${n.toFixed(2)}`
                : `$${Math.round(n).toLocaleString()}`;
            }
            return value ? value : "—";
          })
        );

        const unitPreviewData = vm.groupedBeds.map((item) =>
          vm.previewHeader.map((header) => (item[header.key] ? item[header.key] : "—"))
        );

        const tableData = formattedSummary.map((row) =>
          unitBreakDownHeaders.map((h) => vm.pdfFormatCell(row, h.key))
        );

        const priceBackupByRow = formattedSummary.map((r) => r.price_backup);
        const list_price_index = unitBreakDownHeaders.findIndex(
          (h) => h.key === "field_mls_list_price_value"
        );
        const changeIndex = unitBreakDownHeaders.findIndex(
          (h) => h.key === "custom_price_change"
        );
        const rawPriceChangeByRow = formattedSummary.map(
          (r) => r.custom_price_change
        );

        const computeColumnStyles = (headRow) => {
          const availW = PW - M.l - M.r;
          const n = headRow.length;
          if (!n) return {};
          const weights = headRow.map((label, i) => {
            if (i === 0) return 2.4;
            if (i === 1) return 1.6;
            const t = String(label || '').toLowerCase();
            if (/(price|incentive|date|status|confidence|address|plan name|listing type)/.test(t)) return 1.2;
            if (/(psf|baths|age|unit #|stalls|parking|beds)/.test(t)) return 0.7;
            return 1;
          });
          const sum = weights.reduce((a, b) => a + b, 0);
          const styles = {};
          headRow.forEach((_, i) => {
            styles[i] = { cellWidth: (weights[i] / sum) * availW };
            if (i === 0) { styles[i].halign = 'left'; styles[i].fontStyle = 'bold'; }
          });
          return styles;
        };

        const themedTable = (opts) => {
          const headRow = (opts.head && opts.head[0]) || [];
          const colCount = headRow.length;
          const baseFontSize = colCount > 18 ? 5.6 : colCount > 14 ? 6.2 : colCount > 10 ? 7 : 7.6;
          const headFontSize = Math.max(5.4, baseFontSize - 0.8);
          const userColStyles = opts.columnStyles || {};
          const columnStyles = { ...computeColumnStyles(headRow), ...userColStyles };
          const restOpts = { ...opts };
          delete restOpts.columnStyles;
          autoTable(doc, {
            startY: y,
            margin: { left: M.l, right: M.r, top: 14, bottom: 18 },
            theme: 'plain',
            rowPageBreak: 'avoid',
            tableWidth: PW - M.l - M.r,
            styles: {
              font: 'helvetica',
              fontSize: baseFontSize,
              cellPadding: { top: 1.8, right: 1.4, bottom: 1.8, left: 1.4 },
              textColor: C.fg1,
              overflow: 'linebreak',
            },
            headStyles: {
              fontStyle: 'bold', fontSize: headFontSize, textColor: C.fg2,
              halign: 'center', overflow: 'linebreak', valign: 'bottom',
              cellPadding: { top: 1.6, right: 1.4, bottom: 2, left: 1.4 },
            },
            bodyStyles: { halign: 'center', valign: 'middle' },
            columnStyles,
            ...restOpts,
            didDrawCell: (data) => {
              if (data.section === 'head') {
                doc.setDrawColor(...C.navy);
                doc.setLineWidth(0.5);
                doc.line(data.cell.x, data.cell.y + data.cell.height,
                  data.cell.x + data.cell.width, data.cell.y + data.cell.height);
              }
              if (data.section === 'body') {
                doc.setDrawColor(...C.divider);
                doc.setLineWidth(0.2);
                doc.line(data.cell.x, data.cell.y + data.cell.height,
                  data.cell.x + data.cell.width, data.cell.y + data.cell.height);
              }
              if (typeof opts.didDrawCell === 'function') opts.didDrawCell(data);
            },
          });
          y = doc.lastAutoTable.finalY + 6;
        };

        // ---- Project Summary ----
        drawSectionHeader('Project Summary', `${vm.projects.length} PROJECTS`);
        themedTable({
          head: [vm.projectsHeader.map((h) => h.label)],
          body: projectsData,
          didParseCell: (data) => {
            if (data.section === 'body' && data.cell.text && data.cell.text[0] === '—') {
              data.cell.styles.textColor = C.fg3;
              data.cell.styles.fontStyle = 'normal';
            }
          },
        });

        // ---- Unit Preview ----
        drawSectionHeader('Unit Preview', `${vm.groupedBeds.length} TYPES`);
        themedTable({
          head: [vm.previewHeader.map((h) => h.label)],
          body: unitPreviewData,
          didParseCell: (data) => {
            if (data.section === 'body' && data.cell.text && data.cell.text[0] === '—') {
              data.cell.styles.textColor = C.fg3;
              data.cell.styles.fontStyle = 'normal';
            }
          },
        });

        // ---- Unit Breakdown (new page) ----
        doc.addPage('letter', 'l');
        y = 14;
        doc.setFillColor(...C.coral);
        doc.rect(M.l, y, 10, 1.2, 'F');
        y += 4.5;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...C.violet);
        doc.text('UNIT BREAKDOWN', M.l, y);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(...C.fg1);
        doc.text('Active Listing', M.l, y + 8);
        y += 12;
        doc.setDrawColor(...C.divider);
        doc.setLineWidth(0.2);
        doc.line(M.l, y, PW - M.r, y);
        y += 5;

        themedTable({
          head: [unitBreakDownHeaders.map((h) => h.label)],
          body: tableData,
          didParseCell: (data) => {
            if (data.section !== 'body') return;
            if (data.column.index === changeIndex && changeIndex >= 0) {
              const raw = rawPriceChangeByRow[data.row.index];
              if (raw && typeof raw === 'object') {
                const pct = Number(raw.diffPercentage);
                if (!isNaN(pct) && pct !== 0) {
                  data.cell.styles.textColor = pct > 0 ? C.success : C.danger;
                  data.cell.styles.fontStyle = 'bold';
                  data.cell.text = [(pct > 0 ? '+' : '') + pct + '%'];
                } else {
                  data.cell.text = ['—'];
                }
              }
            }
            if (data.column.index === list_price_index && list_price_index >= 0) {
              const backup = priceBackupByRow[data.row.index];
              if (backup) {
                data.cell.styles.textColor = hexToRgb(vm.setColor(backup));
                data.cell.styles.fontStyle = 'bold';
              }
            }
            if (data.cell.text && data.cell.text[0] === '—') {
              data.cell.styles.textColor = C.fg3;
              data.cell.styles.fontStyle = 'normal';
            }
          },
        });

        // ====== Image pages ======
        await new Promise((resolve) => {
          this.arrayOfImages.forEach((x) => {
            doc.addPage('letter', 'l');
            let yi = 14;
            doc.setFillColor(...C.coral);
            doc.rect(M.l, yi, 10, 1.2, 'F');
            yi += 4.5;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7);
            doc.setTextColor(...C.violet);
            doc.text('FLOOR PLAN', M.l, yi);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(18);
            doc.setTextColor(...C.fg1);
            doc.text(String(x.project.name || ''), M.l, yi + 8);
            yi += 12;
            doc.setDrawColor(...C.divider);
            doc.setLineWidth(0.2);
            doc.line(M.l, yi, PW - M.r, yi);
            yi += 5;

            const facts = [
              { l: 'UNIT #', v: x.project.field_mls_address_value || '—' },
              { l: 'PRICE', v: x.project.price || '—' },
              { l: '$PSF', v: x.project.psf || '—' },
              { l: 'PRICE DATE', v: x.project.priceDate || '—' },
            ];
            const stripH = 15;
            doc.setFillColor(...C.bgSubtle);
            doc.roundedRect(M.l, yi, PW - M.l - M.r, stripH, 2, 2, 'F');
            const cw = (PW - M.l - M.r) / facts.length;
            facts.forEach((f, i) => {
              const fx = M.l + i * cw;
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(6.2);
              doc.setTextColor(...C.violet);
              doc.text(f.l, fx + 4, yi + 4.5);
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(11);
              doc.setTextColor(...C.fg1);
              doc.text(String(f.v), fx + 4, yi + 11);
            });
            yi += stripH + 6;

            const availH = PH - yi - 16;
            const availW = PW - M.l - M.r;
            let iw = x.image.width;
            let ih = x.image.height;
            const sc = Math.min(availW / iw, availH / ih);
            iw *= sc; ih *= sc;
            const ix = M.l + (availW - iw) / 2;
            doc.addImage(x.image.src, ix, yi, iw, ih);
          });
          resolve();
        });

        const total = doc.internal.getNumberOfPages();
        for (let i = 1; i <= total; i++) {
          doc.setPage(i);
          drawUtilBar();
          drawPageFooter(i, total);
        }

        // ====== Append full floor plan PDFs (floor_plan.link) ======
        const floorPlanLinks = [
          ...new Set(
            summaryReport
              .map((row) => row.floor_plan && row.floor_plan.link)
              .filter(Boolean)
          ),
        ];

        let reportBytes = doc.output("arraybuffer");
        if (floorPlanLinks.length) {
          try {
            const { PDFDocument } = await import("pdf-lib");
            const mergedDoc = await PDFDocument.load(reportBytes);
            for (const link of floorPlanLinks) {
              try {
                const bytes = await fetch(link).then((r) => r.arrayBuffer());
                const donor = await PDFDocument.load(bytes, {
                  ignoreEncryption: true,
                });
                const pages = await mergedDoc.copyPages(
                  donor,
                  donor.getPageIndices()
                );
                pages.forEach((p) => mergedDoc.addPage(p));
              } catch (e) {
                console.error("Failed to append floor plan PDF:", link, e);
              }
            }
            reportBytes = await mergedDoc.save();
          } catch (e) {
            console.error("Failed to merge floor plan PDFs:", e);
          }
        }

        this.loading = false;
        vm.$bvToast.toast(`You can now view the downloaded file`, {
          title: "Success!", variant: "success", autoHideDelay: 5000,
        });
        const reportBlob = new Blob([reportBytes], { type: "application/pdf" });
        window.open(
          URL.createObjectURL(reportBlob),
          "_blank",
          "toolbar=no,status=no,menubar=no,scrollbars=no,resizable=no,modal=yes,top=100,left=500,width=1000,height=800"
        );
      } catch (error) {
        console.error("Failed to generate report:", error);
        vm.$bvToast.toast(
          `Something went wrong. \n Please inform us to fix this for you.`,
          { title: "Oops!", variant: "danger", autoHideDelay: 5000 }
        );
      }
    },
    enhanceWordBreak(doc, cell, column) {
      if (cell === undefined) {
        return;
      }

      const hasCustomWidth = typeof cell.styles.cellWidth === "number";

      if (hasCustomWidth || cell.raw == null || cell.colSpan > 1) {
        return;
      }

      let text;

      if (cell.raw instanceof Node) {
        text = cell.raw.innerText;
      } else {
        if (typeof cell.raw == "object") {
          // not implemented yet
          // when a cell contains other cells (colSpan)
          return;
        } else {
          text = "" + cell.raw;
        }
      }

      // split cell string by spaces
      const words = text.split(/\s+/);

      // calculate longest word width
      const maxWordUnitWidth = words
        .map((s) => Math.floor(doc.getStringUnitWidth(s) * 100) / 100)
        .reduce((a, b) => Math.max(a, b), 0);
      const maxWordWidth =
        maxWordUnitWidth * (cell.styles.fontSize / doc.internal.scaleFactor);

      const minWidth = cell.padding("horizontal") + maxWordWidth;

      // update minWidth for cell & column

      if (minWidth > cell.minWidth) {
        cell.minWidth = minWidth;
      }

      if (cell.minWidth > cell.wrappedWidth) {
        cell.wrappedWidth = cell.minWidth;
      }

      if (cell.minWidth > column.minWidth) {
        column.minWidth = cell.minWidth;
      }

      if (column.minWidth > column.wrappedWidth) {
        column.wrappedWidth = column.minWidth;
      }
    },
    async getImageRatio(imgUrl) {
      const max = { height: 190, width: 133 };

      return new Promise(async (resolve) => {
        let image = new Image();
        image.src = imgUrl;

        image.onload = async function () {
          const ratio = image.height / image.width;

          if (image.height > max.height || image.width > max.width) {
            if (image.height > image.width) {
              image.height = max.height;
              image.width = image.height * (1 / ratio);
              // Making reciprocal of ratio because ration of height as width is no valid here needs width as height
            } else if (image.width > image.height) {
              image.width = max.width;
              image.height = image.width * ratio;
              // Ratio is valid here
            } else {
              image.width = max.width;
              image.height = image.width * ratio;
            }
          }
          resolve({
            status: "success",
            image: image,
          });
        };
      });
    },
    reportFormatDate(dateVal) {
      let vm = this;
      var newDate = new Date(dateVal);

      var sMonth = vm.padValue(newDate.getMonth() + 1);
      var sDay = vm.padValue(newDate.getDate());
      var sYear = newDate.getFullYear();
      var sHour = newDate.getHours();
      var sMinute = vm.padValue(newDate.getMinutes());
      var sAMPM = "AM";

      var iHourCheck = parseInt(sHour);

      if (iHourCheck > 12) {
        sAMPM = "PM";
        sHour = iHourCheck - 12;
      } else if (iHourCheck === 0) {
        sHour = "12";
      }

      sHour = vm.padValue(sHour);

      return (
        sMonth +
        "/" +
        sDay +
        "/" +
        sYear +
        " " +
        sHour +
        ":" +
        sMinute +
        " " +
        sAMPM
      );
    },
    padValue(value) {
      return value < 10 ? "0" + value : value;
    },
    exportSummary() {
      let data = {
        project: {
          header: this.projectsHeader,
          data: this.summaryProjects,
        },
        groupedData: {
          header: this.previewHeader,
          data: this.summaryGroupedBeds,
        },
      };
      var csvContent = "";
      for (const [key, value] of Object.entries(data)) {
        for (const [innerKey, innerValue] of Object.entries(value)) {
          if (innerKey == "header") {
            csvContent +=
              value[innerKey]
                .filter((header) => header.key != "view")
                .map((item) => item.label)
                .join(",") + "\n";
          }
          if (innerKey == "data" && key == "project") {
            const rows = value[innerKey].map((item) => {
              return this.projectsHeader.map((header) => {
                let cell = item[header.key];
                return typeof cell === "string" && cell.includes(",")
                  ? `"${cell.replace(/"/g, '""')}"` // Escape commas and quotes
                  : cell;
              });
            });
            rows.forEach((row, index) => {
              csvContent += row.join(",") + "\n";

              if (index == rows.length - 1) {
                csvContent += "\n"; // Add an extra newline between project entries
              }
            });
          }
          if (innerKey == "data" && key == "groupedData") {
            const rows = value[innerKey].map((item) => {
              return this.previewHeader.map((header) => {
                let cell = item[header.key];
                return typeof cell === "string" && cell.includes(",")
                  ? `"${cell.replace(/"/g, '""')}"` // Escape commas and quotes
                  : cell;
              });
            });
            rows.forEach((row) => {
              csvContent += row.join(",") + "\n";
            });
          }
        }
      }
      var pom = document.createElement("a");
      var blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      var url = URL.createObjectURL(blob);
      pom.href = url;
      pom.setAttribute("download", "exported-summary.csv");
      pom.click();
    },
    exportData() {
      const vm = this;
      try {
        let cusData = vm.filteredMlsData;
        let allData = cusData.map((item) => ({
          ...item,
          field_mls_address_value: item.floor_plan.preview
            ? `=HYPERLINK("${item.floor_plan.preview}", "${item.field_mls_address_value}")`
            : item.field_mls_address_value,
          custom_field_mls_beds_value:
            item.custom_field_mls_beds_value.type +
            `${item.custom_field_mls_beds_value.fp_specs ? " + " : ""}` +
            item.custom_field_mls_beds_value.fp_specs,
          custom_price_change:
            item.custom_price_change.diffPercentage != 0
              ? item.custom_price_change.diffPercentage + "%"
              : "-",
        }));

        const csvContent = this.convertToCsv(allData);
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "exported-unit-report.csv");
        link.click();
        URL.revokeObjectURL(url); // Clean up the URL after download

        vm.$bvToast.toast(`You can now view the downloaded file`, {
          title: "Success!",
          variant: "success",
          autoHideDelay: 5000,
        });
      } catch (error) {
        console.error("Failed to fetch user data:", error);
        vm.$bvToast.toast(
          `${"Something went wrong. \n Please inform us to fix this for you."}`,
          {
            title: "Oops!",
            variant: "danger",
            autoHideDelay: 5000,
          }
        );
      }
    },
    convertToCsv(data) {
      const vm = this;

      let headers = [
        {
          key: "name",
          label: "Project",
        },
        {
          key: "developer",
          label: "Developer",
        },
        {
          key: "address",
          label: "Address",
        },
        {
          key: "city",
          label: "City",
        },
      ];
      headers.push(
        ...vm.fields.filter(
          (header) =>
            !["checkbox", "customData", "customLocationData"].includes(
              header.key
            )
        )
      );
      const rows = data.map((row) => {
        // $PSF / Net $PSF export as whole dollars, or two decimals for rental
        // units (matches the table and the MarketOverview export).
        const twoDp = vm.rowTwoDecimalPsf(row);
        return headers.map((header) => {
          let cell = row[header.key];
          if (
            ["psf", "custom_net_psf"].includes(header.key) &&
            vm.isRealValue(cell)
          ) {
            const n = Number(
              typeof cell === "string" ? cell.replace(/[$,]/g, "") : cell
            );
            if (!isNaN(n)) {
              cell = twoDp
                ? `$${n.toFixed(2)}`
                : `$${Math.round(n).toLocaleString()}`;
            }
          }
          // List Price — dash out empty / NaN / zero values instead of
          // exporting "null" or "0".
          if (header.key === "field_mls_list_price_value") {
            const n = Number(
              typeof cell === "string" ? cell.replace(/[$,]/g, "") : cell
            );
            cell =
              !vm.isRealValue(cell) || isNaN(n) || n === 0
                ? "-"
                : `$${Math.round(n).toLocaleString()}`;
          }
          return typeof cell === "string" && cell.includes(",")
            ? `"${cell.replace(/"/g, '""')}"`
            : cell;
        });
      });
      let forRowheaders = headers.map((item) => item.label);
      const headerRow = forRowheaders.join(",");
      const csvRows = [headerRow, ...rows.map((row) => row.join(","))];

      return csvRows.join("\n");
    },
    onFiltered(filteredItems) {
      this.filtered_data = filteredItems;
      this.totalRows = filteredItems.length;
      this.generateArrayOfImages();
    },
    containsString(obj, searchString) {
      return Object.values(obj).some((value) => {
        return String(value).toLowerCase().includes(searchString.toLowerCase());
      });
    },
    getDiffDays(date) {
      let date1 = this.currentDate;
      let date2 = new Date(date);

      if (date == "Invalid Date") return null;

      let Difference_In_Days = Math.floor(
        (date1 - date2) / (1000 * 60 * 60 * 24)
      );

      return Difference_In_Days;
    },
    filterTable(row, criteria) {
      let bath = new Array();
      let type = new Array();
      let fp = new Array();
      let status = new Array();
      let priceDate = new Array();
      let confDate = new Array();
      let listingType = new Array();
      let projectStatus = new Array();
      let age = new Array();

      const matchesCriteria = (
        bathCheck,
        typeCheck,
        fpCheck,
        statusCheck,
        priceDateCheck,
        confDateCheck,
        listingTypeCheck,
        projectStatusCheck,
        ageCheck
      ) => {
        let diffDays = this.getDiffDays(new Date(row.price_date));
        let diffConfDays = this.getDiffDays(new Date(row.sold_date));

        return (
          (!ageCheck || age.some((x) => x == row.customAge)) &&
          (!bathCheck || bath.includes(row.field_mls_baths_value)) &&
          (!typeCheck || type.includes(row.custom_field_mls_beds_value.type)) &&
          (!fpCheck || fp.some((item) => row.fp_specs.includes(item))) &&
          (!statusCheck || status.some((item) => row.status.includes(item))) &&
          (!priceDateCheck ||
            priceDate.some(
              (date) => diffDays <= Number(date) && diffDays != null
            )) &&
          (!confDateCheck ||
            confDate.some(
              (date) => diffConfDays <= Number(date) && diffConfDays != null
            )) &&
          (!listingTypeCheck ||
            listingType.includes(row.custom_listing_type)) &&
          (!projectStatusCheck ||
            projectStatus.includes(row.custom_project_status)) &&
          this.containsString(row, criteria.filterText)
        );
      };
      criteria.filterArray.forEach((val) => {
        if (val.startsWith("bath_")) bath.push(val.replace("bath_", ""));
        if (val.startsWith("bed_")) type.push(val.replace("bed_", ""));
        if (val.startsWith("fp_")) fp.push(val.replace("fp_", ""));
        if (val.startsWith("st_")) status.push(val.replace("st_", ""));
        if (val.startsWith("pd_")) priceDate.push(val.match(/(\d+)/)[0]);
        if (val.startsWith("conf_")) confDate.push(val.match(/(\d+)/)[0]);
        if (val.startsWith("lt_")) listingType.push(val.replace("lt_", ""));
        if (val.startsWith("ps_")) projectStatus.push(val.replace("ps_", ""));
        if (val.startsWith("age_")) age.push(val.match(/\d+/)[0]);
      });

      return criteria.filterArray.length > 0
        ? matchesCriteria(
          bath.length > 0,
          type.length > 0,
          fp.length > 0,
          status.length > 0,
          priceDate.length > 0,
          confDate.length > 0,
          listingType.length > 0,
          projectStatus.length > 0,
          age.length > 0
        )
        : this.containsString(row, criteria.filterText);
    },
    setColor(data) {
      let color = "";
      if (data) {
        switch (data.toLocaleLowerCase()) {
          case "verified":
            color = "#0b36dc";
            break;

          case "educated estimated":
            color = "#dc3545";
            break;

          case "semi-verified":
            color = "#FF6F16";
            break;

          default:
            color = "#000";
            break;
        }
      }
      return color;
    },

    getPdfColumns(tab) {
      if (tab && typeof tab.getColumns === "function") {
        const cols = tab
          .getColumns()
          .filter((c) => c.isVisible && c.isVisible() && c.getField())
          .map((c) => ({
            label: (c.getDefinition && c.getDefinition().title) || c.getField(),
            key: c.getField(),
          }))
          .filter((c) => c.key !== "checkbox" && c.key !== "entity_id");
        if (cols.length) return cols;
      }
      return (this.fields || [])
        .filter((x) => x.key !== "checkbox")
        .map((x) => ({ label: x.label, key: x.key }));
    },

    pdfFormatCell(row, key) {
      const DASH = "—";
      const v = row[key];
      const isEmpty = (x) =>
        x === null || x === undefined || x === "" || x === "-" || x === "0" || x === 0;

      if (key === "customData") {
        const name =
          (v && typeof v === "object" && v.projectName) || row.name || "";
        const dev =
          (v && typeof v === "object" && v.project) || row.developer || "";
        return name || dev ? `${name}\n${dev}` : DASH;
      }
      if (key === "customLocationData") {
        if (v && typeof v === "object") {
          const addr = v.address || "";
          const city = v.municipality || v.neighbour || "";
          return addr || city ? `${addr}\n${city}` : DASH;
        }
        return isEmpty(v) ? DASH : String(v);
      }
      if (key === "custom_field_mls_beds_value") {
        if (v && typeof v === "object") {
          const type = v.type || "";
          const fp = v.fp_specs && v.fp_specs !== "None" ? v.fp_specs : "";
          return type ? (fp ? `${type}\n${fp}` : type) : DASH;
        }
        return isEmpty(v) ? DASH : String(v);
      }
      if (["field_mls_list_price_value", "net_price", "buyer_incentives"].includes(key)) {
        if (isEmpty(v)) return DASH;
        const n = Number(typeof v === "string" ? v.replace(/[$,]/g, "") : v);
        return isNaN(n) || n === 0 ? DASH : `$${Math.round(n).toLocaleString()}`;
      }
      if (key === "psf" || key === "custom_net_psf") {
        if (isEmpty(v)) return DASH;
        const n = Number(typeof v === "string" ? v.replace(/[$,]/g, "") : v);
        if (isNaN(n) || n === 0) return DASH;
        return this.rowTwoDecimalPsf(row)
          ? `$${n.toFixed(2)}`
          : `$${Math.round(n).toLocaleString()}`;
      }
      if (key === "field_mls_unit_size_value") {
        const n = Number(v);
        return n && !isNaN(n) ? Math.round(n).toLocaleString() : DASH;
      }
      if (key === "custom_age") {
        if (v === 0 || v === "0") return "New";
        const n = Number(v);
        return !n || isNaN(n) ? DASH : `${n}d`;
      }
      if (key === "custom_price_change") {
        if (v && typeof v === "object") {
          const pct = Number(v.diffPercentage);
          return isNaN(pct) || pct === 0 ? DASH : (pct > 0 ? "+" : "") + pct + "%";
        }
        return DASH;
      }
      return isEmpty(v) ? DASH : String(v);
    },
    sortTable(a, b, key, sortDesc) {
      const aDate = new Date(a[key]);
      const bDate = new Date(b[key]);
      if (["price_date", "sold_date"].includes(key)) {
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          let aToNum = aDate.getTime();
          let bToNum = bDate.getTime();

          return aToNum < bToNum ? -1 : aToNum > bToNum ? 1 : 0;
        }
        if (sortDesc) {
          if (isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) return -1;

          if (isNaN(bDate.getTime()) && !isNaN(aDate.getTime())) return 1;

          return 0;
        } else {
          if (isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) return 1;

          if (isNaN(bDate.getTime()) && !isNaN(aDate.getTime())) return -1;

          return 0;
        }
      }
    },
    utcLink(utc) {
      let link =
        "/type?project=" +
        utc.city_entity_id +
        "&fid=" +
        utc.field_project_type_collection_value +
        "&sid=" +
        utc.field_utc_status_tid +
        "&uid=" +
        utc.field_unit_type_value;
      return link;
    },
    formatData(value, key, item) {
      if (["-", 0, "", null].includes(value)) return "-";
      let res = null;
      const twoDp = this.rowTwoDecimalPsf(item);
      switch (key) {
        case "psf":
          if (twoDp) res = `$${Number(value).toFixed(2)}`;
          else res = `$${Math.round(value).toLocaleString()}`;
          break;

        case "custom_net_psf":
          if (twoDp) res = `$${Number(value).toFixed(2)}`;
          else res = `$${Math.round(value).toLocaleString()}`;
          break;

        default:
          if (twoDp) res = `$${Number(value).toFixed(2)}`;
          else res = `$${Math.round(value).toLocaleString()}`;
          break;
      }

      return res;
    },
    setRpdHeaderFields() {
      const vm = this;
      var commonFields;
      var custom_header;
      if (this.isMarketRental) {
        commonFields = [
          { label: "Selected", key: "checkbox", thClass: "w-small" },
          {
            label: "Project & Developer",
            key: "customData",
            thClass: "text-left pl-3 w-25",
            sortable: true,
          },
          { label: "Location", key: "customLocationData", sortable: true },
          { label: "Unit #", key: "field_mls_address_value", sortable: true },
          {
            label: "Type",
            key: "custom_field_mls_beds_value",
            sortable: true,
          },
          { label: "Baths", key: "field_mls_baths_value", sortable: true },
          {
            label: "Unit Size",
            key: "field_mls_unit_size_value",
            formatter: (value) => {
              return Number(value) ? Math.round(value).toLocaleString() : "-";
            },
            sortable: true,
          },
          {
            label: "List Price",
            key: "field_mls_list_price_value",
            tdClass: "_w-350",
            thClass: "_w-350",
            sortable: true,
          },
          {
            label: "$PSF",
            key: "psf",
            sortable: true,
          },
          {
            label: "Incentives",
            key: "buyer_incentives",
            sortable: true,
          },
          {
            label: "Net Price",
            key: "net_price",
            sortable: true,
          },
          { label: "% Change", key: "custom_price_change", sortable: true },
          {
            label: "Net $PSF",
            key: "custom_net_psf",
            sortable: true,
          },
          { label: "Price Date", key: "price_date", sortable: true },
          {
            label: "Listing Type",
            key: "custom_listing_type",
            sortable: true,
          },
          { label: "Status", key: "status", sortable: true },
          {
            label: "Leased Date",
            key: "sold_date",
            sortable: true,
          }
        ];
        custom_header = [
          {
            label: "Net Leased Price",
            key: "custom_net_sold_price",
            sortable: true,
          },
          {
            label: "Net Leased $PSF",
            key: "custom_net_sold_psf",
            sortable: true,
          },
          {
            label: "Leased Price",
            key: "sold_price",
            sortable: true,
          },
          { label: "Age", key: "customAge", sortable: true },
          { label: "Balcony", key: "balcony", sortable: true },
          { label: "Balcony Area", key: "balcony_area", sortable: true },
          {
            label: "View Orientation",
            key: "view_orientation",
            sortable: true,
          },
          { label: "Parking", key: "parking" },
          { label: "Stalls", key: "stalls" },
          {
            label: "Construction Status",
            key: "construction_status",
            sortable: true,
          },
          { label: "Product Type", key: "product_type", sortable: true },
          { label: "Project Type", key: "project_type", sortable: true },
          { label: "Neighbourhood", key: "neighbour", sortable: true },
          { label: "Price Status", key: "price_backup", sortable: true },
          { label: "Occupancy Date", key: "occupancy_date", sortable: true },
          {
            label: "Sale Start Date",
            key: "sales_start_date",
            formatter: (value) => {
              let date = new Date(Number(value) * 1000);
              const options = {
                day: "2-digit",
                month: "short",
                year: "2-digit",
              };
              const formattedDate = date
                .toLocaleDateString("en-GB", options)
                .replace(/ /g, "-");
              return formattedDate;
            },
            sortable: true,
          },
          {
            label: "Plan Name",
            key: "field_mls_plan_name_value",
            sortable: true,
          },
          {
            label: "Project Status",
            key: "custom_project_status",
            sortable: true,
          },
        ];
      } else {
        commonFields = [
          { label: "Selected", key: "checkbox", thClass: "w-small" },
          {
            label: "Project & Developer",
            key: "customData",
            thClass: "text-left pl-3 w-25",
            sortable: true,
          },
          { label: "Location", key: "customLocationData", sortable: true },
          { label: "Unit #", key: "field_mls_address_value", sortable: true },
          {
            label: "Type",
            key: "custom_field_mls_beds_value",
            sortable: true,
          },
          { label: "Baths", key: "field_mls_baths_value", sortable: true },
          {
            label: "Unit Size",
            key: "field_mls_unit_size_value",
            formatter: (value) => {
              return Number(value) ? Math.round(value).toLocaleString() : "-";
            },
            sortable: true,
          },
          {
            label: "List Price",
            key: "field_mls_list_price_value",
            tdClass: "_w-350",
            thClass: "_w-350",
            sortable: true,
          },
          {
            label: "$PSF",
            key: "psf",
            sortable: true,
          },
          {
            label: "Incentives",
            key: "buyer_incentives",
            sortable: true,
          },
          {
            label: "Net Price",
            key: "net_price",
            sortable: true,
          },
          { label: "% Change", key: "custom_price_change", sortable: true },
          {
            label: "Net $PSF",
            key: "custom_net_psf",
            sortable: true,
          },
          { label: "Price Date", key: "price_date", sortable: true },
          {
            label: "Listing Type",
            key: "custom_listing_type",
            sortable: true,
          },
          { label: "Status", key: "status", sortable: true },
          { label: "Sold / Leased Date", key: "sold_date", sortable: true },
        ];

        custom_header = [
          {
            label: "Sold Price",
            key: "sold_price",
            sortable: true,
          },
          { label: "Age", key: "customAge", sortable: true },
          { label: "Balcony", key: "balcony", sortable: true },
          { label: "Balcony Area", key: "balcony_area", sortable: true },
          {
            label: "View Orientation",
            key: "view_orientation",
            sortable: true,
          },
          { label: "Parking", key: "parking" },
          { label: "Stalls", key: "stalls" },
          {
            label: "Construction Status",
            key: "construction_status",
            sortable: true,
          },
          { label: "Product Type", key: "product_type", sortable: true },
          { label: "Project Type", key: "project_type", sortable: true },
          { label: "Neighbourhood", key: "neighbour", sortable: true },
          { label: "Price Status", key: "price_backup", sortable: true },
          { label: "Occupancy Date", key: "occupancy_date", sortable: true },
          {
            label: "Sale Start Date",
            key: "sales_start_date",
            formatter: (value) => {
              let date = new Date(Number(value) * 1000);
              const options = {
                day: "2-digit",
                month: "short",
                year: "2-digit",
              };
              const formattedDate = date
                .toLocaleDateString("en-GB", options)
                .replace(/ /g, "-");
              return formattedDate;
            },
            sortable: true,
          },
          {
            label: "Plan Name",
            key: "field_mls_plan_name_value",
            sortable: true,
          },
          {
            label: "Project Status",
            key: "custom_project_status",
            sortable: true,
          },
        ];
      }

      if (this.isAdmin) {
        let confIndex = vm.previewHeader.findIndex(
          (header) => header.key == "Sold %"
        );
        vm.previewHeader.splice(2, 0, {
          key: "Pkg",
          label: "Pkg",
          sortable: true,
        });
        vm.previewHeader.splice(++confIndex, 0, {
          key: "Conf Sold",
          label: "Conf Sold",
          sortable: true,
        });
      }
      vm.fields = commonFields;
      vm.field_selected = JSON.parse(JSON.stringify(commonFields));
      vm.list2 = JSON.parse(JSON.stringify(custom_header));
    },

    initTabulator() {
      const vm = this;
      if (this.tabulator) {
        this.tabulator.destroy();
        this.tabulator = null;
      }
      if (!this.$refs.tabulatorTable) return;
      this.tabulator = new Tabulator(this.$refs.tabulatorTable, {
        data: this.filteredMlsData || [],
        layout: 'fitDataStretch',
        placeholder: "<div class='ur-placeholder'>No units found</div>",
        reactiveData: false,
        height: "100%",
        rowHeight: 42,
        pagination: 'local',
        paginationSize: this.perPage || 15,
        paginationSizeSelector: [15, 25, 50, 100],
        movableColumns: true,
        selectable: true,
        initialSort: [{ column: 'price_date', dir: 'desc' }],
        columns: wrapColumns(this.buildColumnDefs(this.fields || [])),
      });

      this.tabulator.on('rowClick', (e, row) => {
        vm.popHistory(row.getData());
        vm.showListingModal = true;
      });

      this.tabulator.on('rowSelectionChanged', (data, rows) => {
        vm.selectedCount = rows.length;
        if (vm.filterToSelected && !rows.length) {
          vm.filterToSelected = false;
          if (vm.selectionFilterRefs) {
            vm.tabulator.removeFilter(vm._selectionFilter);
            vm.selectionFilterRefs = null;
            vm._selectionFilter = null;
          }
        }
      });
    },

    toggleFilterSelected() {
      if (!this.tabulator) return;
      if (this.filterToSelected) {
        if (this._selectionFilter) {
          this.tabulator.removeFilter(this._selectionFilter);
          this._selectionFilter = null;
        }
        this.selectionFilterRefs = null;
        this.filterToSelected = false;
      } else {
        const sel = this.tabulator.getSelectedData();
        if (!sel.length) return;
        this.selectionFilterRefs = new Set(sel);
        const refs = this.selectionFilterRefs;
        this._selectionFilter = (data) => refs.has(data);
        this.tabulator.addFilter(this._selectionFilter);
        this.filterToSelected = true;
      }
    },

    initSummaryTabulators() {
      const vm = this;
      // Built once and kept: the views are v-show'd, so the containers survive
      // a tab switch and rebuilding would only throw away the user's sorting.
      if (this.$refs.summaryProjectsTable && !this.summaryProjectsTab) {
        this.summaryProjectsTab = new Tabulator(this.$refs.summaryProjectsTable, {
          data: this.summaryProjects,
          layout: 'fitColumns',
          // Bounded height hands the scroll to Tabulator, which is what keeps
          // the header row and the Total / Average line pinned. Capped rather
          // than fixed, so a short list still sizes to its rows.
          maxHeight: '52vh',
          rowHeight: 42,
          placeholder: "<div class='ur-placeholder'>No projects found</div>",
          columnCalcs: 'bottom',
          columns: wrapColumns([
            {
              title: 'Projects', field: 'name',
              formatter: (cell) => {
                const d = cell.getData();
                return `<a href="${d.link}" target="_blank" class="ur-pname ur-plink">${cell.getValue()}</a>`;
              },
              bottomCalc: () => 'Total / Average',
            },
            {
              title: 'Units', field: 'units', hozAlign: 'center',
              bottomCalc: () => vm.getUnitReportSummaryAverage.units,
            },
            {
              title: 'Size', field: 'size', hozAlign: 'center',
              bottomCalc: () => vm.getUnitReportSummaryAverage.size,
            },
            {
              title: 'List Price', field: 'list_price', hozAlign: 'center',
              formatter: (cell) => vm.isRealValue(cell.getValue()) ? `$${Math.round(Number(cell.getValue())).toLocaleString()}` : '-',
              bottomCalc: () => `$${Math.round(Number(vm.getUnitReportSummaryAverage.list_price)).toLocaleString()}`,
            },
            {
              title: '$PSF', field: 'psf', hozAlign: 'center',
              formatter: (cell) => !vm.isRealValue(cell.getValue()) ? '-' : (cell.getRow().getData()._twoDp ? `$${Number(cell.getValue()).toFixed(2)}` : `$${Number(cell.getValue()).toLocaleString()}`),
              bottomCalc: () => `$${vm.summaryAvailability.psf}`,
            },
            {
              title: 'Net Price', field: 'net_price', hozAlign: 'center',
              formatter: (cell) => vm.isRealValue(cell.getValue()) ? `$${Number(cell.getValue()).toLocaleString()}` : '-',
              bottomCalc: () => `$${vm.getUnitReportSummaryAverage.net_price.toLocaleString()}`,
            },
            {
              title: 'Net $PSF', field: 'net_psf', hozAlign: 'center',
              formatter: (cell) => !vm.isRealValue(cell.getValue()) ? '-' : (cell.getRow().getData()._twoDp ? `$${Number(cell.getValue()).toFixed(2)}` : `$${Number(cell.getValue()).toLocaleString()}`),
              bottomCalc: () => `$${vm.summaryAvailability.net_psf}`,
            },
            {
              // Opens every unit row this line was averaged from. `view` is not
              // a data key, so exportSummary's projectsHeader never sees it.
              title: '', field: 'view', hozAlign: 'center', headerSort: false, width: 92,
              formatter: () => `<button class="ur-cell-btn">View</button>`,
              bottomCalc: () => '',
              cellClick: (e, cell) => vm.openProjectDetail(cell.getData().name),
            },
          ]),
        });
      }

      if (this.$refs.summaryBedsTable && !this.summaryBedsTab) {
        this.summaryBedsTab = new Tabulator(this.$refs.summaryBedsTable, {
          data: this.summaryGroupedBeds,
          layout: 'fitColumns',
          maxHeight: '52vh',
          rowHeight: 42,
          placeholder: "<div class='ur-placeholder'>No unit types found</div>",
          columnCalcs: 'bottom',
          columns: wrapColumns([
            {
              title: 'Type', field: 'Type', minWidth: 120,
              formatter: (cell) => {
                const parts = String(cell.getValue() || '').split(' ');
                return `<span class="ur-pname">${parts[0]} ${parts.slice(1).join(' ')}</span>`;
              },
              bottomCalc: () => 'Total / Average',
              sorter: (a, b) => String(a).localeCompare(String(b)),
            },
            { title: 'Baths', field: 'Baths', hozAlign: 'center', bottomCalc: () => '' },
            {
              title: 'Units', field: '# Units', hozAlign: 'center',
              bottomCalc: () => vm.getGroupedBedsAverage.units,
            },
            {
              title: 'Units %', field: 'Units %', hozAlign: 'center',
              bottomCalc: () => vm.getGroupedBedsAverage.units_percentage ? `${vm.getGroupedBedsAverage.units_percentage}%` : '',
            },
            {
              title: vm.isMarketRental ? 'Conf Leased' : 'Conf Sold', field: 'Conf Sold', hozAlign: 'center',
              bottomCalc: () => vm.isRealValue(vm.getGroupedBedsAverage.conf_sold) ? Math.round(vm.getGroupedBedsAverage.conf_sold) : '-',
            },
            {
              title: vm.isMarketRental ? 'Leased %' : 'Sold %', field: 'Sold %', hozAlign: 'center',
              bottomCalc: () => '',
            },
            {
              title: 'Avg Size', field: 'Avg Size', hozAlign: 'center',
              sorter: (a, b) => Number(String(a).replace(',', '')) - Number(String(b).replace(',', '')),
              bottomCalc: () => vm.getGroupedBedsAverage.size.toLocaleString(),
            },
            {
              title: 'List Price', field: 'List Price', hozAlign: 'center',
              bottomCalc: () => `$${vm.getGroupedBedsAverage.list_price.toLocaleString()}`,
            },
            {
              title: '$PSF', field: '$PSF', hozAlign: 'center',
              bottomCalc: () => `$${vm.summaryAvailability.psf}`,
            },
            {
              title: 'Net Price', field: 'Net Price', hozAlign: 'center',
              bottomCalc: () => `$${vm.getGroupedBedsAverage.net_price.toLocaleString()}`,
            },
            {
              title: 'Net $PSF', field: 'Net $PSF', hozAlign: 'center',
              bottomCalc: () => `$${vm.summaryAvailability.net_psf}`,
            },
            {
              title: '', field: 'view', hozAlign: 'center', headerSort: false, width: 120,
              formatter: () => `<button class="ur-cell-btn">View Unit Mix</button>`,
              bottomCalc: () => '',
              cellClick: (e, cell) => {
                const item = cell.getData();
                const idx = vm.groupedBeds.findIndex(r => r.Type === item.Type && r.Baths === item.Baths);
                vm.setUnits(item['Units'], idx !== -1 ? idx : 0);
              },
            },
          ]),
        });
      }
    },

    destroySummaryTabulators() {
      if (this.summaryProjectsTab) { this.summaryProjectsTab.destroy(); this.summaryProjectsTab = null; }
      if (this.summaryBedsTab) { this.summaryBedsTab.destroy(); this.summaryBedsTab = null; }
    },

    // The unit rows behind one project of the summary. Same column definitions
    // as the main report, minus the selection checkbox — this view is a
    // read-through, not another place to build a selection from.
    initProjectDetailTabulator() {
      const vm = this;
      if (!this.$refs.projectDetailTable) return;
      if (this.projectDetailTab) {
        this.projectDetailTab.replaceData(this.projectDetailRows);
        return;
      }
      this.projectDetailTab = new Tabulator(this.$refs.projectDetailTable, {
        data: this.projectDetailRows,
        layout: 'fitDataStretch',
        placeholder: "<div class='ur-placeholder'>No units found</div>",
        reactiveData: false,
        height: "100%",
        rowHeight: 42,
        pagination: 'local',
        paginationSize: this.perPage || 15,
        paginationSizeSelector: [15, 25, 50, 100],
        movableColumns: true,
        initialSort: [{ column: 'price_date', dir: 'desc' }],
        columns: wrapColumns(this.projectDetailColumnDefs),
      });

      this.projectDetailTab.on('rowClick', (e, row) => {
        vm.popHistory(row.getData());
        vm.showListingModal = true;
      });
    },

    buildColumnDefs(fields) {
      const vm = this;
      if (!fields || !fields.length) return [];

      const colMap = {
        checkbox: {
          formatter: 'rowSelection',
          titleFormatter: 'rowSelection',
          hozAlign: 'center',
          headerHozAlign: 'center',
          width: 45,
          headerSort: false,
          frozen: true,
          cellClick(e) { e.stopPropagation(); },
        },
        customData: {
          title: 'Project & Developer',
          field: 'customData',
          width: 240,
          minWidth: 200,
          // Frozen alongside the selection column so the project stays visible
          // while the rest of the row scrolls horizontally.
          frozen: true,
          cssClass: 'ur-col-project',
          sorter(a, b) {
            const aName = (a && a.projectName) ? a.projectName : '';
            const bName = (b && b.projectName) ? b.projectName : '';
            return aName.localeCompare(bName);
          },
          formatter(cell) {
            const v = cell.getValue();
            if (!v) return '-';
            const name = v.projectName || '';
            const dev = v.project || '';
            const link = v.href_link || '';
            const nameEl = link
              ? `<a href="${link}" target="_blank" class="ur-pname ur-plink" onclick="event.stopPropagation()" style="font-weight:600;">${name}</a>`
              : `<span class="ur-pname">${name}</span>`;
            const devEl = dev ? `<span class="ur-pdev">${dev}</span>` : '';
            return `<div class="ur-pcell">${nameEl}${devEl}</div>`;
          },
        },
        customLocationData: {
          title: 'Location',
          field: 'customLocationData',
          width: 110,
          minWidth: 90,
          sorter(a, b) {
            const aAddr = (a && a.address) ? a.address : '';
            const bAddr = (b && b.address) ? b.address : '';
            return aAddr.localeCompare(bAddr);
          },
          formatter(cell) {
            const v = cell.getValue();
            if (!v) return '-';
            const city = v.municipality || v.neighbour || '';
            return `<div class="ur-cell-loc" style="cursor:pointer; font-size:12px;">
              <div>${v.address || ''}</div>
              <div class="ur-cell-loc__city">${city}</div>
            </div>`;
          },
          cellClick(e, cell) {
            const row = cell.getRow().getData();
            vm.popHistory(row);
            vm.showListingModal = true;
          },
        },
        field_mls_address_value: {
          title: 'Unit #',
          field: 'field_mls_address_value',
          width: 80,
          formatter(cell) {
            const val = cell.getValue();
            const row = cell.getRow().getData();
            if (row.floor_plan && row.floor_plan.fid) {
              return `<u class="text-primary" style="cursor:pointer">${val}</u>`;
            }
            return `<span class="ur-unit-link">#${val}</span>`;
          },
          cellClick(e, cell) {
            const row = cell.getRow().getData();
            if (row.floor_plan && row.floor_plan.fid) {
              // Stop the click from also triggering the row-level handler,
              // which would open the Listing Summary modal on top of the
              // floor-plan preview (mirrors the checkbox column above).
              e.stopPropagation();
              vm.$bvModal.show('mls-fp-' + row.floor_plan.fid);
            }
          },
        },
        custom_field_mls_beds_value: {
          title: 'Type',
          field: 'custom_field_mls_beds_value',
          sorter(a, b) {
            const aType = (a && a.type) ? a.type : '';
            const bType = (b && b.type) ? b.type : '';
            return aType.localeCompare(bType);
          },
          formatter(cell) {
            const v = cell.getValue();
            if (!v) return '-';
            const specs = v.fp_specs ? `<div style="font-size:10px;color:#8A8A8A;text-transform:uppercase;letter-spacing:0.08em">${v.fp_specs}</div>` : '';
            return `<div style="cursor:pointer"><strong>${v.type || ''}</strong>${specs}</div>`;
          },
          cellClick(e, cell) {
            const row = cell.getRow().getData();
            vm.popHistory(row);
            vm.showListingModal = true;
          },
        },
        custom_price_change: {
          title: '% Change',
          field: 'custom_price_change',
          sorter(a, b) {
            const aVal = (a && a.diffPercentage) ? Number(a.diffPercentage) : 0;
            const bVal = (b && b.diffPercentage) ? Number(b.diffPercentage) : 0;
            return aVal - bVal;
          },
          formatter(cell) {
            const v = cell.getValue();
            if (!v || v.diffPercentage == 0) return `<span style="color:#C9CCD2">&mdash;</span>`;
            const cls = Number(v.diffPercentage) > 0 ? 'ur-change-pill--up' : 'ur-change-pill--dn';
            return `<span class="ur-change-pill ${cls}">${v.diffPercentage}%</span>`;
          },
        },
        status: {
          title: 'Status',
          field: 'status',
          formatter(cell) {
            const val = cell.getValue() || '';
            const cls = val === 'Active' ? 'ur-status-pill--active'
              : ['Sold', 'Leased'].includes(val) ? 'ur-status-pill--sold' : '';
            return `<span class="ur-status-pill ${cls}"><span class="ur-status-pill__dot"></span>${val}</span>`;
          },
        },
        custom_listing_type: {
          title: 'Listing Type',
          field: 'custom_listing_type',
          formatter(cell) {
            return listingTypePillHtml(cell.getValue(), '');
          },
        },
        net_price: {
          title: 'Net Price',
          field: 'net_price',
          sorter: 'number',
          formatter(cell) {
            const val = cell.getValue();
            if (!val || val === '-') return '-';
            return `<span style="cursor:pointer">$${Math.round(parseInt(val)).toLocaleString('en-US')}</span>`;
          },
          cellClick(e, cell) {
            const row = cell.getRow().getData();
            vm.popHistory(row);
            vm.showListingModal = true;
          },
        },
        field_mls_list_price_value: {
          title: 'List Price',
          field: 'field_mls_list_price_value',
          sorter: 'number',
          formatter(cell) {
            const val = cell.getValue();
            const row = cell.getRow().getData();
            if (!val || val === '-') return '-';
            const color = vm.setColor(row.price_backup);
            return `<span style="color:${color};cursor:pointer">$${Math.round(parseInt(val)).toLocaleString('en-US')}</span>`;
          },
          cellClick(e, cell) {
            const row = cell.getRow().getData();
            vm.popHistory(row);
            vm.showListingModal = true;
          },
        },
        field_mls_unit_size_value: {
          title: 'Unit Size',
          field: 'field_mls_unit_size_value',
          sorter: 'number',
          formatter(cell) {
            const val = cell.getValue();
            return Number(val) ? Math.round(val).toLocaleString() : '-';
          },
        },
        psf: {
          title: '$PSF',
          field: 'psf',
          sorter: 'number',
          formatter(cell) {
            const val = cell.getValue();
            if (!val || val === '-') return '-';
            return vm.rowTwoDecimalPsf(cell.getRow().getData()) ? `$${Number(val).toFixed(2)}` : `$${Math.round(parseInt(val)).toLocaleString('en-US')}`;
          },
        },
        custom_net_psf: {
          title: 'Net $PSF',
          field: 'custom_net_psf',
          sorter: 'number',
          formatter(cell) {
            const val = cell.getValue();
            if (!val || val === '-') return '-';
            return vm.rowTwoDecimalPsf(cell.getRow().getData()) ? `$${Number(val).toFixed(2)}` : `$${Math.round(parseInt(val)).toLocaleString('en-US')}`;
          },
        },
        buyer_incentives: {
          title: 'Incentives',
          field: 'buyer_incentives',
          sorter: 'number',
          formatter(cell) {
            const val = cell.getValue();
            if (!val || val === '-' || val == 0) return '-';
            return `$${Math.round(parseInt(val)).toLocaleString('en-US')}`;
          },
        },
        price_date: {
          title: 'Price Date',
          field: 'price_date',
          sorter: 'date',
          cellClick(e, cell) {
            const row = cell.getRow().getData();
            vm.popHistory(row);
            vm.showListingModal = true;
          },
        },
        sold_date: {
          title: vm.isMarketRental ? 'Leased Date' : 'Sold Date',
          field: 'sold_date',
          sorter: 'date',
        },
        field_mls_baths_value: {
          title: 'Baths',
          field: 'field_mls_baths_value',
          sorter: 'number',
          width: 70,
        },
      };

      return fields
        .filter(f => f.key !== undefined)
        .map(f => {
          const def = colMap[f.key];
          if (def) return def;
          // Generic column fallback
          return {
            title: f.label || f.key,
            field: f.key,
            sorter: 'alphanum',
            formatter(cell) {
              const val = cell.getValue();
              return (val === null || val === undefined || val === '') ? '-' : String(val);
            },
            cellClick(e, cell) {
              const row = cell.getRow().getData();
              vm.popHistory(row);
              vm.showListingModal = true;
            },
          };
        });
    },

    getNetPsf(mlsRow) {
      let res;
      var unit_size = this.isRealValue(mlsRow.field_mls_unit_size_value) ? mlsRow.field_mls_unit_size_value : 0
      res = unit_size ? Number(
        (mlsRow.field_mls_list_price_value - mlsRow.buyer_incentives) /
        unit_size
      ).toFixed(2) : mlsRow.psf;
      return res;
    },
    isRealValue(value) {
      return !["", "-", null, 0, "0"].includes(value);
    },
    // Match a selected filter value against a row value. Options that carry a
    // `values` list match any of them, so one button can cover a for-sale label
    // and its market rental equivalent (Presale → New Lease, Sold → Leased).
    filterOptionMatches(options, selected, value) {
      const opt = (options || []).find((o) => o.value === selected);
      return opt && opt.values
        ? opt.values.includes(value)
        : selected === value;
    },
    // Unit status tids that identify a market rental unit
    // (Leasing Soon, Now Leasing, Leased Out and their variants)
    isMarketRentalStatus(tid) {
      return ["2976", "11128", "21181", "11126", "21182"].includes(String(tid));
    },
    // Status tids whose $PSF / Net $PSF must render with 2 decimal places
    isTwoDecimalPsfStatus(tid) {
      return this.isMarketRentalStatus(tid);
    },
    // Whether a single unit row should show 2-decimal $PSF / Net $PSF. Rental
    // units carry a small monthly $PSF that reads better with two decimals, so
    // detect them by General Unit Type (`general_unit_type` → segmentKey) as
    // well as by the rental route and the rental status tids.
    rowTwoDecimalPsf(row) {
      return (
        this.isMarketRental ||
        this.segmentKey(row) === "rental" ||
        this.isTwoDecimalPsfStatus(row && row.field_utc_status_tid)
      );
    },
    // Whether a group of units should show 2-decimal $PSF / Net $PSF
    groupTwoDecimalPsf(units) {
      return (
        this.isMarketRental ||
        (units || []).some(
          (u) =>
            this.segmentKey(u) === "rental" ||
            this.isTwoDecimalPsfStatus(u.field_utc_status_tid)
        )
      );
    },
    async fetchData() {
      const vm = this;
      const counter = useRequestCounter();
      counter.incrementRequestCounter();
      this.isBusy = true;
      const updatedData = [];
      const BATCH_SIZE = 25;
      // The query is forwarded to the endpoint as-is. Taken off the path rather
      // than stripping "/unit-report" from it, because this report also runs as
      // a tab of /project-reports.
      const queryStart = this.$route.fullPath.indexOf("?");
      let croppedQuery =
        queryStart === -1 ? "" : this.$route.fullPath.slice(queryStart);

      function calculateNetPrice(data) {
        let res = Number(
          data.field_mls_list_price_value - data.buyer_incentives
        );
        return res > 0 ? res : "-";
      }

      function calculateSecondNetPrice(data) {
        let res = Number(data.price_second - data.buyer_incentives_second);
        return res > 0 ? res : "-";
      }
      try {
        let res = await axios.get(
          BACKEND_URL + "api/get_mls_list_utc" + croppedQuery
        );
        if (res && res.data != "" && res.data.length > 0) {
          let mappedData = res.data
            .filter((mlsRow) => {
              if (
                !["admin"].includes(this.current_user.role)
              ) {
                if (mlsRow.price_backup == null)
                  return false
                return (
                  !["", null].includes(mlsRow.field_mls_list_price_value) &&
                  ["verified", "semi-verified"].includes(
                    mlsRow.price_backup.toLowerCase()
                  )
                );
              }
              else
                return true;
            })
            .map((mlsRow) => {
              const netPrice = calculateNetPrice(mlsRow);
              const prevNetPrice = calculateSecondNetPrice(mlsRow);
              var latestIncentives = mlsRow.mls_history ? mlsRow.mls_history[mlsRow.mls_history.length - 1].buyer_incentives : ''
              return {
                ...mlsRow,
                customData: {
                  project: mlsRow.developer,
                  projectName: mlsRow.name,
                  href_link: this.utcLink(mlsRow),
                },
                buyer_incentives: latestIncentives,
                customAge: this.getAge(mlsRow),
                customLocationData: {
                  address: mlsRow.address,
                  municipality: mlsRow.city,
                  neighbour: mlsRow.neighbour,
                },
                custom_field_mls_beds_value: {
                  type:
                    mlsRow.field_mls_beds_value == "s"
                      ? "Studio"
                      : mlsRow.field_mls_beds_value == "jr"
                        ? "Jr 1" + "-bed"
                        : mlsRow.field_mls_beds_value + "-bed",
                  fp_specs:
                    mlsRow.fp_specs.toLocaleLowerCase() != "none"
                      ? mlsRow.fp_specs
                      : "",
                },
                custom_net_psf: ["", 0, null].includes(
                  mlsRow.field_mls_list_price_value
                )
                  ? "-"
                  : this.getNetPsf(mlsRow),
                psf: ["", 0, null].includes(mlsRow.field_mls_list_price_value)
                  ? "-"
                  : mlsRow.psf,
                custom_project_status: this.setProjectStatus(
                  mlsRow.field_utc_status_tid
                ),
                custom_price_change: this.getPriceChange(
                  prevNetPrice,
                  netPrice
                ),
                rowSelected: false,
                net_price: netPrice,
                status:
                  vm.isMarketRentalStatus(mlsRow.field_utc_status_tid) &&
                    mlsRow.status == "Sold"
                    ? "Leased"
                    : mlsRow.status,
                custom_net_sold_price: this.isRealValue(mlsRow.sold_price)
                  ? Number(mlsRow.sold_price - mlsRow.buyer_incentives)
                  : "-",
                custom_listing_type: this.setListingType(mlsRow),
                custom_net_sold_psf: this.isRealValue(mlsRow.sold_price)
                  ? Number(
                    (mlsRow.sold_price - mlsRow.buyer_incentives) /
                    mlsRow.field_mls_unit_size_value
                  )
                  : "-",
              };
            });

          this.reports = mappedData;
          this.loadFilters();
          var priceDates = this.reports
            .filter((data) => data.price_date != "-")
            .map((item) => new Date(item.price_date));

          var soldDates = this.reports
            .filter((data) => data.sold_date != "-")
            .map((item) => new Date(item.sold_date));

          let maxDays = this.reports
            .filter((data) => data.price_date != "-")
            .reduce((max, item) => {
              let diffDays = this.getDiffDays(item.price_date);
              return diffDays > max ? diffDays : max;
            }, 0);
          let maxSoldDays = this.reports
            .filter((data) => data.sold_date != "-")
            .reduce((max, item) => {
              let diffDays = this.getDiffDays(item.sold_date);
              return diffDays > max ? diffDays : max;
            }, 0);

          this.rangeValue.max = maxDays;
          this.rangeValue.startDate = new Date(Math.min(...priceDates));
          this.rangeValue.endDate = new Date(Math.max(...priceDates));

          this.rangeValueSoldDate.max = maxSoldDays;
          this.rangeValueSoldDate.startDate = new Date(Math.min(...soldDates));
          this.rangeValueSoldDate.endDate = new Date(Math.max(...soldDates));

          this.error = null;
          this.loading = false;
          this.isBusy = false;
        } else {
          this.error =
            "Fetching data might take a little bit longer. Please wait.";
        }
      } catch (error) {
        this.error =
          "Fetching data might take a little bit longer. Please wait.";
      } finally {
        this.isBusy = false;
        counter.incrementCompletedRequest();
      }
    },

    buildProjectRows(data) {
      const vm = this;
      const groups = Object.groupBy(data, ({ name }) => name);
      const result = [];
      for (const group in groups) {
        const unitsWithListPrice = groups[group].filter(
          item => item.field_mls_list_price_value && !isNaN(Number(item.field_mls_list_price_value))
        ).length;
        const rowData = Array.isArray(groups[group])
          ? groups[group].reduce((acc, curr, currentIndex) => {
            acc.units++;
            acc.size += this.haveValue(curr.field_mls_unit_size_value) ? Number(curr.field_mls_unit_size_value || 0) : 0;
            acc.filteredSize += this.haveValue(curr.field_mls_list_price_value) ? Number(curr.field_mls_unit_size_value || 0) : 0;
            acc.list_price += this.haveValue(curr.field_mls_list_price_value) ? Number(curr.field_mls_list_price_value || 0) : 0;
            acc.buyer_incentives += this.haveValue(curr.buyer_incentives) ? Number(curr.buyer_incentives || 0) : 0;
            acc.psf += this.haveValue(curr.psf) ? Number(curr.psf || 0) : 0;
            if (currentIndex === groups[group].length - 1) acc.link = vm.utcLink(curr);
            return acc;
          }, { units: 0, size: 0, list_price: 0, psf: 0, buyer_incentives: 0, filteredSize: 0, link: '' })
          : { units: 0, size: 0, list_price: 0, psf: 0, buyer_incentives: 0, filteredSize: 0, link: '' };
        const netPrice = Math.round((rowData.list_price - rowData.buyer_incentives) / unitsWithListPrice);
        const twoDp = vm.groupTwoDecimalPsf(groups[group]);
        const result_psf = twoDp
          ? `${Number(rowData.list_price / rowData.filteredSize).toFixed(2)}`
          : `${Math.round(rowData.list_price / rowData.filteredSize)}`;
        const result_netpsf = twoDp
          ? `${Number(netPrice / (rowData.filteredSize / unitsWithListPrice)).toFixed(2)}`
          : `${Math.round(netPrice / (rowData.filteredSize / unitsWithListPrice))}`;
        result.push({
          name: group,
          units: rowData.units,
          size: Math.round(rowData.size / rowData.units),
          list_price: rowData.list_price ? rowData.list_price / unitsWithListPrice : `-`,
          psf: rowData.list_price ? result_psf : "-",
          net_price: rowData.list_price ? netPrice : "-",
          net_psf: rowData.list_price ? result_netpsf : "-",
          link: rowData.link,
          _twoDp: twoDp,
        });
      }
      return result;
    },

    buildGroupedBedsRows(data) {
      const vm = this;
      const { isAdmin, isMarketRental } = this;
      const bedWeights = {
        "Studio": 1, "Jr 1-bed": 2, "1-bed": 3, "1-bed + Jr Bed": 4,
        "2-bed": 5, "2-bed + Jr Bed": 6, "3-bed": 7, "3-bed + Jr Bed": 8, "4-bed": 9,
      };
      const dataWithBedType = data.map(v => ({ ...v, bed_type: v.custom_field_mls_beds_value?.type || "N/A" }));
      const groupKeys = ["bed_type", "field_mls_baths_value"];
      if (isAdmin) groupKeys.push("parking");
      const grouped = dataWithBedType.reduce((acc, item) => {
        const key = groupKeys.map(k => item[k] || "N/A").join('|');
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {});
      const calcAvg = (arr) => {
        const f = arr.filter(v => ![null, undefined, "", 0, "-"].includes(v));
        if (!f.length) return 0;
        const avg = f.reduce((a, b) => a + b, 0) / f.length;
        return isMarketRental ? Number(avg.toFixed(2)) : Math.round(avg);
      };
      const fmtCurrency = (val) => (val > 0 ? `$${Math.round(val).toLocaleString()}` : "N/A");
      const fmtVal = (val, suffix = "") => (vm.isRealValue(val) ? `${val}${suffix}` : "-");
      const resultData = Object.entries(grouped).reduce((acc, [key, units]) => {
        const [bedType, bathCount, parkingType] = key.split('|');
        const uwlp = units.filter(u => !["", null, 0].includes(u.field_mls_list_price_value));
        if (!uwlp.length && !isAdmin) return acc;
        const twoDp = vm.groupTwoDecimalPsf(units);
        const avgSize = calcAvg(uwlp.map(u => Number(u.field_mls_unit_size_value)));
        const soldPrice = calcAvg(uwlp.map(u => Number(u.sold_price)));
        const incentives = calcAvg(uwlp.map(u => Number(u.buyer_incentives)));
        const avgPrice = calcAvg(uwlp.map(u => Number(u.field_mls_list_price_value)));
        const psfVals = uwlp.map(u => Number(u.psf)).filter(v => !isNaN(v) && v > 0);
        const avgPsfRaw = psfVals.length ? psfVals.reduce((a, b) => a + b, 0) / psfVals.length : 0;
        const avgNetPrice = calcAvg(uwlp.map(u => u.net_price));
        const soldPsf = avgSize > 0 ? soldPrice / avgSize : 0;
        const fmtSoldPsf = twoDp ? soldPsf.toFixed(2) : Math.round(soldPsf);
        const confSold = uwlp.filter(u => ["Sold", "Leased"].includes(u.status)).length;
        const netSoldPrice = Math.round(soldPrice - incentives);
        const avgNetPSF = avgSize > 0 ? avgNetPrice / avgSize : 0;
        const fmtNetPSF = twoDp ? avgNetPSF.toFixed(2) : Math.round(avgNetPSF);
        const unitsPercentage = Math.round((units.length / dataWithBedType.length) * 100);
        const unitsCount = isAdmin ? units.length : uwlp.length;
        acc.push({
          Type: bedType, Baths: bathCount, Pkg: parkingType || "-",
          "# Units": unitsCount,
          "Units %": fmtVal(unitsPercentage, "%"),
          "Avg Size": uwlp.length > 0 ? Math.round(avgSize).toLocaleString() : "N/A",
          "List Price": fmtCurrency(avgPrice),
          "$PSF": uwlp.length > 0 ? `$${twoDp ? avgPsfRaw.toFixed(2) : Math.round(avgPsfRaw)}` : "N/A",
          "Net Price": fmtCurrency(avgNetPrice),
          "Net $PSF": uwlp.length > 0 && !isNaN(avgNetPSF) ? `$${fmtNetPSF}` : "N/A",
          "Conf Sold": fmtVal(confSold),
          "Sold %": fmtVal(Math.round((confSold / unitsCount) * 100), "%"),
          "Sold Price": soldPrice > 0 ? `$${Math.round(soldPrice).toLocaleString()}` : "-",
          "Sold $PSF": soldPsf > 0 ? `$${fmtSoldPsf}` : "-",
          "Net Sold Price": netSoldPrice > 0 ? `$${netSoldPrice.toLocaleString()}` : "-",
          "Net Sold $PSF": (netSoldPrice / avgSize) > 0 ? `$${Math.round(netSoldPrice / avgSize)}` : "-",
          Units: [...uwlp],
        });
        return acc;
      }, []);
      return resultData.sort((a, b) => {
        const wA = bedWeights[a.Type] ?? 99, wB = bedWeights[b.Type] ?? 99;
        if (wA !== wB) return wA - wB;
        return (parseFloat(a.Baths) || 0) - (parseFloat(b.Baths) || 0);
      });
    },

    toggleSummaryCityFilter(val) {
      const idx = this.summaryCityFilter.indexOf(val);
      if (idx === -1) this.summaryCityFilter.push(val);
      else this.summaryCityFilter.splice(idx, 1);
    },
  },
  watch: {
    priceDateDays(val) {
      this.enablePricedate = val > 0;
      this.custom_filter.priceDate.priceDate_selected = val > 0 ? String(val) : '';
    },
    soldDateDays(val) {
      this.enableSoldDate = val > 0;
      this.custom_filter.soldDate.soldDate_selected = val > 0 ? String(val) : '';
    },
    selectedQuarter: function (newVal) {
      if (newVal) {
        this.generateQuarterlyReport();
        this.generateQuarterlyReportCsv();
      }
    },
    reports: function (newVal) {
      this.totalRows = newVal.length;
    },
    filteredMlsData: {
      handler(newVal) {
        this.totalRows = newVal.length;
        this.filtered_data = newVal;
        if (this.tabulator) {
          if (this._selectionFilter) {
            this.tabulator.removeFilter(this._selectionFilter);
            this._selectionFilter = null;
          }
          this.selectionFilterRefs = null;
          this.filterToSelected = false;
          this.selectedCount = 0;
          this.tabulator.replaceData(newVal);
        }
      },
      immediate: false,
    },
    fields: {
      handler(newVal) {
        if (!newVal) return;
        if (this.tabulator) {
          this.tabulator.setColumns(this.buildColumnDefs(newVal));
        }
        if (this.projectDetailTab) {
          this.projectDetailTab.setColumns(this.projectDetailColumnDefs);
        }
      },
      deep: true,
    },
    perPage(val) {
      if (this.tabulator) this.tabulator.setPageSize(val);
      if (this.projectDetailTab) this.projectDetailTab.setPageSize(val);
    },
    // Building a Tabulator against a hidden container gives it no width, so
    // each view builds (or remeasures) its tables as it comes on screen.
    activeView() {
      this.redrawActiveView();
    },
    summaryProjects(val) {
      if (this.summaryProjectsTab) this.summaryProjectsTab.replaceData(val);
    },
    summaryGroupedBeds(val) {
      if (this.summaryBedsTab) this.summaryBedsTab.replaceData(val);
    },
    projectDetailRows(val) {
      if (this.projectDetailTab) this.projectDetailTab.replaceData(val);
    },
  },
  computed: {
    // Embedded, the tab bar on /project-reports decides; standalone the
    // component decides for itself.
    activeView() {
      return (this.embedded ? this.view : this.internalView) || "unit";
    },
    isUnitView() {
      return this.activeView === "unit";
    },
    isSummaryView() {
      return this.activeView === "summary";
    },
    isProjectView() {
      return this.activeView.indexOf("project:") === 0;
    },
    projectDetailName() {
      return this.isProjectView ? this.activeView.slice("project:".length) : null;
    },
    // The rows behind one line of the summary's Projects table. Read off the
    // summary's own base data so the city/neighbourhood filter carries through.
    projectDetailRows() {
      const name = this.projectDetailName;
      if (!name) return [];
      return (this.summaryBaseData || []).filter((r) => r.name === name);
    },
    // The main report's columns minus the selection checkbox.
    projectDetailColumnDefs() {
      return this.buildColumnDefs(
        (this.fields || []).filter((f) => f.key !== "checkbox")
      );
    },
    viewTitle() {
      if (this.isSummaryView) return "Unit Report Summary";
      if (this.isProjectView) return this.projectDetailName;
      return "Unit Report";
    },
    viewCrumb() {
      if (this.isSummaryView) return "Summary";
      if (this.isProjectView) return this.projectDetailName;
      return null;
    },
    summaryAvailability() {
      const data = this.summaryBaseData || [];
      const twoDp = this.groupTwoDecimalPsf(data);
      const unitsWithListPrice = data.filter(
        (item) =>
          item.field_mls_list_price_value &&
          !isNaN(Number(item.field_mls_list_price_value))
      ).length;

      if (unitsWithListPrice === 0) {
        return {
          psf: twoDp ? '0.00' : '0',
          net_psf: twoDp ? '0.00' : 0,
        };
      }

      const sums = data.reduce(
        (acc, curr) => {
          if (
            curr.field_mls_list_price_value &&
            !isNaN(Number(curr.field_mls_list_price_value))
          ) {
            acc.size += Math.round(curr.field_mls_unit_size_value);
            acc.list_price += Math.round(
              Number(curr.field_mls_list_price_value)
            );
          }
          if (curr.buyer_incentives && !isNaN(Number(curr.buyer_incentives))) {
            acc.buyer_incentives += Number(curr.buyer_incentives);
          }
          return acc;
        },
        { size: 0, list_price: 0, buyer_incentives: 0 }
      );

      const avgSize = sums.size / unitsWithListPrice;
      const avgListPrice = sums.list_price / unitsWithListPrice;
      const avgNetPrice = Math.round(
        avgListPrice - sums.buyer_incentives / unitsWithListPrice
      );

      return {
        psf: !twoDp
          ? Math.round(avgListPrice / avgSize).toLocaleString()
          : Number(avgListPrice / avgSize).toFixed(2),
        net_psf: !twoDp
          ? Math.round(avgNetPrice / avgSize)
          : Number(avgNetPrice / avgSize).toFixed(2),
      };
    },
    getUnitReportSummaryAverage() {
      const units = this.summaryProjects;
      const totalUnits = units.reduce((s, u) => s + u.units, 0) || this.totalRows;
      function calculateWeightedAverage(total, unitCount) {
        return total === 0 ? 0 : Number((unitCount / totalUnits) * total);
      }
      var result = units.map(unit => {
        return {
          units: unit.units,
          size: this.isRealValue(unit.size) ? calculateWeightedAverage(unit.size, unit.units) : 0,
          list_price: this.isRealValue(unit.list_price) ? calculateWeightedAverage(unit.list_price, unit.units) : 0,
          net_price: this.isRealValue(unit.net_price) ? calculateWeightedAverage(unit.net_price, unit.units) : 0,
          psf: this.isRealValue(unit.psf) ? calculateWeightedAverage(unit.psf, unit.units) : 0,
          net_psf: this.isRealValue(unit.net_psf) ? calculateWeightedAverage(unit.net_psf, unit.units) : 0,
        }
      }).reduce((acc, curr) => {
        acc.units += curr.units;
        acc.size += Math.round(curr.size);
        acc.list_price += Math.round(curr.list_price);
        acc.net_price += Math.round(curr.net_price);
        acc.psf += curr.psf;
        acc.net_psf += curr.net_psf;
        return acc;
      }, {
        units: 0,
        size: 0,
        list_price: 0,
        net_price: 0,
        psf: 0,
        net_psf: 0,
      })

      return result
    },
    getGroupedBedsAverage() {
      const vm = this;
      const units = this.summaryGroupedBeds;
      const totalUnits = units.reduce((sum, unit) => sum + unit['# Units'], 0);
      function calculateWeightedAverage(total, unitCount) {
        let mappedData = vm.dataMapper(total);
        return mappedData === 0 ? 0 : Number((unitCount / totalUnits) * mappedData);
      }
      var result = units.map(unit => {
        function formatPercentage(value) {
          if (!vm.isRealValue(value)) return 0;
          return Number(String(value).replace('%', ''));
        }
        const formatUnitPercentage = this.isRealValue(unit['Units %']) ? formatPercentage(unit['Units %']) : 0;
        const formatSoldPercentage = this.isRealValue(unit['Sold %']) ? formatPercentage(unit['Sold %']) : 0;
        return {
          units: unit['# Units'],
          size: this.isRealValue(unit['Avg Size']) && unit['Avg Size'] != 'N/A' ? calculateWeightedAverage(unit['Avg Size'], unit['# Units']) : 0,
          conf_sold: this.isRealValue(unit['Conf Sold']) ? Number(unit['Conf Sold']) : 0,
          list_price: this.isRealValue(unit['List Price']) && unit['List Price'] != 'N/A' ? calculateWeightedAverage(unit['List Price'], unit['# Units']) : 0,
          units_percentage: Number(formatUnitPercentage) || 0,
          sold_percentage: Number(formatSoldPercentage) || 0,
          net_price: this.isRealValue(unit['Net Price']) && unit['Net Price'] != 'N/A' ? calculateWeightedAverage(unit['Net Price'], unit['# Units']) : 0,
          psf: this.isRealValue(unit['$PSF']) && unit['$PSF'] != 'N/A' ? calculateWeightedAverage(unit['$PSF'], unit['# Units']) : 0,
          net_psf: this.isRealValue(unit['Net $PSF']) && unit['Net $PSF'] != 'N/A' ? calculateWeightedAverage(unit['Net $PSF'], unit['# Units']) : 0,
        }
      }).reduce((acc, curr) => {
        acc.units += curr.units;
        acc.size += Math.round(curr.size);
        acc.list_price += Math.round(curr.list_price);
        acc.net_price += Math.round(curr.net_price);
        acc.psf += curr.psf;
        acc.net_psf += curr.net_psf;
        acc.conf_sold += curr.conf_sold;
        acc.units_percentage += curr.units_percentage;
        acc.sold_percentage += curr.sold_percentage;
        return acc;
      }, {
        units: 0,
        size: 0,
        list_price: 0,
        net_price: 0,
        psf: 0,
        net_psf: 0,
        conf_sold: 0,
        units_percentage: 0,
        sold_percentage: 0
      })

      return result
    },
    getViewCategory() {
      switch (this.isMarketRental) {
        case true:
          return 30;
        case false:
          return 31;
      }
    },
    showFlag() {
      return [
        "darcysrai@gmail.com",
        "mark.wang+urban@devradius.com",
        "erloucabilan94@gmail.com",
        "Erl@devbc.com",
        "darcy@gmail.com",
        "markdeveloperbc@test.com",
      ].includes(this.current_user.mail);
    },
    ...mapState(useUserStore, ["current_user"]),
    quarterToDates() {
      const quarter = this.selectedQuarter;
      if (!quarter) return null;

      const [q, year] = quarter.split("-");
      const yearNum = parseInt(year);
      let startDate, endDate;

      switch (q) {
        case "Q1":
          startDate = new Date(yearNum, 0, 1); // January 1st
          endDate = new Date(yearNum, 2, 31, 23, 59, 59); // March 31st
          break;
        case "Q2":
          startDate = new Date(yearNum, 3, 1); // April 1st
          endDate = new Date(yearNum, 5, 30, 23, 59, 59); // June 30th
          break;
        case "Q3":
          startDate = new Date(yearNum, 6, 1); // July 1st
          endDate = new Date(yearNum, 8, 30, 23, 59, 59); // September 30th
          break;
        case "Q4":
          startDate = new Date(yearNum, 9, 1); // October 1st
          endDate = new Date(yearNum, 11, 31, 23, 59, 59); // December 31st
          break;
        default:
          return null;
      }
      return { startDate, endDate };
    },
    categorizedProjects() {
      const vm = this;
      const cityGroups = [];

      function groupByName(projects) {
        return Object.groupBy(projects, ({ name }) => name);
      }

      var groups = this.groupByCity(this.filtered_data);
      if (groups) {
        for (let group in groups) {
          let isTempProject = groupByName(groups[group]);
          for (const name in isTempProject) {
            let projectResults = {
              newProjects: [],
              soldOutProjects: [],
              constructionCompletionProject: [],
              activelySellingProjects: [],
              tempoProjects: [],
            };
            isTempProject[name].forEach((name) => {
              const salesStartDate = name.sales_start_date;
              const soldOutDate = name.sold_out_date;
              const quarterSalesStartDate = this.getQuarterDate(salesStartDate);

              if (salesStartDate && soldOutDate == "") {
                if (quarterSalesStartDate == vm.selectedQuarter)
                  projectResults.newProjects.push(name);
                else if (quarterSalesStartDate > vm.selectedQuarter)
                  projectResults.activelySellingProjects.push(name);
              } else {
                if (quarterSalesStartDate == vm.selectedQuarter)
                  projectResults.newProjects.push(name);
                if (
                  this.quarterDifference(
                    vm.selectedQuarter,
                    quarterSalesStartDate
                  ) <= 4
                )
                  projectResults.activelySellingProjects.push(name);
                else projectResults.soldOutProjects.push(name);
              }
            });

            cityGroups.push({
              city: group,
              project: {
                projectName: name,
                projects: projectResults,
              },
            });
          }
        }
      }
      return Object.groupBy(cityGroups, ({ city }) => city);
    },
    projectSummaries() {
      const quarterDates = this.quarterToDates;
      if (!quarterDates) {
        return {
          newProjects: [],
          soldOutProjects: [],
          constructionCompletionProject: [],
        };
      }

      const newProjects = [];
      const soldOutProjects = [];
      const constructionCompletionProject = [];

      // Helper function to parse 'DD-Mon-YY' format
      const parseDDMonYY = (dateString) => {
        if (!dateString) return null;
        const parts = dateString.split("-");
        if (parts.length !== 3) return null;

        const day = parseInt(parts[0]);
        // Date.parse can handle 'Jan', 'Feb', etc., but needs a full year for consistency
        // We'll create a dummy date string with a full year for parsing the month correctly
        const monthIndex = new Date(
          Date.parse(parts[1] + " 1, 2000")
        ).getMonth();
        const year = 2000 + parseInt(parts[2]); // Assuming '22' means 2022

        return new Date(year, monthIndex, day);
      };

      this.filtered_data.forEach((project) => {
        // Check for New Projects
        if (project.sales_start_date) {
          const salesStartDate = new Date(project.sales_start_date * 1000); // Unix timestamp to milliseconds
          if (
            salesStartDate >= quarterDates.startDate &&
            salesStartDate <= quarterDates.endDate
          ) {
            newProjects.push(project);
          }
        }

        // Check for Sold Out Projects
        const soldOutDate = parseDDMonYY(project.sold_date);
        if (
          soldOutDate &&
          soldOutDate >= quarterDates.startDate &&
          soldOutDate <= quarterDates.endDate
        ) {
          soldOutProjects.push(project);
        }

        // Check for Construction Completions (using occupancy_date as a proxy)
        const completionDate = parseDDMonYY(project.occupancy_date);
        if (
          completionDate &&
          completionDate >= quarterDates.startDate &&
          completionDate <= quarterDates.endDate
        ) {
          constructionCompletionProject.push(project);
        }
      });

      return {
        newProjects,
        soldOutProjects,
        constructionCompletionProject,
      };
    },
    isAdmin() {
      return this.current_user.role == "admin";
    },
    isStatusAll() {
      return !this.custom_filter.status.status_selected.length;
    },
    isListingAll() {
      return !this.custom_filter.listingType.listingType_selected.length;
    },
    selectedTypeLabel() {
      const sel = this.custom_filter.beds.beds_selected;
      if (!sel.length) return 'All Types';
      if (sel.length === 1) {
        const opt = this.custom_filter.beds.beds_options.find(o => o.id === sel[0]);
        return opt ? opt.label : 'All Types';
      }
      return sel.length + ' types';
    },
    selectedBathsLabel() {
      const sel = this.custom_filter.baths.baths_selected;
      if (!sel.length) return 'Any baths';
      if (sel.length === 1) {
        const opt = this.custom_filter.baths.baths_options.find(o => o.id === sel[0]);
        return opt ? opt.label : 'Any baths';
      }
      return sel.length + ' baths';
    },
    activeCount() {
      if (!this.filteredMlsData) return 0;
      return this.filteredMlsData.filter(r => r.status === 'Active').length;
    },
    soldCount() {
      if (!this.filteredMlsData) return 0;
      return this.filteredMlsData.filter(r => ['Sold', 'Leased'].includes(r.status)).length;
    },
    // Per-segment metrics powering the KPI cards. Each unit row is bucketed by
    // its General Unit Type (`general_unit_type`) into For Sale (Market Strata
    // + Fee-Simple) or Rental (see segmentKey), matching the Market Overview
    // split — refer to MarketOverview.vue kpiCards.
    kpiSegments() {
      const rows = this.filteredMlsData || [];
      const isNum = (v) =>
        v !== null && v !== undefined && v !== "" && v !== "-" && !isNaN(Number(v));
      const avg = (arr, fn) => {
        const vals = arr.map(fn).filter(isNum).map(Number);
        if (!vals.length) return null;
        return vals.reduce((s, v) => s + v, 0) / vals.length;
      };
      const projectCount = (arr) =>
        new Set(arr.map((r) => r.name).filter(Boolean)).size;

      const buildSeg = (arr) => {
        const sold = arr.filter((r) => ["Sold", "Leased"].includes(r.status));
        return {
          projects: projectCount(arr),
          units: arr.length,
          soldUnits: sold.length,
          soldPct: arr.length ? Math.round((sold.length / arr.length) * 100) : 0,
          size: avg(arr, (r) => r.field_mls_unit_size_value),
          grossPsf: avg(arr, (r) => r.psf),
          netPsf: avg(arr, (r) => r.custom_net_psf),
        };
      };

      return {
        forsale: buildSeg(rows.filter((r) => this.segmentKey(r) !== "rental")),
        rental: buildSeg(rows.filter((r) => this.segmentKey(r) === "rental")),
      };
    },
    kpiCards() {
      const g = this.kpiSegments;
      const SEGS = [
        { key: "forsale", label: "For Sale" },
        { key: "rental", label: "Rental" },
      ];
      const int = (n) => (n == null ? "—" : Math.round(n).toLocaleString());
      // Rental $PSF is a small monthly figure, so keep two decimals; For Sale
      // sale $PSF reads better rounded (matches MarketOverview).
      const psf = (n, key) => {
        if (n == null) return "—";
        return key === "rental"
          ? `$${Number(n).toFixed(2)}`
          : `$${Math.round(n).toLocaleString()}`;
      };
      const soldWord = (key) => (key === "rental" ? "leased" : "sold");
      const card = (key, label, accent, valueFor, subFor, subAccent) => ({
        key,
        label,
        accent,
        segments: SEGS.map(({ key: sk, label: sl }) => ({
          key: sk,
          label: sl,
          value: valueFor(g[sk], sk),
          sub: subFor(g[sk], sk),
          subAccent: !!subAccent,
        })),
      });
      return [
        card("projects", "Total Projects", "#12283F",
          (d) => int(d.projects), () => "Distinct projects"),
        card("units", "Total Units", "#1F9D55",
          (d) => int(d.units),
          (d, k) => `${int(d.soldUnits)} ${soldWord(k)} · ${d.soldPct}%`, true),
        card("size", "Avg Size", "#FC5868",
          (d) => (d.size == null ? "—" : int(d.size)), () => "sqft · per unit"),
        card("gross", "Avg Gross $PSF", "#343376",
          (d, k) => psf(d.grossPsf, k), () => "List price"),
        card("net", "Avg Net $PSF", "#E8833A",
          (d, k) => psf(d.netPsf, k), () => "After incentives"),
      ];
    },
    formatUpdated() {
      const d = new Date();
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    userInitials() {
      const u = this.current_user;
      if (!u || !u.mail) return 'U';
      const name = u.name || u.mail;
      const parts = name.split(/[\s@.]+/).filter(Boolean);
      return parts.slice(0, 2).map(p => p[0].toUpperCase()).join('');
    },
    maxDateValue() {

      let maxDays = this.filteredMlsData
        .filter((data) => data.price_date != "-")
        .reduce((max, item) => {
          let diffDays = this.getDiffDays(item.price_date);
          return diffDays > max ? diffDays : max;
        }, 0);
      var priceDates = this.filteredMlsData
        .filter((data) => data.mls_price_date != "-")
        .map((item) => new Date(item.mls_price_date));
      this.rangeValue.startDate = new Date(Math.min(...priceDates));
      this.rangeValue.endDate = new Date(Math.max(...priceDates));
      return maxDays;
    },
    maxSoldDateValue() {
      let maxDays = this.filteredMlsData
        .filter((data) => data.sold_date != "-")
        .reduce((max, item) => {
          let diffDays = this.getDiffDays(item.sold_date);
          return diffDays > max ? diffDays : max;
        }, 0);
      var soldDates = this.filteredMlsData
        .filter((data) => data.sold_date != "-")
        .map((item) => new Date(item.sold_date));
      this.rangeValueSoldDate.startDate = new Date(Math.min(...soldDates));
      this.rangeValueSoldDate.endDate = new Date(Math.max(...soldDates));
      return maxDays;
    },
    projects() {
      return this.buildProjectRows(this.filteredMlsData);
    },
    isMarketRental() {
      return this.$route.query.marketStatus === "rental";
    },
    filteredMlsData() {
      const vm = this;
      let res = vm.reports;

      if (
        vm.filter_rows.filterArray.length > 0 ||
        [
          vm.custom_filter.baths.baths_selected,
          vm.custom_filter.beds.beds_selected,
          vm.custom_filter.fpSpecs.fpSpecs_selected,
          vm.custom_filter.listingType.listingType_selected,
          vm.custom_filter.status.status_selected,
          vm.custom_filter.priceDate.priceDate_selected,
        ].some((x) => x.length > 0) ||
        vm.priceDateStart ||
        vm.enablePricedate ||
        vm.enableSoldDate
      ) {
        let bath = new Array();
        let type = new Array();
        let fp = new Array();
        let status = new Array();
        let priceDate = new Array();
        let confDate = new Array();
        let listingType = new Array();
        let projectStatus = new Array();

        vm.filter_rows.filterArray.forEach((val) => {
          if (val.startsWith("conf_")) confDate.push(val.match(/(\d+)/)[0]);
          if (val.startsWith("ps_")) projectStatus.push(val.replace("ps_", ""));
        });
        if (confDate.length > 0) {
          res = res.filter((item) => {
            let diffConfDays = this.getDiffDays(new Date(item.sold_date));

            return confDate.some(
              (date) => diffConfDays <= Number(date) && diffConfDays != null
            );
          });
        }
        if (projectStatus.length > 0)
          res = res.filter((item) =>
            projectStatus.includes(item.custom_project_status)
          );

        if (
          vm.priceDateStart != null &&
          vm.priceDateStart.every((x) => x != null)
        ) {
          res = res.filter((row) => {
            return (
              new Date(row.price_date) >= new Date(vm.priceDateStart[0]) &&
              new Date(row.price_date) <= new Date(vm.priceDateStart[1])
            );
          });
        }

        for (var item in vm.custom_filter) {
          if (
            vm.custom_filter[item]["beds_selected"] &&
            vm.custom_filter[item]["beds_selected"].length > 0
          ) {
            vm.custom_filter[item]["beds_selected"].forEach((val) => {
              if (val.startsWith("bed_")) type.push(val.replace("bed_", ""));
            });
            res = res.filter((item) =>
              type.some((t) => {
                if (
                  t === item.custom_field_mls_beds_value.type &&
                  item.custom_field_mls_beds_value.fp_specs != "Jr Bed"
                )
                  return true;

                if (item.custom_field_mls_beds_value.fp_specs == "Jr Bed") {
                  return (
                    t.search(item.custom_field_mls_beds_value.type) > -1 &&
                    t.search(item.custom_field_mls_beds_value.fp_specs) > -1
                  );
                }
              })
            );
          }

          if (
            vm.custom_filter[item]["baths_selected"] &&
            vm.custom_filter[item]["baths_selected"].length > 0
          ) {
            vm.custom_filter[item]["baths_selected"].forEach((val) => {
              if (val.startsWith("bath_")) bath.push(val.replace("bath_", ""));
            });
            res = res.filter((item) =>
              bath.includes(item.field_mls_baths_value)
            );
          }

          if (
            vm.custom_filter[item]["fpSpecs_selected"] &&
            vm.custom_filter[item]["fpSpecs_selected"].length > 0
          ) {
            vm.custom_filter[item]["fpSpecs_selected"].forEach((val) => {
              fp.push(val);
            });
            res = res.filter((item) =>
              fp.some((spec) => {
                let split = item.fp_specs.split(",").map((s) => s.trim());
                return split.includes(spec);
              })
            );
          }

          if (
            vm.custom_filter[item]["listingType_selected"] &&
            vm.custom_filter[item]["listingType_selected"].length > 0
          ) {
            res = res.filter((item) =>
              vm.custom_filter["listingType"]["listingType_selected"].some(
                (val) =>
                  vm.filterOptionMatches(
                    vm.custom_filter.listingType.listingType_options,
                    val,
                    item.custom_listing_type
                  )
              )
            );
          }

          if (
            vm.custom_filter[item]["status_selected"] &&
            vm.custom_filter[item]["status_selected"].length > 0
          ) {
            res = res.filter((item) =>
              vm.custom_filter["status"]["status_selected"].some((val) =>
                vm.filterOptionMatches(
                  vm.custom_filter.status.status_options,
                  val,
                  item.status
                )
              )
            );
          }
          // DateSlider drives a single day-count for all users (admin included):
          // keep units whose price/sold date is within the selected number of days.
          if (vm.enablePricedate) {
            if (
              vm.custom_filter[item]["priceDate_selected"] &&
              vm.custom_filter[item]["priceDate_selected"].length > 0
            ) {
              res = res.filter(
                (item) =>
                  this.getDiffDays(item.price_date) <=
                  Number(vm.custom_filter.priceDate["priceDate_selected"])
              );
            }
          }
          if (vm.enableSoldDate) {
            if (
              vm.custom_filter[item]["soldDate_selected"] &&
              vm.custom_filter[item]["soldDate_selected"].length > 0
            ) {
              res = res.filter(
                (item) =>
                  this.getDiffDays(item.sold_date) <=
                  Number(vm.custom_filter.soldDate["soldDate_selected"])
              );
            }
          }
        }
      }

      // Segment dropdown (All / For Sale / Rental) — bucket each unit by its
      // General Unit Type via segmentKey(), mirroring MarketOverview's segment
      // filter. Applies regardless of the other filters above.
      if (vm.segFilter && vm.segFilter !== "All") {
        const target = vm.segFilter === "Rental" ? "rental" : "forsale";
        res = res.filter((row) => vm.segmentKey(row) === target);
      }

      for (const filterKey in vm.rangeInputFilters) {
        const filter = vm.rangeInputFilters[filterKey];
        const minValue = filter.value.min;
        const maxValue = filter.value.max;
        const hasMin = typeof minValue === "number" && !isNaN(minValue);
        const hasMax = typeof maxValue === "number" && !isNaN(maxValue);

        if (hasMin || hasMax) {
          res = res.filter((item) => {
            const itemValue = item[filterKey];
            if (typeof Math.round(itemValue) !== "number" || isNaN(itemValue)) {
              return false;
            }

            let passMin = true;
            let passMax = true;

            if (hasMin) {
              passMin = Math.round(itemValue) >= minValue;
            }
            if (hasMax) {
              passMax = Math.round(itemValue) <= maxValue;
            }
            return passMin && passMax;
          });
        }
      }

      if (vm.filter_rows.filterText) {
        res = res.filter(row => vm.containsString(row, vm.filter_rows.filterText));
      }

      return res;
    },
    groupedListingType() {
      const vm = this;
      const data = this.filtered_data.map((value) => {
        return {
          ...value,
          custom_field_mls_beds_value:
            value.custom_field_mls_beds_value.type +
            " " +
            value.custom_field_mls_beds_value.fp_specs,
        };
      });

      const groups = [
        "custom_field_mls_beds_value",
        "field_mls_baths_value",
        "parking",
      ];

      const groupByListingType = Object.groupBy(
        data,
        ({ custom_listing_type }) => custom_listing_type
      );
      let res = Object.entries(groupByListingType).map(([key, value]) => {
        return {
          listingType: key,
          units: mappedGrouped(value),
          value: Object.entries(Object.groupBy(value, ({ status }) => status))
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([key, value]) => {
              return {
                status: key,
                units: mappedGrouped(value),
              };
            }),
        };
      });

      function calculateAverage(arr) {
        return vm.isMarketRental
          ? Number(arr.reduce((sum, value) => sum + value, 0) / arr.length)
            .toFixed(2)
            .toLocaleString()
          : Math.round(arr.reduce((sum, value) => sum + value, 0) / arr.length);
      }
      function mappedGrouped(value) {
        const grouped = {};
        value.forEach((item) => {
          groups
            .reduce((acc, group, index) => {
              const key = item[group];

              acc[key] = acc[key] || (index + 1 === groups.length ? [] : {});

              return acc[key];
            }, grouped)
            .push(item);
        });

        const mappedData = Object.keys(grouped).map((bedType) => {
          // Collect both 'None' and 'U/G' parking types for this bed type
          const units = Object.values(grouped[bedType]).flatMap((floor) => {
            return [
              "None",
              "U/G",
              "Pad",
              "Double",
              "Single",
              "Tandem",
              "Triple",
            ].flatMap((parking) => {
              return floor[parking] || []; // Collect units from 'None' and 'U/G'
            });
          });

          // If there are no units after collecting, just return an empty row.
          if (units.length === 0) {
            return {
              Type: `${bedType}`,
              Baths: "N/A",
              Pkg: "N/A",
              "# Units": 0,
              "Avg Size": "N/A",
              "Gross Price": "N/A",
              "Gross $PSF": "N/A",
              "Net Price": "N/A",
              "Net $PSF": "N/A",
            };
          }

          // Calculate averages for the collected units
          const avgSize = calculateAverage(
            units.map((unit) => Number(unit.field_mls_unit_size_value) || 0)
          );
          const soldPrice = calculateAverage(
            units.map((unit) => Number(unit.sold_price) || 0)
          );
          const buyer_incentives = calculateAverage(
            units.map((unit) => Number(unit.buyer_incentives) || 0)
          );
          const soldPsf = vm.isMarketRental
            ? Number(soldPrice / avgSize).toFixed(2)
            : Math.round(soldPrice / avgSize);
          const avgPrice = calculateAverage(
            units.map((unit) => unit.net_price || 0)
          );
          const avgPSF = calculateAverage(units.map((unit) => unit.psf || 0));
          const avgNetPrice = calculateAverage(
            units.map((unit) => unit.net_price || 0)
          );
          const avgNetPSF = calculateAverage(
            units.map((unit) => unit.psf || 0)
          );
          const confSold = units.reduce((acc, unit) => {
            return unit.status == "Sold" ? (acc += 1) : acc;
          }, 0);
          // Use the first unit's details for Baths and Parking
          const firstUnit = units[0] || {};
          const baths = firstUnit.field_mls_baths_value || "N/A"; // Default to 'N/A' if not available
          const parking = firstUnit.parking || "N/A"; // Default to 'N/A' if not available
          const net_sold_price = Math.round(soldPrice - buyer_incentives);
          return {
            Type: `${bedType}`,
            Baths: baths,
            Pkg: parking,
            "# Units": units.length,
            "Avg Size":
              units.length > 0 ? Math.round(avgSize).toLocaleString() : "N/A",
            "Gross Price":
              units.length > 0
                ? `$${Math.round(avgPrice).toLocaleString()}`
                : "N/A",
            "Gross $PSF": units.length > 0 ? `$${avgPSF}` : "N/A",
            "Net Price":
              units.length > 0
                ? `$${Math.round(avgNetPrice).toLocaleString()}`
                : "N/A",
            "Net $PSF": units.length > 0 ? `$${avgNetPSF}` : "N/A",
            Units: [...units],
            "Conf Sold": confSold,
            "Sold Price":
              soldPrice > 0
                ? `$${Math.round(soldPrice).toLocaleString()}`
                : "-",
            "Sold $PSF": soldPsf > 0 ? `$${soldPsf}` : "-",
            "Net Sold Price":
              net_sold_price > 0 ? `$${net_sold_price.toLocaleString()}` : "-",
            "Net Sold $PSF":
              Math.round(net_sold_price / avgSize) > 0
                ? `$${Math.round(net_sold_price / avgSize)}`
                : "-",
          };
        });
        return mappedData;
      }
      const sortOrder = ["Presale", "Assignment", "Resale"];

      return res.sort((a, b) => {
        return (
          sortOrder.indexOf(a.listingType) - sortOrder.indexOf(b.listingType)
        );
      });
    },
    groupedBeds() {
      return this.buildGroupedBedsRows(this.filtered_data);
    },
    summaryUniqueCities() {
      return [...new Set(this.filteredMlsData.map(u => u.city).filter(Boolean))].sort();
    },
    summaryUniqueNeighbourhoods() {
      return [...new Set(this.filteredMlsData.map(u => u.neighbour).filter(Boolean))].sort();
    },
    summaryBaseData() {
      if (!this.summaryCityFilter.length) return this.filteredMlsData;
      return this.filteredMlsData.filter(u =>
        this.summaryCityFilter.includes(u.city) || this.summaryCityFilter.includes(u.neighbour)
      );
    },
    summaryProjects() {
      return this.buildProjectRows(this.summaryBaseData);
    },
    summaryGroupedBeds() {
      return this.buildGroupedBedsRows(this.summaryBaseData);
    },
  },
};
</script>

<style lang="scss" scoped>
/* ── Page Shell ─────────────────────────────────────────── */
.ur-page {
  background: #F4F5F7;
  min-height: 100vh;
  font-family: 'Helvetica Light', Helvetica, sans-serif;
}

/* ── Header ──────────────────────────────────────────────── */
.ur-header {
  background: #fff;
  border-bottom: 1px solid #E9E8E9;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  position: sticky;
  top: 0;
  z-index: 100;
}

.ur-breadcrumb {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #8A8A8A;
  margin-bottom: 4px;
}

.ur-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ur-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 18px;
  color: #12283F;
  margin: 0;
  line-height: 1.2;
}

.ur-badge {
  background: #EDF2FE;
  color: #343376;
  border: none;
  border-radius: 4px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 11px;
  padding: 3px 10px;
  cursor: pointer;
}

.ur-badge--ghost {
  background: transparent;
  border: 1px solid #C9CCD2;
  color: #12283F;
}

.ur-last-updated {
  font-size: 11px;
  color: #8A8A8A;
}

.ur-header-divider {
  width: 1px;
  height: 20px;
  background: #E9E8E9;
  margin: 0 4px;
}

.ur-icon-btn {
  width: 30px;
  height: 30px;
  border: 1px solid #E9E8E9;
  border-radius: 6px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #12283F;

  &--sm {
    width: 26px;
    height: 26px;
  }
}

.ur-btn-ghost {
  background: #fff;
  border: 1px solid #C9CCD2;
  border-radius: 6px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 11px;
  color: #12283F;
  padding: 6px 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;

  &--sm {
    font-size: 11px;
    padding: 5px 10px;
  }
}

.ur-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #FC5868;
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Content area ────────────────────────────────────────── */
.ur-content {
  padding: 20px 24px;
}

/* ── KPI Grid (metric cards, Active vs Sold/Leased segments) ── */
.ur-kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.ur-kpi2-card {
  position: relative;
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 12px;
  padding: 16px 14px 13px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(18, 40, 63, 0.06);
  transition: box-shadow 160ms cubic-bezier(0.2, 0, 0, 1), border-color 160ms;

  &:hover {
    border-color: #D6D9E0;
    box-shadow: 0 6px 18px -8px rgba(18, 40, 63, 0.20);
  }
}

.ur-kpi2-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
}

.ur-kpi2-label {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.13em;
  color: #545454;
}

.ur-kpi2-segs {
  display: flex;
  margin-top: 12px;
}

.ur-kpi2-seg {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.ur-kpi2-vdiv {
  width: 1px;
  align-self: stretch;
  flex-shrink: 0;
  background: #EEF0F4;
  margin: 2px 7px;
}

.ur-kpi2-seg__lbl {
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 8px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 3px;

  &--forsale {
    color: #12283F;

    .ur-kpi2-dot {
      background: #12283F;
    }
  }

  &--rental {
    color: #B23A48;

    .ur-kpi2-dot {
      background: #FC5868;
    }
  }
}

/* Segment dropdown dots (toolbar filter) */
.ur-seg-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 2px;

  &--forsale {
    background: #12283F;
  }

  &--rental {
    background: #FC5868;
  }
}

.ur-kpi2-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
}

.ur-kpi2-seg__val {
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 15px;
  color: #12283F;
  letter-spacing: -0.02em;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.ur-kpi2-seg__sub {
  font-size: 9px;
  color: #8A8A8A;
  margin-top: 3px;
  line-height: 1.25;
  font-family: 'Helvetica Light', Helvetica, sans-serif;

  &--pos {
    color: #1F9D55;
  }
}

@media (max-width: 1200px) {
  .ur-kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

/* ── Toolbar ─────────────────────────────────────────────── */
.ur-toolbar {
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.ur-search {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #E9E8E9;
  border-radius: 6px;
  padding: 5px 8px;
  background: #F7F8FB;
  min-width: 200px;
}

.ur-search__input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 12px;
  color: #12283F;
  font-family: 'Helvetica Light', Helvetica, sans-serif;
  width: 100%;

  &::placeholder {
    color: #B6BAC2;
  }
}

.ur-search__clear {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #8A8A8A;
  padding: 0;
  display: flex;
}

.ur-segmented {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ur-seg-label {
  font-size: 11px;
  color: #8A8A8A;
  font-family: 'Helvetica Light', Helvetica, sans-serif;
  white-space: nowrap;
}

.ur-seg-group {
  display: flex;
  border: 1px solid #E9E8E9;
  border-radius: 6px;
  overflow: hidden;
}

.ur-seg-btn {
  background: #fff;
  border: none;
  border-right: 1px solid #E9E8E9;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 11px;
  color: #545454;
  padding: 5px 10px;
  cursor: pointer;
  white-space: nowrap;

  &:last-child {
    border-right: none;
  }

  &--on {
    background: #12283F;
    color: #fff;
  }
}

.ur-dropdown {
  position: relative;
}

.ur-dropdown__btn {
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 6px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 11px;
  color: #12283F;
  padding: 5px 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.ur-dropdown__overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
}

.ur-dropdown__menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(18, 40, 63, 0.10);
  z-index: 201;
  min-width: 140px;
  padding: 4px;
}

.ur-dropdown__item {
  display: block;
  width: 100%;
  background: transparent;
  border: none;
  font-family: 'Helvetica Light', Helvetica, sans-serif;
  font-size: 12px;
  color: #12283F;
  padding: 7px 10px;
  text-align: left;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: #F4F5F7;
  }

  &--on {
    background: #EDF2FE;
    color: #343376;
    font-weight: 600;
  }

  &--check {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.ur-dropdown__menu--multi {
  max-height: 260px;
  overflow-y: auto;
  min-width: 160px;
}

.ur-dropdown__check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border: 1px solid #C8CCD3;
  border-radius: 3px;
  background: #fff;
  color: #343376;
  flex-shrink: 0;

  .ur-dropdown__item--on & {
    border-color: #343376;
    background: #EDF2FE;
  }
}

.ur-date-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 6px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 11px;
  color: #545454;
  padding: 5px 10px;
  cursor: pointer;
  white-space: nowrap;

  &--on {
    border-color: #12283F;
    color: #12283F;
  }
}

.ur-toggle {
  display: inline-flex;
  align-items: center;
  width: 26px;
  height: 14px;
  border-radius: 99px;
  background: #C9CCD2;
  padding: 2px;
  transition: background 120ms;

  &--on {
    background: #12283F;
  }
}

.ur-toggle__knob {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #fff;
  transition: transform 120ms;

  .ur-toggle--on & {
    transform: translateX(12px);
  }
}

.ur-fields-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 6px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 11px;
  color: #12283F;
  padding: 5px 10px;
  cursor: pointer;

  &--on {
    border-color: #12283F;
    background: #EDF2FE;
  }
}

.ur-filter-selected-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #d97706;
  border-radius: 6px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 11px;
  color: #d97706;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #fef3c7;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &--active {
    background: #d97706;
    color: #ffffff;
    border-color: #d97706;
  }

  &--active:hover:not(:disabled) {
    background: #b45309;
    border-color: #b45309;
  }
}

.ur-ghost-link {
  background: transparent;
  border: none;
  font-family: 'Helvetica Light', Helvetica, sans-serif;
  font-size: 11px;
  color: #343376;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
}

/* ── Fields Panel ────────────────────────────────────────── */
.ur-fields-panel {
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 8px;
  padding: 14px 16px 16px;
  margin-bottom: 12px;
}

.ur-fp-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.ur-fp-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 13px;
  color: #12283F;
}

.ur-fp-hint {
  font-size: 11px;
  color: #8A8A8A;
}

.ur-fp-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.ur-field-list {
  background: #FAFBFD;
  border: 1px dashed #E2E4EA;
  border-radius: 8px;
  padding: 12px;
}

.ur-fl-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.ur-fl-accent {
  display: inline-block;
  width: 3px;
  height: 12px;
  border-radius: 1px;

  &--navy {
    background: #12283F;
  }

  &--grey {
    background: #8A8A8A;
  }
}

.ur-fl-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #12283F;
}

.ur-fl-count {
  font-size: 10px;
  color: #8A8A8A;
}

.ur-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 32px;
}

.ur-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 5px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 11px;
  cursor: grab;
  user-select: none;

  &--filled {
    background: #12283F;
    color: #fff;
    padding: 5px 8px;
  }

  &--outline {
    background: #fff;
    color: #12283F;
    border: 1px solid #E2E4EA;
    padding: 5px 10px;
  }
}

.ur-chip__num {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  font-variant-numeric: tabular-nums;
}

/* ── Table ───────────────────────────────────────────────── */
.ur-table-wrap {
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 12px;
  /* Bounded height so Tabulator scrolls its own body instead of the page —
     this is what keeps the header row (and the frozen columns) in view. */
  height: calc(100vh - 200px);
  min-height: 420px;
}

::v-deep .ur-table-wrap .tabulator {
  border: none !important;
  background: #fff !important;
  font-family: 'Helvetica Light', Helvetica, sans-serif !important;
  font-size: 12px !important;
}

::v-deep .ur-table-wrap .tabulator-header {
  background: #F7F8FB !important;
  border-bottom: 1px solid #E9E8E9 !important;
  border-top: none !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 30 !important;
}

/* Frozen columns (selection + Project & Developer) need an opaque background
   so the horizontally scrolled columns pass behind them, not through them. */
::v-deep .ur-table-wrap .tabulator-col.tabulator-frozen {
  background: #F7F8FB !important;
}

::v-deep .ur-table-wrap .tabulator-cell.tabulator-frozen {
  background: #fff !important;
}

::v-deep .ur-table-wrap .tabulator-row:hover .tabulator-cell.tabulator-frozen {
  background: #FAFBFD !important;
}

::v-deep .ur-table-wrap .tabulator-row.tabulator-selected .tabulator-cell.tabulator-frozen {
  background: #eff6ff !important;
}

::v-deep .ur-table-wrap .tabulator-row.tabulator-selected:hover .tabulator-cell.tabulator-frozen {
  background: #dbeafe !important;
}

/* Edge shadow on the last frozen column marks where the scroll area starts */
::v-deep .ur-table-wrap .tabulator-col.ur-col-project,
::v-deep .ur-table-wrap .tabulator-cell.ur-col-project {
  box-shadow: 4px 0 6px -4px rgba(18, 40, 63, 0.14) !important;
}

::v-deep .ur-table-wrap .tabulator-col {
  background: transparent !important;
  border-right: none !important;
  padding: 0 !important;
}

::v-deep .ur-table-wrap .tabulator-col-content {
  padding: 10px 14px !important;
}

::v-deep .ur-table-wrap .tabulator-col-title {
  font-size: 10px !important;
  font-weight: 600 !important;
  color: #8A8A8A !important;
  text-transform: uppercase !important;
  letter-spacing: 0.08em !important;
  white-space: nowrap !important;
  font-family: 'Poppins', sans-serif !important;
}

::v-deep .ur-table-wrap .tabulator-col.tabulator-sortable:hover .tabulator-col-title {
  color: #12283F !important;
}

::v-deep .ur-table-wrap .tabulator-col.tabulator-col-sorter-element:hover {
  background: #f0f1f3 !important;
}

::v-deep .ur-table-wrap .tabulator-col .tabulator-col-sorter {
  color: #d1d5db !important;
}

::v-deep .ur-table-wrap .tabulator-col.tabulator-col-sorter-element .tabulator-col-sorter {
  color: #12283F !important;
}

::v-deep .ur-table-wrap .tabulator-tableHolder {
  background: #fff !important;
}

::v-deep .ur-table-wrap .tabulator-row {
  background: #fff !important;
  border-bottom: 1px solid #f3f4f6 !important;
  transition: background 0.1s !important;
}

::v-deep .ur-table-wrap .tabulator-row:hover {
  background: #FAFBFD !important;
}

::v-deep .ur-table-wrap .tabulator-row.tabulator-row-even {
  background: #fff !important;
}

::v-deep .ur-table-wrap .tabulator-row.tabulator-row-even:hover {
  background: #FAFBFD !important;
}

::v-deep .ur-table-wrap .tabulator-cell {
  border-right: none !important;
  padding: 0 14px !important;
  color: #374151 !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
}

::v-deep .ur-table-wrap .tabulator-row.tabulator-selected {
  background: #eff6ff !important;
}

::v-deep .ur-table-wrap .tabulator-row.tabulator-selected:hover {
  background: #dbeafe !important;
}

::v-deep .ur-table-wrap .tabulator-tableHolder::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::v-deep .ur-table-wrap .tabulator-tableHolder::-webkit-scrollbar-track {
  background: transparent;
}

::v-deep .ur-table-wrap .tabulator-tableHolder::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 3px;
}

::v-deep .ur-table-wrap .tabulator-paginator {
  background: #fff !important;
  border-top: 1px solid #F2F3F5 !important;
  padding: 8px 14px !important;
  font-size: 11px !important;
  color: #8A8A8A !important;
}

::v-deep .ur-table-wrap .tabulator-page {
  border: 1px solid #E9E8E9 !important;
  border-radius: 6px !important;
  font-family: 'Poppins', sans-serif !important;
  font-size: 11px !important;
  color: #12283F !important;
  padding: 4px 10px !important;
  margin: 0 2px !important;
  background: #fff !important;
}

::v-deep .ur-table-wrap .tabulator-page.active {
  background: #12283F !important;
  border-color: #12283F !important;
  color: #fff !important;
}

::v-deep .ur-table-wrap .tabulator-page:disabled {
  color: #C9CCD2 !important;
}

::v-deep .ur-table-wrap .tabulator-page-size {
  border: 1px solid #E9E8E9 !important;
  border-radius: 6px !important;
  font-size: 11px !important;
  color: #12283F !important;
  padding: 4px 8px !important;
  background: #fff !important;
}

.ur-placeholder {
  padding: 48px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
}


.ur-cell-loc {
  cursor: pointer;
  font-size: 12px !important;

  &:hover {
    color: #343376;
  }
}

.ur-cell-loc__city {
  font-size: 12px;
  color: #8A8A8A;
}

.ur-unit-link {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 12px;
}

.ur-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 99px;
  font-size: 11px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  padding: 3px 8px;

  &--active {
    background: rgba(31, 157, 85, 0.10);
    color: #1F9D55;

    .ur-status-pill__dot {
      background: #1F9D55;
    }
  }

  &--sold {
    background: rgba(252, 88, 104, 0.10);
    color: #D43847;

    .ur-status-pill__dot {
      background: #D43847;
    }
  }
}

.ur-status-pill__dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #8A8A8A;
  flex-shrink: 0;
}

/* Listing Type pill: see assets/styles/listing-type-pill.css (global, shared
   with the other tables that render the column). */

.ur-change-pill {
  display: inline-block;
  border-radius: 4px;
  font-size: 10px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  padding: 2px 6px;

  &--up {
    background: rgba(31, 157, 85, 0.10);
    color: #1F9D55;
  }

  &--dn {
    background: rgba(252, 88, 104, 0.10);
    color: #D43847;
  }
}

/* ── Pagination ──────────────────────────────────────────── */
.ur-pagination-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #F2F3F5;
  background: #fff;
}

.ur-pagination-info {
  font-size: 11px;
  color: #8A8A8A;
  white-space: nowrap;
}

::v-deep .ur-pagination {
  .page-item .page-link {
    border: 1px solid #E9E8E9;
    border-radius: 6px !important;
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #12283F;
    padding: 4px 10px;
    margin: 0 2px;
  }

  .page-item.active .page-link {
    background: #12283F;
    border-color: #12283F;
    color: #fff;
  }

  .page-item.disabled .page-link {
    color: #C9CCD2;
  }
}

.ur-perpage-select {
  border: 1px solid #E9E8E9;
  border-radius: 6px;
  font-size: 11px;
  color: #12283F;
  padding: 4px 8px;
  background: #fff;
  cursor: pointer;
  height: auto;
  margin-left: auto;
}

/* The summary and the per-project drill-down are views of this report, not
   overlays on it, so they sit in the same padded content column as the table
   and inherit .ur-content's gutter rather than adding one of their own. */
.ur-summary-view,
.ur-project-view {
  padding-bottom: 8px;
}

/* The KPI strip takes the room the toolbar takes on the unit view, so the
   drill-down's table is shortened by the same amount and the page still fits
   one screen. */
.ur-project-view .ur-table-wrap {
  height: calc(100vh - 380px);
  min-height: 360px;
}

.ur-summary-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

/* Each summary table sits in the same card the main report's table does. */
.ur-summary-card {
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
}

.ur-summary-table {
  width: 100%;
}

/* ── Tabulator reset — mirrors the Market Overview table (.ms-table-wrap), so
      the two tables read as one component: tinted header strip, uppercase
      micro-caps column titles, hairline row rules and a 14px cell gutter. ── */
::v-deep .ur-summary-table .tabulator {
  border: none !important;
  background: #ffffff !important;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", sans-serif !important;
  font-size: 11px !important;
  white-space: nowrap;
  width: 100% !important;
}

::v-deep .ur-summary-table .tabulator-tableHolder,
::v-deep .ur-summary-table .tabulator-table {
  width: 100% !important;
  background: #ffffff !important;
}

::v-deep .ur-summary-table .tabulator-header {
  background: #f8f9fa !important;
  border-bottom: 1px solid #e8eaed !important;
  border-top: none !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 3 !important;
}

::v-deep .ur-summary-table .tabulator-col {
  background: transparent !important;
  border-right: none !important;
  padding: 0 !important;
}

::v-deep .ur-summary-table .tabulator-col-content {
  padding: 10px 14px !important;
}

::v-deep .ur-summary-table .tabulator-col-title {
  font-size: 10px !important;
  font-weight: 600 !important;
  color: #6b7280 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.06em !important;
  white-space: nowrap !important;
  overflow: visible !important;
  text-overflow: clip !important;
}

::v-deep .ur-summary-table .tabulator-col.tabulator-sortable:hover .tabulator-col-title {
  color: #0e1b2c !important;
}

::v-deep .ur-summary-table .tabulator-col.tabulator-col-sorter-element:hover {
  background: #f0f1f3 !important;
}

::v-deep .ur-summary-table .tabulator-col .tabulator-col-sorter {
  color: #d1d5db !important;
}

::v-deep .ur-summary-table .tabulator-col.tabulator-col-sorter-element .tabulator-col-sorter {
  color: #0e1b2c !important;
}

/* The cells below are laid out with flex, so the row has to BE a flex row —
   without this every cell drops onto its own line. .tabulator-calcs carries
   .tabulator-row too, so the Total / Average line is covered here as well. */
::v-deep .ur-summary-table .tabulator-row {
  background: #ffffff !important;
  border-bottom: 1px solid #f3f4f6 !important;
  transition: background 0.1s !important;
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: stretch !important;
  overflow: visible !important;
}

::v-deep .ur-summary-table .tabulator-row.tabulator-row-even {
  background: #ffffff !important;
}

::v-deep .ur-summary-table .tabulator-row:hover,
::v-deep .ur-summary-table .tabulator-row.tabulator-row-even:hover {
  background: #f9fafb !important;
}

::v-deep .ur-summary-table .tabulator-row:hover .tabulator-cell {
  background-color: transparent !important;
}

::v-deep .ur-summary-table .tabulator-cell {
  display: flex !important;
  align-items: center !important;
  flex-shrink: 0 !important;
  border-right: none !important;
  padding: 0 14px !important;
  color: #374151 !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
}

::v-deep .ur-summary-table .tabulator-calcs-bottom .tabulator-cell {
  background: #f8f9fa !important;
  font-weight: 600 !important;
  color: #0e1b2c !important;
  border-top: 1px solid #e8eaed !important;
}

::v-deep .ur-summary-table .tabulator-tableHolder::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::v-deep .ur-summary-table .tabulator-tableHolder::-webkit-scrollbar-track {
  background: transparent;
}

::v-deep .ur-summary-table .tabulator-tableHolder::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 3px;
}

/* ───────────────────────────────────────────────────────────── */
/*  Responsive — tablet and mobile                                */
/* ───────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .ur-header {
    flex-wrap: wrap;
    padding: 12px 16px;
    gap: 8px;
  }

  .ur-content {
    padding: 14px 16px;
  }

  .ur-header-divider {
    display: none;
  }
}

@media (max-width: 768px) {
  .ur-header {
    padding: 10px 12px;
    gap: 6px;
    position: relative;
  }

  .ur-title-row {
    flex-wrap: wrap;
    gap: 6px;
  }

  .ur-title {
    font-size: 16px;
  }

  .ur-last-updated {
    flex-basis: 100%;
    margin-top: 2px;
  }

  .ur-btn-ghost {
    padding: 5px 9px;
    font-size: 10.5px;

    & svg {
      width: 11px;
      height: 11px;
    }
  }

  .ur-avatar {
    width: 26px;
    height: 26px;
    font-size: 10px;
  }

  .ur-content {
    padding: 10px 10px 20px;
  }

  /* KPI grid — 2-column layout on tablet */
  .ur-kpi-grid {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 10px;
  }

  .ur-kpi2-card {
    padding: 12px 12px 11px;
  }

  .ur-kpi2-seg__val {
    font-size: 16px;
  }

  .ur-kpi2-vdiv {
    margin: 2px 8px;
  }

  /* Toolbar — tighten gaps and let everything wrap */
  .ur-toolbar {
    padding: 10px 12px;
    gap: 8px;
  }

  .ur-search {
    width: 100%;
    min-width: 0;
  }

  .ur-segmented {
    flex-wrap: wrap;
  }

  .ur-seg-group {
    flex-wrap: wrap;
  }

  /* Table — let it scroll horizontally */
  .ur-table-wrap {
    border-radius: 8px;
    height: calc(100vh - 150px);
    min-height: 320px;
  }

  /* Fields panel */
  .ur-fields-panel {
    padding: 12px;
  }

  .ur-fp-header {
    flex-wrap: wrap;
    gap: 6px;
  }

  .ur-summary-bar {
    flex-wrap: wrap;
  }
}

@media (max-width: 480px) {
  .ur-header {
    padding: 8px 10px;
  }

  .ur-title {
    font-size: 15px;
  }

  .ur-badge {
    font-size: 10px;
    padding: 2px 8px;
  }

  .ur-btn-ghost {
    padding: 5px 7px;
    font-size: 10px;
  }

  .ur-kpi-grid {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .ur-kpi2-card {
    padding: 11px 12px 10px;
  }

  .ur-kpi2-seg__val {
    font-size: 17px;
  }

  .ur-kpi2-label,
  .ur-kpi2-seg__sub {
    font-size: 9.5px;
  }
}
</style>

<style>
/* ── Project & Developer cell (Tabulator-rendered HTML; must be unscoped) ── */
.ur-table-wrap .tabulator-cell.ur-col-project {
  align-items: flex-start !important;
  padding-top: 7px !important;
  padding-bottom: 7px !important;
  font-size: 12px !important;
}

.ur-pcell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ur-pname {
  font-size: 12px;
  font-weight: 500;
  color: #111827;
  line-height: 1.3;
}

.ur-plink {
  color: #0e1b2c;
  text-decoration: none;
}

.ur-plink:hover {
  text-decoration: underline;
  color: #1d4ed8;
}

.ur-pdev {
  font-size: 10px;
  color: #9ca3af;
  line-height: 1.2;
}

/* Row action inside a table cell. Quiet by default so a column of them does not
   out-shout the figures they sit beside; it fills in on hover. */
.ur-cell-btn {
  border: 1px solid #C9CCD2;
  border-radius: 6px;
  background: #fff;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 10.5px;
  color: #12283F;
  padding: 4px 10px;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  transition: background 120ms cubic-bezier(0.2, 0, 0, 1),
    border-color 120ms cubic-bezier(0.2, 0, 0, 1),
    color 120ms cubic-bezier(0.2, 0, 0, 1);
}

.ur-cell-btn:hover {
  background: #12283F;
  border-color: #12283F;
  color: #fff;
}

html.dark .ur-pname {
  color: #d6e4f0 !important;
}

html.dark .ur-plink {
  color: #93c5fd !important;
}

html.dark .ur-pdev {
  color: rgba(255, 255, 255, 0.28) !important;
}

/* ── Dark mode ── */
html.dark .ur-page {
  background: #0b1625 !important;
}

html.dark .ur-header {
  background: #0e1b2c !important;
  border-bottom-color: rgba(255, 255, 255, 0.06) !important;
}

html.dark .ur-breadcrumb {
  color: rgba(255, 255, 255, 0.35) !important;
}

html.dark .ur-title {
  color: #d6e4f0 !important;
}

html.dark .ur-badge {
  background: rgba(96, 165, 250, 0.12) !important;
  color: #93c5fd !important;
}

html.dark .ur-last-updated {
  color: rgba(255, 255, 255, 0.4) !important;
}

html.dark .ur-header-divider {
  background: rgba(255, 255, 255, 0.08) !important;
}

html.dark .ur-icon-btn {
  background: #0e1b2c !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  color: rgba(255, 255, 255, 0.7) !important;
}

html.dark .ur-icon-btn:hover {
  background: #16263d !important;
}

html.dark .ur-btn-ghost {
  background: #0e1b2c !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
  color: rgba(255, 255, 255, 0.7) !important;
}

html.dark .ur-btn-ghost:hover {
  background: #16263d !important;
  border-color: rgba(255, 255, 255, 0.25) !important;
}

/* ── KPI cards ── */
html.dark .ur-kpi2-card {
  background: #0e1b2c !important;
  border-color: rgba(255, 255, 255, 0.07) !important;
  box-shadow: none !important;
}

html.dark .ur-kpi2-card:hover {
  border-color: rgba(255, 255, 255, 0.18) !important;
  box-shadow: 0 6px 18px -8px rgba(0, 0, 0, 0.5) !important;
}

html.dark .ur-kpi2-label,
html.dark .ur-kpi2-seg__sub {
  color: rgba(255, 255, 255, 0.4) !important;
}

html.dark .ur-kpi2-seg__sub--pos {
  color: #4ecf87 !important;
}

html.dark .ur-kpi2-seg__val {
  color: #d6e4f0 !important;
}

html.dark .ur-kpi2-vdiv {
  background: rgba(255, 255, 255, 0.07) !important;
}

/* Segment tag colours legible on the dark KPI cards */
html.dark .ur-kpi2-seg__lbl--forsale {
  color: #9bd3ff !important;
}

html.dark .ur-kpi2-seg__lbl--forsale .ur-kpi2-dot {
  background: #9bd3ff !important;
}

html.dark .ur-kpi2-seg__lbl--rental {
  color: #ff9aa4 !important;
}

html.dark .ur-kpi2-seg__lbl--rental .ur-kpi2-dot {
  background: #ff9aa4 !important;
}

html.dark .ur-seg-dot--forsale {
  background: #9bd3ff !important;
}

html.dark .ur-seg-dot--rental {
  background: #ff9aa4 !important;
}

/* ── Toolbar ── */
html.dark .ur-toolbar {
  background: #0e1b2c !important;
  border-color: rgba(255, 255, 255, 0.07) !important;
}

html.dark .ur-search {
  background: #152030 !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

html.dark .ur-search__input {
  color: #d6e4f0 !important;
}

html.dark .ur-search__input::placeholder {
  color: rgba(255, 255, 255, 0.3) !important;
}

html.dark .ur-search__clear {
  color: rgba(255, 255, 255, 0.4) !important;
}

html.dark .ur-seg-label {
  color: rgba(255, 255, 255, 0.4) !important;
}

html.dark .ur-seg-group {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

html.dark .ur-seg-btn {
  background: #0e1b2c !important;
  border-right-color: rgba(255, 255, 255, 0.07) !important;
  color: rgba(255, 255, 255, 0.55) !important;
}

html.dark .ur-seg-btn--on {
  background: #1e3a5f !important;
  color: #d6e4f0 !important;
}

html.dark .ur-dropdown__btn,
html.dark .ur-date-toggle,
html.dark .ur-fields-btn {
  background: #0e1b2c !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  color: rgba(255, 255, 255, 0.7) !important;
}

html.dark .ur-date-toggle--on {
  border-color: #4db7fe !important;
  color: #4db7fe !important;
}

html.dark .ur-fields-btn--on {
  border-color: #4db7fe !important;
  background: rgba(77, 183, 254, 0.1) !important;
  color: #4db7fe !important;
}

html.dark .ur-filter-selected-btn {
  color: #fbbf24 !important;
  border-color: #d97706 !important;
}

html.dark .ur-filter-selected-btn:hover:not(:disabled) {
  background: rgba(217, 119, 6, 0.12) !important;
}

html.dark .ur-filter-selected-btn--active {
  background: #d97706 !important;
  color: #ffffff !important;
  border-color: #d97706 !important;
}

html.dark .ur-dropdown__menu {
  background: #0e1b2c !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4) !important;
}

html.dark .ur-dropdown__item {
  color: rgba(255, 255, 255, 0.7) !important;
}

html.dark .ur-dropdown__item:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

html.dark .ur-dropdown__item--on {
  background: rgba(77, 183, 254, 0.12) !important;
  color: #93c5fd !important;
}

html.dark .ur-dropdown__check {
  background: rgba(255, 255, 255, 0.04) !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
  color: #93c5fd !important;
}

html.dark .ur-dropdown__item--on .ur-dropdown__check {
  background: rgba(77, 183, 254, 0.18) !important;
  border-color: #4db7fe !important;
}

html.dark .ur-toggle {
  background: rgba(255, 255, 255, 0.15) !important;
}

html.dark .ur-toggle--on {
  background: #4db7fe !important;
}

html.dark .ur-ghost-link {
  color: #a78bfa !important;
}

/* ── Fields Panel ── */
html.dark .ur-fields-panel {
  background: #0e1b2c !important;
  border-color: rgba(255, 255, 255, 0.07) !important;
}

html.dark .ur-fp-title {
  color: #d6e4f0 !important;
}

html.dark .ur-fp-hint,
html.dark .ur-fl-count {
  color: rgba(255, 255, 255, 0.4) !important;
}

html.dark .ur-field-list {
  background: #152030 !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

html.dark .ur-fl-title {
  color: #d6e4f0 !important;
}

html.dark .ur-chip--filled {
  background: #1e3a5f !important;
  color: #d6e4f0 !important;
}

html.dark .ur-chip--outline {
  background: #152030 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: rgba(255, 255, 255, 0.7) !important;
}

/* ── Table ── */
html.dark .ur-table-wrap {
  background: #0e1b2c !important;
  border-color: rgba(255, 255, 255, 0.07) !important;
}

html.dark .ur-table-wrap .tabulator,
html.dark .ur-table-wrap .tabulator-tableHolder {
  background: #0b1625 !important;
}

html.dark .ur-table-wrap .tabulator-header {
  background: #0e1b2c !important;
  border-bottom-color: rgba(255, 255, 255, 0.07) !important;
}

html.dark .ur-table-wrap .tabulator-col.tabulator-frozen {
  background: #0e1b2c !important;
}

html.dark .ur-table-wrap .tabulator-cell.tabulator-frozen {
  background: #0b1625 !important;
}

html.dark .ur-table-wrap .tabulator-row:hover .tabulator-cell.tabulator-frozen {
  background: #16263d !important;
}

html.dark .ur-table-wrap .tabulator-row.tabulator-selected .tabulator-cell.tabulator-frozen,
html.dark .ur-table-wrap .tabulator-row.tabulator-selected:hover .tabulator-cell.tabulator-frozen {
  background: #1e3a5f !important;
}

html.dark .ur-table-wrap .tabulator-col.ur-col-project,
html.dark .ur-table-wrap .tabulator-cell.ur-col-project {
  box-shadow: 4px 0 6px -4px rgba(0, 0, 0, 0.55) !important;
}

html.dark .ur-table-wrap .tabulator-col-title {
  color: rgba(255, 255, 255, 0.4) !important;
}

html.dark .ur-table-wrap .tabulator-col.tabulator-sortable:hover .tabulator-col-title {
  color: #d6e4f0 !important;
}

html.dark .ur-table-wrap .tabulator-col.tabulator-col-sorter-element:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

html.dark .ur-table-wrap .tabulator-col .tabulator-col-sorter {
  color: rgba(255, 255, 255, 0.2) !important;
}

html.dark .ur-table-wrap .tabulator-col.tabulator-col-sorter-element .tabulator-col-sorter {
  color: #d6e4f0 !important;
}

html.dark .ur-table-wrap .tabulator-row {
  background: #0b1625 !important;
  border-bottom-color: rgba(255, 255, 255, 0.04) !important;
}

html.dark .ur-table-wrap .tabulator-row.tabulator-row-even {
  background: #0b1625 !important;
}

html.dark .ur-table-wrap .tabulator-row:hover,
html.dark .ur-table-wrap .tabulator-row.tabulator-row-even:hover {
  background: #16263d !important;
}

html.dark .ur-table-wrap .tabulator-row:hover .tabulator-cell {
  background-color: transparent !important;
}

html.dark .ur-table-wrap .tabulator-row.tabulator-selected,
html.dark .ur-table-wrap .tabulator-row.tabulator-selected:hover {
  background: #1e3a5f !important;
}

html.dark .ur-table-wrap .tabulator-row.tabulator-selected:hover .tabulator-cell {
  background-color: transparent !important;
}

html.dark .ur-table-wrap .tabulator-cell {
  color: rgba(255, 255, 255, 0.7) !important;
}

html.dark .ur-table-wrap .tabulator-tableHolder::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1) !important;
}

html.dark .ur-table-wrap .tabulator-paginator {
  background: #0e1b2c !important;
  border-top-color: rgba(255, 255, 255, 0.07) !important;
  color: rgba(255, 255, 255, 0.5) !important;
}

html.dark .ur-table-wrap .tabulator-page {
  background: #0e1b2c !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  color: rgba(255, 255, 255, 0.7) !important;
}

html.dark .ur-table-wrap .tabulator-page.active {
  background: #1e3a5f !important;
  border-color: #1e3a5f !important;
  color: #d6e4f0 !important;
}

html.dark .ur-table-wrap .tabulator-page:disabled {
  color: rgba(255, 255, 255, 0.25) !important;
}

html.dark .ur-table-wrap .tabulator-page-size {
  background: #0e1b2c !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  color: rgba(255, 255, 255, 0.7) !important;
}

/* ── Cell content ── */
html.dark .ur-placeholder {
  color: rgba(255, 255, 255, 0.3) !important;
}

html.dark .ur-cell-loc {
  color: rgba(255, 255, 255, 0.7) !important;
}

html.dark .ur-cell-loc:hover {
  color: #93c5fd !important;
}

html.dark .ur-cell-loc__city {
  color: rgba(255, 255, 255, 0.35) !important;
}

html.dark .ur-unit-link {
  color: #93c5fd !important;
}

/* ── Pagination bar ── */
html.dark .ur-pagination-bar {
  background: #0e1b2c !important;
  border-top-color: rgba(255, 255, 255, 0.07) !important;
}

html.dark .ur-pagination-info {
  color: rgba(255, 255, 255, 0.4) !important;
}

html.dark .ur-pagination .page-item .page-link {
  background: #0e1b2c !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  color: rgba(255, 255, 255, 0.7) !important;
}

html.dark .ur-pagination .page-item.active .page-link {
  background: #1e3a5f !important;
  border-color: #1e3a5f !important;
  color: #d6e4f0 !important;
}

html.dark .ur-pagination .page-item.disabled .page-link {
  color: rgba(255, 255, 255, 0.25) !important;
}

html.dark .ur-perpage-select {
  background: #0e1b2c !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  color: rgba(255, 255, 255, 0.7) !important;
}

/* ── Summary & drill-down sub-tables ── */
html.dark .ur-badge--ghost {
  border-color: rgba(255, 255, 255, 0.14) !important;
  color: #d6e4f0 !important;
}

html.dark .ur-summary-card {
  background: #0e1b2c !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

html.dark .ur-summary-table .tabulator,
html.dark .ur-summary-table .tabulator-tableHolder,
html.dark .ur-summary-table .tabulator-table {
  background: #0b1625 !important;
}

html.dark .ur-summary-table .tabulator-header {
  background: #0e1b2c !important;
  border-bottom-color: rgba(255, 255, 255, 0.07) !important;
}

html.dark .ur-cell-btn {
  background: #15273d !important;
  border-color: rgba(255, 255, 255, 0.14) !important;
  color: #d6e4f0 !important;
}

html.dark .ur-cell-btn:hover {
  background: #1e3a5f !important;
  border-color: #1e3a5f !important;
  color: #fff !important;
}

/* ── Tabulator responsive (unscoped so it reaches dynamic DOM) ── */
@media (max-width: 768px) {
  .ur-table-wrap .tabulator-col-content {
    padding: 8px 10px !important;
  }

  .ur-table-wrap .tabulator-cell {
    padding: 0 10px !important;
    font-size: 11.5px !important;
  }

  .ur-table-wrap .tabulator-tableHolder {
    -webkit-overflow-scrolling: touch;
  }
}

html.dark .ur-summary-table .tabulator-col-title {
  color: rgba(255, 255, 255, 0.4) !important;
}

html.dark .ur-summary-table .tabulator-col.tabulator-sortable:hover .tabulator-col-title {
  color: #d6e4f0 !important;
}

html.dark .ur-summary-table .tabulator-col.tabulator-col-sorter-element:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

html.dark .ur-summary-table .tabulator-row {
  background: #0b1625 !important;
  border-bottom-color: rgba(255, 255, 255, 0.04) !important;
  display: flex !important;
  align-items: stretch !important;
}

html.dark .ur-summary-table .tabulator-row .tabulator-cell {
  color: rgba(255, 255, 255, 0.7) !important;
}

html.dark .ur-summary-table .tabulator-row.tabulator-row-even {
  background: #0b1625 !important;
}

html.dark .ur-summary-table .tabulator-row:hover,
html.dark .ur-summary-table .tabulator-row.tabulator-row-even:hover {
  background: #16263d !important;
}

html.dark .ur-summary-table .tabulator-row:hover .tabulator-cell {
  background-color: transparent !important;
}

html.dark .ur-summary-table .tabulator-calcs-bottom .tabulator-cell {
  background: #0e1b2c !important;
  color: #d6e4f0 !important;
  border-top-color: rgba(255, 255, 255, 0.07) !important;
}

html.dark .ur-summary-table .tabulator-tableHolder::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
}
</style>
